import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaEye, FaPlus, FaUser, FaClipboardList, FaFileAlt, FaBox, FaDollarSign, FaClock, FaCalendar, FaHammer, FaUsers, FaBuilding, FaEllipsisH, FaCogs, FaHistory, FaEdit, FaCheck, FaTimes, FaMapMarkerAlt, FaUserTie } from 'react-icons/fa';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, arrayUnion, Timestamp, onSnapshot, getFirestore, addDoc, orderBy, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import ScanningReportModal from '../../components/ScanningReportModal';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';

// 1. Definición de tipos para documentos y workers
interface ProjectDocument {
  id: string;
  name: string;
  url: string;
  type: string;
  size?: number;
  uploadDate?: string;
}

interface WorkerType {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  photoURL?: string;
  experience?: { skills?: Array<{ id: string; name_en: string; name_es: string }> };
  certifications?: Array<{ name: string }>;
  address?: { city?: string; state?: string; country?: string };
  [key: string]: any;
}



// Función para generar el texto de findings basado en el checklist
function generateFindingsText(selected: string[]) {
  if (!selected || selected.length === 0) {
    return "No findings were recorded for this section.";
  }

  const sentences: string[] = [];

  const categories = {
    reinforcement: ["Rebar detected", "Wire mesh detected", "Post-tension tendons detected", "No reinforcement detected in the scanned area"],
    utilities: ["Electrical conduit", "Communications conduit", "Metal pipe", "PVC pipe", "Energized utilities detected (EM readings)", "No utilities detected"],
    slab: ["High moisture levels in slab", "Interference in signals", "Limited depth penetration", "Irregular surface affected scanning"],
    markings: ["Marked in red (electrical)", "Marked in yellow (reinforcement)", "High-risk areas marked", "Manual exposure required before drilling/cutting"],
    recommendations: ["Avoid drilling near marked areas", "Manually expose marked items before cutting", "Review structural drawings", "Recommend re-scan due to interference"]
  };

  function addSectionText(title: string, items: string[]) {
    if (items.length > 0) {
      const list = items.join(", ");
      sentences.push(`${title} ${list}.`);
    }
  }

  addSectionText(
    "Structural elements detected include:",
    selected.filter(s => categories.reinforcement.includes(s))
  );

  addSectionText(
    "Subsurface utilities identified include:",
    selected.filter(s => categories.utilities.includes(s))
  );

  addSectionText(
    "Conditions that may affect scan accuracy were observed, including:",
    selected.filter(s => categories.slab.includes(s))
  );

  addSectionText(
    "Surface markings applied:",
    selected.filter(s => categories.markings.includes(s))
  );

  addSectionText(
    "Recommended actions:",
    selected.filter(s => categories.recommendations.includes(s))
  );

  return sentences.join(" ");
}

// Utilidad para cargar el script de Google Maps nativo
function loadGoogleMapsScript(apiKey: string, mapId: string) {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(undefined);
      return;
    }
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.onload = () => resolve(undefined);
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&map_ids=${mapId}&libraries=places`;
    script.async = true;
    script.onload = () => resolve(undefined);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// Componente separado para el tab de finances
const FinancesTab = React.memo(({ project, projectId, businessId }: { project: any, projectId: string, businessId: string }) => {
  const { t } = useTranslation();
  const [financialTransactions, setFinancialTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  // const [activeTasks, setActiveTasks] = useState<any[]>([]);
  const [workerAssignments, setWorkerAssignments] = useState<any[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [calculatedLaborCost, setCalculatedLaborCost] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  // Efecto para cargar tasks activos y calcular costos de labor
  useEffect(() => {
    const loadTasksAndCalculateLaborCost = async () => {
      if (!projectId || !businessId) return;
      
      setLoadingTasks(true);
      try {
        // 0. MIGRACIÓN: Actualizar assignment requests existentes que no tienen projectId
        console.log('[Finances] Verificando assignment requests sin projectId...');
        const allRequestsQuery = query(
          collection(db, 'assignmentRequests'),
          where('businessId', '==', businessId)
        );
        const allRequestsSnap = await getDocs(allRequestsQuery);
        
        let migratedCount = 0;
        for (const requestDoc of allRequestsSnap.docs) {
          const request = requestDoc.data();
          
          // Si no tiene projectId, intentar encontrarlo desde el task
          if (!request.projectId && request.taskId) {
            // Buscar el task en este proyecto
            const taskRef = doc(db, `projects/${businessId}/projects/${projectId}/tasks/${request.taskId}`);
            const taskSnap = await getDoc(taskRef);
            
            if (taskSnap.exists()) {
              // El task existe en este proyecto, agregar el projectId
              await updateDoc(doc(db, 'assignmentRequests', requestDoc.id), {
                projectId: projectId
              });
              migratedCount++;
              console.log(`[Finances] ✅ Migrado assignmentRequest ${requestDoc.id} con projectId`);
            }
          }
        }
        
        if (migratedCount > 0) {
          console.log(`[Finances] ✅ ${migratedCount} assignment requests actualizadas con projectId`);
        } else {
          console.log('[Finances] No hay assignment requests para migrar');
        }
        
        // 1. Cargar todas las tasks del proyecto
        // const tasksRef = collection(db, `projects/${businessId}/projects/${projectId}/tasks`);
        // const tasksSnap = await getDocs(tasksRef);
        // const tasks = tasksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // setActiveTasks(tasks);
        
        // 2. Obtener assignment requests aceptadas para este proyecto
        const acceptedRequestsQuery = query(
          collection(db, 'assignmentRequests'),
          where('businessId', '==', businessId),
          where('projectId', '==', projectId),
          where('status', '==', 'accepted')
        );
        const acceptedRequestsSnap = await getDocs(acceptedRequestsQuery);
        
        // 3. Crear array con información detallada de asignaciones
        const assignments: any[] = [];
        let totalLaborCost = 0;
        
        for (const requestDoc of acceptedRequestsSnap.docs) {
          const request = { id: requestDoc.id, ...requestDoc.data() } as any;
          
          // Obtener información del worker
          const workerDoc = await getDoc(doc(db, 'users', request.workerId));
          const workerData = workerDoc.exists() ? workerDoc.data() : {};
          const workerName = `${workerData.firstName || ''} ${workerData.lastName || ''}`.trim() || 'Worker';
          
          // Calcular el total (puede estar en counterPrice o price)
          const pricePerUnit = request.counterPrice || request.price || 0;
          const duration = request.duration || 1;
          const totalCost = pricePerUnit * duration;
          
          totalLaborCost += totalCost;
          
          // Guardar información detallada de la asignación
          assignments.push({
            id: request.id,
            taskId: request.taskId,
            taskTitle: request.taskTitle,
            workerId: request.workerId,
            workerName: workerName,
            workerEmail: workerData.email || '',
            pricePerUnit: pricePerUnit,
            priceUnit: request.priceUnit || 'fixed',
            duration: duration,
            totalCost: totalCost,
            acceptedAt: request.updatedAt || request.createdAt,
            startDate: request.startDate,
            status: 'active'
          });
        }
        
        setWorkerAssignments(assignments);
        setCalculatedLaborCost(totalLaborCost);
        
        console.log('[Finances] Assignments encontradas:', assignments.length);
        console.log('[Finances] Total Labor Cost calculado:', totalLaborCost);
        
        // 4. Actualizar laborCosts en Firestore si cambió
        if (totalLaborCost > 0) {
          const projectRef = doc(db, `projects/${businessId}/projects/${projectId}`);
          await updateDoc(projectRef, {
            laborCosts: totalLaborCost,
            workerAssignments: assignments,
            lastLaborCostUpdate: new Date().toISOString()
          });
          console.log(`[Finances] ✅ Labor cost actualizado en Firestore: $${totalLaborCost}`);
        }
        
      } catch (error) {
        console.error('[Finances] Error cargando tasks y calculando labor cost:', error);
      } finally {
        setLoadingTasks(false);
      }
    };
    
    loadTasksAndCalculateLaborCost();
  }, [projectId, businessId]);

  // Cálculos financieros memoizados (ahora usa el labor cost calculado)
  const financialCalculations = React.useMemo(() => {
    // Usar totalAmount o total (ambos campos pueden existir dependiendo de cómo se creó el proyecto)
    const projectTotal = project?.totalAmount || project?.total || 0;
    const projectTaxes = project?.taxes || 0;
    const budget = projectTotal - projectTaxes;

    const materialsCost = project?.materials?.reduce((sum: number, mat: any) => sum + ((Number(mat.quantity) || 0) * (Number(mat.price) || 0)), 0) || 0;
    // EXCLUIMOS LOS SERVICIOS COTIZADOS DEL EGRESO
    const servicesCost = 0;
    // Usar el labor cost calculado dinámicamente de los tasks con workers asignados
    const laborCost = calculatedLaborCost;
    const overheadCost = project?.overheadCosts || 0;
    const otherCost = project?.otherCosts || 0;
    const totalExpenses = materialsCost + laborCost + overheadCost + otherCost;
    const profit = budget - totalExpenses;

    return {
      budget,
      projectTotal,
      projectTaxes,
      materialsCost,
      servicesCost,
      laborCost,
      overheadCost,
      otherCost,
      totalExpenses,
      profit,
      isProfit: profit >= 0
    };
  }, [project?.totalAmount, project?.total, project?.taxes, project?.materials, project?.services, calculatedLaborCost, project?.overheadCosts, project?.otherCosts]);

  // Efecto para cargar transacciones financieras
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    if (projectId && businessId) {
      setLoadingTransactions(true);
      const transactionsRef = collection(db, 'projects', businessId, 'projects', projectId, 'payments');
      const q = query(transactionsRef, orderBy('date', 'desc'));
      
      unsubscribe = onSnapshot(q, (snapshot) => {
        const transactions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFinancialTransactions(transactions);
        setLoadingTransactions(false);
      }, (error) => {
        console.error('Error loading financial transactions:', error);
        setLoadingTransactions(false);
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [projectId, businessId]);

  return (
    <div className="space-y-6">
      {/* Botones de Acción */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('projects.finances.title')}</h2>
        <div className="flex space-x-3">
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
            onClick={() => {/* TODO: Implementar modal de agregar ingreso */}}
          >
            <FaPlus /> {t('projects.finances.addIncome')}
          </button>
          <button
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
            onClick={() => {/* TODO: Implementar modal de agregar gasto */}}
          >
            <FaPlus /> {t('projects.finances.addExpense')}
          </button>
        </div>
      </div>

      {/* Resumen Financiero */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('projects.finances.budget')}</p>
              <p className="text-2xl font-bold text-blue-600">
                ${financialCalculations.budget.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">
                {t('projects.finances.total')}: ${financialCalculations.projectTotal.toLocaleString()} - {t('projects.finances.taxes')}: ${financialCalculations.projectTaxes.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FaDollarSign className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('projects.finances.totalExpenses')}</p>
              <p className="text-2xl font-bold text-red-600">
                ${financialCalculations.totalExpenses.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <FaDollarSign className="text-red-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('projects.finances.netProfit')}</p>
              <p className={`text-2xl font-bold ${financialCalculations.isProfit ? 'text-green-600' : 'text-red-600'}`}>
                ${financialCalculations.profit.toFixed(2)}
              </p>
            </div>
            <div className={`p-3 rounded-full ${financialCalculations.isProfit ? 'bg-green-100' : 'bg-red-100'}`}>
              <FaDollarSign className={`text-xl ${financialCalculations.isProfit ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Desglose de Gastos */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('projects.finances.expenseBreakdown')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('projects.finances.materials')}</p>
                <p className="text-xl font-bold text-gray-800">
                  ${financialCalculations.materialsCost.toFixed(2)}
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded-full">
                <FaHammer className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('projects.finances.services')}</p>
                <p className="text-xl font-bold text-gray-800">
                  ${financialCalculations.servicesCost.toFixed(2)}
                </p>
              </div>
              <div className="p-2 bg-orange-100 rounded-full">
                <FaCogs className="text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('projects.finances.labor')}</p>
                <p className="text-xl font-bold text-gray-800">
                  ${financialCalculations.laborCost.toFixed(2)}
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-full">
                <FaUsers className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('projects.finances.overhead')}</p>
                <p className="text-xl font-bold text-gray-800">
                  ${financialCalculations.overheadCost.toFixed(2)}
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-full">
                <FaBuilding className="text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('projects.finances.other')}</p>
                <p className="text-xl font-bold text-gray-800">
                  ${financialCalculations.otherCost.toFixed(2)}
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-full">
                <FaEllipsisH className="text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detalle de Workers Contratados */}
      {loadingTasks ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            <span className="ml-2 text-gray-600">Cargando workers contratados...</span>
          </div>
        </div>
      ) : workerAssignments.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FaUsers className="text-green-600" />
              Workers Contratados ({workerAssignments.length})
            </h3>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total Labor</p>
              <p className="text-xl font-bold text-green-600">${calculatedLaborCost.toLocaleString()}</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {workerAssignments.map((assignment) => (
              <div key={assignment.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FaUserTie className="text-green-600" />
                      <h4 className="font-semibold text-gray-900">{assignment.workerName}</h4>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        {assignment.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 ml-6">
                      <div>
                        <span className="font-medium">Task:</span> {assignment.taskTitle}
                        <span className="text-xs text-gray-400 ml-2">(ID: {assignment.taskId})</span>
                      </div>
                      <div>
                        <span className="font-medium">Worker ID:</span> {assignment.workerId}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {assignment.workerEmail}
                      </div>
                      <div>
                        <span className="font-medium">Aceptado:</span>{' '}
                        {assignment.acceptedAt ? new Date(assignment.acceptedAt).toLocaleDateString() : 'N/A'}
                      </div>
                      {assignment.startDate && (
                        <div>
                          <span className="font-medium">Inicio:</span>{' '}
                          {new Date(assignment.startDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right ml-4">
                    <div className="text-xs text-gray-500 mb-1">Costo</div>
                    <div className="text-lg font-bold text-gray-900">
                      ${assignment.totalCost.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      ${assignment.pricePerUnit.toLocaleString()}/{assignment.priceUnit}
                      {assignment.duration > 1 && ` × ${assignment.duration}`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Tabla de Transacciones */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{t('projects.finances.transactions')}</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => {/* TODO: Implementar modal de agregar ingreso */}}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <FaPlus className="text-sm" />
              <span>{t('projects.finances.addIncome')}</span>
            </button>
            <button
              onClick={() => {/* TODO: Implementar modal de agregar gasto */}}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <FaPlus className="text-sm" />
              <span>{t('projects.finances.addExpense')}</span>
            </button>
          </div>
        </div>

        {loadingTransactions ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">{t('projects.finances.loading')}</span>
          </div>
        ) : financialTransactions.length === 0 ? (
          <div className="text-center py-8">
            <FaDollarSign className="mx-auto text-gray-400 text-4xl mb-4" />
            <p className="text-gray-600 text-lg">{t('projects.finances.noTransactions')}</p>
            <p className="text-gray-500 text-sm mt-2">{t('projects.finances.startTracking')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('projects.finances.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('projects.finances.description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('projects.finances.category')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('projects.finances.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('projects.finances.status.title')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {financialTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.date?.toDate ? 
                        transaction.date.toDate().toLocaleDateString() : 
                        new Date(transaction.date).toLocaleDateString()
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        transaction.type === 'income' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.category}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                      transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}${transaction.amount?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.status === 'pagado' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'cancelado' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {transaction.status === 'pagado' ? `✓ ${t('projects.finances.status.paid')}` :
                           transaction.status === 'cancelado' ? `✗ ${t('projects.finances.status.cancelled')}` :
                           `⏳ ${t('projects.finances.status.pending')}`}
                        </span>
                        {transaction.status === 'pendiente' && (
                          <button
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setShowPaymentModal(true);
                            }}
                            className="text-green-600 hover:text-green-900"
                            title={t('projects.finances.markAsPaid')}
                          >
                            <FaCheck />
                          </button>
                        )}
                        {transaction.receiptURL && (
                          <a
                            href={transaction.receiptURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-900"
                            title={t('projects.finances.viewReceipt')}
                          >
                            <FaFileAlt />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal para marcar como pagado y adjuntar comprobante */}
      {showPaymentModal && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('projects.finances.markAsPaidModal.title')}</h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedTransaction(null);
                  setReceiptFile(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('projects.finances.markAsPaidModal.transaction')}:</p>
                <p className="font-medium text-gray-900">{selectedTransaction.description}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-1">{t('projects.finances.markAsPaidModal.amount')}:</p>
                <p className="font-medium text-gray-900">
                  ${selectedTransaction.amount?.toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('projects.finances.markAsPaidModal.attachReceipt')}
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    cursor-pointer"
                />
                {receiptFile && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ {receiptFile.name}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={async () => {
                    try {
                      setUploadingReceipt(true);
                      let receiptURL = null;

                      // Si hay archivo, subirlo a Storage
                      if (receiptFile) {
                        const storageRef = ref(storage, `projects/${businessId}/receipts/${selectedTransaction.id}_${receiptFile.name}`);
                        await uploadBytes(storageRef, receiptFile);
                        receiptURL = await getDownloadURL(storageRef);
                      }

                      // Actualizar el documento en Firestore
                      const transactionRef = doc(db, 'projects', businessId, 'projects', projectId, 'payments', selectedTransaction.id);
                      await updateDoc(transactionRef, {
                        status: 'pagado',
                        paidAt: new Date().toISOString(),
                        ...(receiptURL && { receiptURL })
                      });

                      toast.success('Transacción marcada como pagada');
                      setShowPaymentModal(false);
                      setSelectedTransaction(null);
                      setReceiptFile(null);
                    } catch (error) {
                      console.error('Error al marcar como pagado:', error);
                      toast.error('Error al actualizar transacción');
                    } finally {
                      setUploadingReceipt(false);
                    }
                  }}
                  disabled={uploadingReceipt}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploadingReceipt ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('projects.finances.markAsPaidModal.saving')}
                    </>
                  ) : (
                    <>
                      <FaCheck />
                      {t('projects.finances.markAsPaid')}
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedTransaction(null);
                    setReceiptFile(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('projects.finances.markAsPaidModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const ProjectView: React.FC = () => {
  const { userData, currentUser } = useAuth();
  // ...otros hooks...

  // Notas del proyecto
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [project, setProject] = useState<any>(null);

  // Cargar notas al cargar el proyecto
  useEffect(() => {
    if (project && Array.isArray(project.notes)) {
      setNotes([...project.notes].sort((a, b) => b.timestamp - a.timestamp));
    }
  }, [project]);

  // Guardar nueva nota
  const handleAddNote = async () => {
    if (!newNote.trim() || !currentUser) return;
    setSavingNote(true);
    const noteObj = {
      text: newNote.trim(),
      user: currentUser.displayName || currentUser.email || 'Usuario',
      uid: currentUser.uid,
      timestamp: Date.now()
    };
    try {
      const projectRef = doc(db, `projects/${userData?.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        notes: arrayUnion(noteObj)
      });
      setNotes(prev => [noteObj, ...prev]);
      setNewNote('');
      toast.success('Nota agregada');
    } catch (e) {
      toast.error('Error al guardar la nota');
    } finally {
      setSavingNote(false);
    }
  };
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();

  const [editMode, setEditMode] = useState<string | false>(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [description, setDescription] = useState('');
  const [businessLogo, setBusinessLogo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [showModal, setShowModal] = useState(false);
  const [workers, setWorkers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [skillFilter] = useState('all');
  const [certFilter] = useState('all');
  const [cityFilter] = useState('all');
  const [roleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const [showWorkerDetails, setShowWorkerDetails] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  const navigate = useNavigate();

  // 1. Definir los nombres por defecto internacionalizados
  const defaultColumns = [
    { id: 'new', name: t('projects.kanban.new') },
    { id: 'in_progress', name: t('projects.kanban.inProgress') },
    { id: 'done', name: t('projects.kanban.done') }
  ];

  const [columns, setColumns] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [newColumnName, setNewColumnName] = useState('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [taskFormData, setTaskFormData] = useState<any>({
    title: '',
    description: '',
    columnId: '',
    assignedTo: null as string | null,
    dueDate: '',
    startDate: '',
    startTime: '',
    duration: '',
    durationUnit: 'days',
    checklistEnabled: false,
    checklist: [] as Array<{
      id: string;
      text: string;
      completed: boolean;
    }>,
    location: {
      address: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      latitude: '',
      longitude: '',
      zoom: 13
    },
    documents: [] as Array<{
      id: string;
      name: string;
      size: number;
      type: string;
      uploadDate: string;
      url: string;
    }>,
    isScanningJob: false,
    workerScanningEdit: [] as string[], // Array de IDs de workers que pueden editar el scanning report
    scanningData: {
      purpose: 'The purpose of the project was to locate rebar & utilities within the slab . The client marked out the desired locations prior to the scanning and our markings were then placed onto the concrete using spray paint.',
      equipment: '• Concrete Scanning EM Antenna. This GPR antenna is handheld and rolls over the surface. The antenna needs a reasonably smooth, unobstructed surface for scanning and is not able to scan within 2"-4" of obstructions such as walls and metal tracks unless they are removed prior to our work. The data is displayed on a screen during the scanning and marked on the surface in real time. The total depth achieved can be as much as 18" or more with this antenna but can vary widely depending on the types of materials being scanned through and other factors such as the spacing of the reinforcing and/or moisture content. Depth accuracy depends on the ability to obtain an accurate depth calibration for the concrete. No harmful radiation is emitted and the work can be performed at any time with people in close proximity.',
      process: 'The process begins by using GPR to collect initial scans throughout the area. These scans are used to calibrate the equipment and determine the type of slab, reinforcing patterns, maximum depth penetration, and any other potential limitations. Each location is then scanned in a grid pattern in 4 directions and then rescanned with the antenna cross polarized. The GPR data is viewed in real time and anomalies in the data were located and marked on the surface. Relevant scan examples were saved and will be provided in this report. The EM locator was used to sweep the areas for live power readings and radio frequency signals.',
      limitations: 'Please keep in mind that there are limitations to any subsurface investigation. The equipment may not achieve maximum effectiveness due to conditions in the concrete or soil such as moisture content, age of the concrete, reinforcing size and spacing, and a variety of other factors. Depths are dependent on many factors so depth accuracy can vary throughout a site and should be treated as estimates only. No subsurface investigation or equipment can provide a complete image of what lies below. Our results should always be used in conjunction with as many methods as possible such as consulting existing plans and drawings, visual inspection of above-ground features, drilling or cutting as far as possible from all of our markings, etc.',
      findings: '',
      checklistItems: [] as string[],
      images: [] as Array<{
        id: string;
        scanImage: { url: string; name: string; uploadDate: string };
        diagramImage: { url: string; name: string; uploadDate: string };
      }>
    }
  });
  const [activeTaskTab, setActiveTaskTab] = useState('general');

  // Estado para edición de nombre de columna
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [columnNameEdit, setColumnNameEdit] = useState('');

  // Estado para modal de selección de trabajador en asignación de tarea
  const [showAssignWorkerModal, setShowAssignWorkerModal] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');

  // Ref para el div del mapa y para el marker
  const nativeMapRef = useRef<HTMLDivElement>(null);
  const nativeMapInstance = useRef<any>(null);
  const nativeMarkerInstance = useRef<any>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteInstance = useRef<any>(null);

  // Estados para documentos del proyecto
  const [projectDocuments, setProjectDocuments] = useState<Array<{
    id: string;
    name: string;
    url: string;
    type: string;
    size?: number;
    uploadDate?: string;
  }>>([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  // Estados para materiales del proyecto
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    description: '',
    quantity: 1,
    unit: '',
    price: 0
  });
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<any>(null);
  
  // Estados para servicios del proyecto
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    quantity: 1,
    unit: '',
    price: 0
  });
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  
  // Estados para el modal de solicitudes de asignación
  const [selectedAssignmentRequest, setSelectedAssignmentRequest] = useState<any>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showCounterOffer, setShowCounterOffer] = useState(false);
  const [counterPrice, setCounterPrice] = useState('');
  const [processingAssignment, setProcessingAssignment] = useState(false);

  // Estado para modal de error al eliminar tarea con worker asignado
  const [showDeleteErrorModal, setShowDeleteErrorModal] = useState(false);
  const [showScanningReportModal, setShowScanningReportModal] = useState(false);

  // Forzar re-render cuando cambie el idioma
  const [_, setRerender] = useState(0);
  useEffect(() => {
    const handler = () => setRerender(x => x + 1);
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, [i18n]);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId || !userData?.businessId) return;
      console.log('[ProjectView] Cargando proyecto con projectId:', projectId);
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectSnap = await getDoc(projectRef);
      if (projectSnap.exists()) {
        setProject(projectSnap.data());
        setName(projectSnap.data().name || '');
        setDescription(projectSnap.data().description || '');
        // Verificar si el historial existe, si no, crearlo
        const historyRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/history`);
        const snap = await getDocs(historyRef);
        if (snap.empty) {
          console.log('[ProjectView] Historial vacío, creando entrada inicial para projectId:', projectId);
          await addDoc(historyRef, {
            projectId,
            businessId: userData.businessId,
            businessName: (userData as any).businessName || (userData as any).email || 'Business',
            action: 'project_created',
            data: { ...projectSnap.data() },
            timestamp: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            performedBy: {
              uid: currentUser?.uid || 'unknown',
              email: currentUser?.email || 'unknown',
              role: userData.role || 'business'
            }
          });
        }
      }
    };
    fetchProject();
    // Ejecutar prueba simple de acceso a usuarios
    testSimpleUserAccess();
  }, [projectId, userData?.businessId]);

  useEffect(() => {
    const fetchBusinessLogo = async () => {
      if (!userData?.businessId) return;
      const businessRef = doc(db, 'businesses', userData.businessId);
      const businessSnap = await getDoc(businessRef);
      if (businessSnap.exists()) {
        setBusinessLogo(businessSnap.data().logo || null);
      }
    };
    fetchBusinessLogo();
  }, [userData?.businessId]);



  // Función de prueba simple para verificar acceso a usuarios
  const testSimpleUserAccess = async () => {
    try {
      console.log('[ProjectView] Testing simple user access...');
      
      // Intentar leer un documento específico que sabemos que existe
      const userRef = doc(db, 'users', 'A5kTjPf7UfYvKpzhrlftJGZIWg22');
      const userSnap = await getDoc(userRef);
      console.log('[ProjectView] Specific user document exists:', userSnap.exists());
      
      if (userSnap.exists()) {
        console.log('[ProjectView] Specific user data:', userSnap.data());
      }
      
      // Intentar leer toda la colección
      const allUsersSnap = await getDocs(collection(db, 'users'));
      console.log('[ProjectView] All users count:', allUsersSnap.docs.length);
      console.log('[ProjectView] All users empty:', allUsersSnap.empty);
      
      // Mostrar los primeros 3 usuarios si existen
      allUsersSnap.docs.slice(0, 3).forEach((doc, index) => {
        console.log(`[ProjectView] User ${index} (${doc.id}):`, doc.data());
      });
      
    } catch (error) {
      console.error('[ProjectView] Error in simple user access test:', error);
    }
  };

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        // Obtener todos los usuarios y filtrar en el frontend para evitar problemas de permisos
        const snap = await getDocs(collection(db, 'users'));
        const allUsers = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
        
        // Filtrar employees del business y workers independientes activos
        const availableWorkers = allUsers.filter(user => {
          const userRole = (user as any).role;
          const userBusinessId = (user as any).businessId;
          const userStatus = (user as any).status;
          
          // Incluir employees del mismo business
          if (userRole === 'employee' && userData?.businessId && userBusinessId === userData.businessId) {
            return true;
          }
          
          // Incluir workers independientes activos
          if (userRole === 'worker' && userStatus === 'active') {
            return true;
          }
          
          return false;
        });
        
        console.log('[ProjectView] Workers cargados:', availableWorkers.length, availableWorkers);
        setWorkers(availableWorkers);
      } catch (error) {
        console.error('[ProjectView] Error fetching workers:', error);
        setWorkers([]);
      }
    };
    
    // Cargar workers siempre que tengamos userData
    if (userData) {
      fetchWorkers();
    }
  }, [userData]);

  // Cargar solicitudes de asignación para la tarea actual
  useEffect(() => {
    if (!userData?.businessId || !editingTask?.id) return;
    
    const fetchAssignmentRequests = async () => {
      try {
      const q = query(
          collection(db, 'assignmentRequests'),
          where('businessId', '==', userData.businessId),
          where('taskId', '==', editingTask.id)
      );
      const snap = await getDocs(q);
        const requests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAssignmentRequests(requests);
        setShowAssignmentRequests(requests.length > 0);
      } catch (error) {
        console.error('[ProjectView] Error fetching assignment requests:', error);
        setAssignmentRequests([]);
      }
    };
    
    fetchAssignmentRequests();
  }, [userData?.businessId, editingTask?.id]);

  // Cargar todas las assignment requests del proyecto para mostrar workers en Staff
  useEffect(() => {
    if (!userData?.businessId || !projectId) return;
    
    const fetchProjectAssignmentRequests = async () => {
      try {
        const q = query(
          collection(db, 'assignmentRequests'),
          where('businessId', '==', userData.businessId),
          where('projectId', '==', projectId),
          where('status', '==', 'accepted')
        );
        const snap = await getDocs(q);
        const requests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAllProjectAssignmentRequests(requests);
        console.log('[ProjectView] Assignment requests aceptadas del proyecto:', requests);
      } catch (error) {
        console.error('[ProjectView] Error fetching project assignment requests:', error);
        setAllProjectAssignmentRequests([]);
      }
    };
    
    fetchProjectAssignmentRequests();
  }, [userData?.businessId, projectId]);

  // Cargar columnas y tareas desde Firestore al cargar el proyecto
  useEffect(() => {
    if (project) {
      setColumns(project.columns && project.columns.length > 0 ? project.columns : defaultColumns);
      // Ya no actualizar setTasks aquí, solo columnas
    }
  }, [project, t]);

  useEffect(() => {
    const fetchClient = async () => {
      if (!project?.clientId || !userData?.businessId) return;
      try {
        const clientRef = doc(db, `clients/${userData.businessId}/clients/${project.clientId}`);
        const clientSnap = await getDoc(clientRef);
        if (clientSnap.exists()) {
          setClientData(clientSnap.data());
        }
      } catch (e) {
        setClientData(null);
      }
    };
    fetchClient();
  }, [project?.clientId, userData?.businessId]);

  const handleSave = async () => {
    if (!projectId || !userData?.businessId) return;
    setSaving(true);
    const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
    await updateDoc(projectRef, { name });
    setProject((prev: any) => ({ ...prev, name }));
    setEditMode(false);
    setSaving(false);
    await logProjectHistoryEntry('project_updated', { field: 'name', newValue: name });
  };

  const handleSaveDescription = async () => {
    if (!projectId || !userData?.businessId) return;
    setSaving(true);
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, { description });
      await logProjectHistoryEntry('project_updated', { field: 'description', oldValue: project.description, newValue: description });
      setProject((prev: any) => ({ ...prev, description }));
      setEditingDescription(false);
    } catch (error) {
      console.error('Error al actualizar descripción del proyecto:', error);
    } finally {
      setSaving(false);
    }
  };

  // Obtener todas las habilidades únicas usando un Map para evitar duplicados
  const skillsMap = new Map();
  workers.forEach(worker => {
    (worker.experience?.skills || []).forEach((skill: any) => {
      if (!skillsMap.has(skill.id)) {
        skillsMap.set(skill.id, {
          id: skill.id,
          name_en: skill.name_en,
          name_es: skill.name_es
        });
      }
    });
  });
  // const allSkills = Array.from(skillsMap.values());

  // Obtener todas las ciudades únicas
  // const allCities = Array.from(new Set(workers.map(w => w.address?.city).filter(Boolean))) as string[];

  // Obtener todas las certificaciones únicas
  // const allCerts = Array.from(new Set(workers.flatMap(u => (u.certifications || []).map((c:any) => c.name)))) as string[];

  const filteredUsers = workers.filter(worker => {
    if (!worker) return false;

    // Filtro por rol
    if (roleFilter && roleFilter !== 'all') {
      if (worker.role !== roleFilter) return false;
    }

    // Filtro por skills
    if (skillFilter && skillFilter !== 'all' && worker.experience?.skills) {
      const hasSkill = worker.experience.skills.some((skill: any) => 
        skill.id === skillFilter || 
        skill.name_en === skillFilter || 
        skill.name_es === skillFilter
      );
      if (!hasSkill) return false;
    }

    // Filtro por certificaciones
    if (certFilter && certFilter !== 'all' && worker.certifications) {
      const hasCertification = worker.certifications.some((cert: any) => 
        cert.name === certFilter
      );
      if (!hasCertification) return false;
    }

    // Filtro por ciudad
    if (cityFilter && cityFilter !== 'all' && worker.address?.city) {
      if (worker.address.city !== cityFilter) {
        return false;
      }
    }

    return true;
  });



  const paginatedWorkers = filteredUsers.slice((page-1)*pageSize, page*pageSize);
  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  const handleAddStaff = async (worker: any) => {
    if (!projectId || !userData?.businessId) return;
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      const currentStaff = projectData.staff || [];

      // Verificar si el trabajador ya está en el proyecto
      const isAlreadyAdded = currentStaff.some((s: any) => s.id === worker.uid);
      if (isAlreadyAdded) {
        throw new Error('Worker is already added to this project');
      }

      // Crear objeto del trabajador para agregar al proyecto (eliminar valores undefined)
      const workerToAdd: any = {
        id: worker.uid || worker.id,
        firstName: worker.firstName || '',
        lastName: worker.lastName || '',
        email: worker.email || '',
        role: worker.role || 'employee', // Rol del usuario en el sistema (no cambia)
        projectRole: '' // Rol en el proyecto (se asignará después si es necesario)
      };

      // Solo agregar campos opcionales si tienen valor
      if (worker.photoURL) {
        workerToAdd.photoURL = worker.photoURL;
      }
      
      if (worker.experience?.skills && worker.experience.skills.length > 0) {
        workerToAdd.skills = worker.experience.skills;
      }
      
      if (worker.address?.city) {
        workerToAdd.city = worker.address.city;
      }

      await updateDoc(projectRef, {
        staff: arrayUnion(workerToAdd)
      });

      setShowModal(false);
      // Actualizar el estado local del proyecto
      setProject((prev: any) => ({
        ...prev,
        staff: [...(prev.staff || []), workerToAdd]
      }));
    } catch (error) {
      console.error('Error adding staff:', error);
      alert(error instanceof Error ? error.message : 'Error adding staff');
    }
  };

  const handleAddSelfToStaff = async () => {
    if (!projectId || !userData?.businessId || !currentUser) return;
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      const currentStaff = projectData.staff || [];

      // Verificar si el business ya está en el proyecto
      const isAlreadyAdded = currentStaff.some((s: any) => s.id === currentUser.uid);
      if (isAlreadyAdded) {
        toast.error(t('projects.staff.alreadyAdded') || 'Ya estás agregado al equipo del proyecto');
        setShowAddSelfModal(false);
        return;
      }

      // Crear objeto del business para agregar al staff
      const businessToAdd: any = {
        id: currentUser.uid,
        firstName: (userData as any).firstName || (userData as any).name?.split(' ')[0] || 'Business',
        lastName: (userData as any).lastName || (userData as any).name?.split(' ').slice(1).join(' ') || 'Owner',
        email: currentUser.email || '',
        role: 'business',
        projectRole: selectedBusinessRole // Rol específico dentro del proyecto (Project Manager, Supervisor)
      };

      // Agregar foto si existe
      if ((userData as any).photoURL || currentUser.photoURL) {
        businessToAdd.photoURL = (userData as any).photoURL || currentUser.photoURL;
      }

      await updateDoc(projectRef, {
        staff: arrayUnion(businessToAdd)
      });

      setShowAddSelfModal(false);
      setSelectedBusinessRole('Project Manager');
      
      // Actualizar el estado local del proyecto
      setProject((prev: any) => ({
        ...prev,
        staff: [...(prev.staff || []), businessToAdd]
      }));

      toast.success(t('projects.staff.selfAdded') || 'Te has añadido exitosamente al equipo del proyecto');
      
      // Registrar en historial
      await logProjectHistoryEntry('staff_updated', { 
        action: 'self_added', 
        member: businessToAdd,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error adding self to staff:', error);
      toast.error(t('projects.staff.errorAddingSelf') || 'Error al añadirte al equipo');
    }
  };

  const handleUpdateStaffRole = async () => {
    if (!projectId || !userData?.businessId || !editingStaffMember) return;
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      const currentStaff = projectData.staff || [];

      // Actualizar el rol del miembro del staff
      const updatedStaff = currentStaff.map((member: any) => {
        if (member.id === editingStaffMember.id) {
          return {
            ...member,
            projectRole: newProjectRole
          };
        }
        return member;
      });

      await updateDoc(projectRef, {
        staff: updatedStaff
      });

      setShowEditRoleModal(false);
      setEditingStaffMember(null);
      setNewProjectRole('');
      
      // Actualizar el estado local del proyecto
      setProject((prev: any) => ({
        ...prev,
        staff: updatedStaff
      }));

      toast.success(t('projects.staff.roleUpdated') || 'Rol actualizado exitosamente');
      
      // Registrar en historial
      await logProjectHistoryEntry('staff_updated', { 
        action: 'role_updated', 
        member: editingStaffMember,
        oldRole: editingStaffMember.projectRole,
        newRole: newProjectRole,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating staff role:', error);
      toast.error(t('projects.staff.errorUpdatingRole') || 'Error al actualizar el rol');
    }
  };

  const handleRemoveStaffMember = async (memberId: string) => {
    if (!projectId || !userData?.businessId) return;
    
    if (!confirm(t('projects.staff.confirmRemove') || '¿Estás seguro de eliminar este miembro del equipo?')) {
      return;
    }
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      const currentStaff = projectData.staff || [];
      const memberToRemove = currentStaff.find((m: any) => m.id === memberId);

      // Filtrar el miembro del staff
      const updatedStaff = currentStaff.filter((member: any) => member.id !== memberId);

      await updateDoc(projectRef, {
        staff: updatedStaff
      });
      
      // Actualizar el estado local del proyecto
      setProject((prev: any) => ({
        ...prev,
        staff: updatedStaff
      }));

      toast.success(t('projects.staff.memberRemoved') || 'Miembro eliminado del equipo');
      
      // Registrar en historial
      await logProjectHistoryEntry('staff_updated', { 
        action: 'member_removed', 
        member: memberToRemove,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error removing staff member:', error);
      toast.error(t('projects.staff.errorRemoving') || 'Error al eliminar miembro');
    }
  };

  const handleOpenStaffMemberModal = (member: any) => {
    setSelectedStaffMember(member);
    setNewProjectRole(member.projectRole || '');
    
    // Obtener el permiso del USUARIO ACTUAL (quien está dando el permiso)
    // "Yo (Juan) permito que [member] (Pedro) firme por mí"
    const currentUserId = currentUser?.uid;
    const selectedMemberId = member.uid || member.id;
    
    // Buscar el usuario actual en el staff para ver su canSignForMe
    const currentUserInStaff = project?.staff?.find((m: any) => (m.id || m.uid) === currentUserId);
    const canSignPermissions = currentUserInStaff?.canSignForMe || {};
    const permissionValue = canSignPermissions[selectedMemberId] || false;
    
    setCanSignForMe(permissionValue);
    setShowStaffMemberModal(true);
  };

  const handleUpdateStaffMember = async () => {
    if (!projectId || !userData?.businessId || !selectedStaffMember) return;
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      const currentStaff = projectData.staff || [];
      const selectedMemberId = selectedStaffMember.id || selectedStaffMember.uid;

      // Buscar si el miembro ya existe en staff
      const memberExists = currentStaff.some((member: any) => {
        const memberId = member.id || member.uid;
        return memberId === selectedMemberId;
      });

      let updatedStaff;
      const currentUserId = currentUser?.uid;
      
      if (memberExists) {
        // Actualizar el miembro existente Y actualizar los permisos del usuario actual
        updatedStaff = currentStaff.map((member: any) => {
          const memberId = member.id || member.uid;
          
          if (memberId === selectedMemberId) {
            // Actualizar el miembro seleccionado con su rol
            return {
              ...member,
              projectRole: newProjectRole
            };
          }
          
          // Si es el usuario actual (quien está dando permiso), actualizar su canSignForMe
          if (memberId === currentUserId && currentUserId) {
            const updatedCanSignForMe = { ...(member.canSignForMe || {}) };
            updatedCanSignForMe[selectedMemberId] = canSignForMe;
            
            return {
              ...member,
              canSignForMe: updatedCanSignForMe
            };
          }
          
          return member;
        });
        
        // Si el usuario actual no está en el staff, agregarlo con el permiso
        const currentUserInStaff = currentStaff.some((m: any) => (m.id || m.uid) === currentUserId);
        if (!currentUserInStaff && currentUserId) {
          // Obtener info del usuario actual
          const currentUserData = await getDoc(doc(db, 'users', currentUserId));
          if (currentUserData.exists()) {
            const userData = currentUserData.data();
            updatedStaff.push({
              id: currentUserId,
              uid: currentUserId,
              email: userData.email,
              firstName: userData.firstName || userData.name || '',
              lastName: userData.lastName || '',
              role: userData.role,
              photoURL: userData.photoURL || null,
              projectRole: 'business',
              canSignForMe: { [selectedMemberId]: canSignForMe },
              addedAt: new Date().toISOString()
            });
          }
        }
      } else {
        // Agregar el miembro nuevo al staff
        const newMember = {
          id: selectedStaffMember.uid || selectedStaffMember.id,
          uid: selectedStaffMember.uid || selectedStaffMember.id,
          email: selectedStaffMember.email,
          firstName: selectedStaffMember.firstName,
          lastName: selectedStaffMember.lastName,
          role: selectedStaffMember.role,
          photoURL: selectedStaffMember.photoURL || null,
          projectRole: newProjectRole,
          addedAt: new Date().toISOString()
        };
        updatedStaff = [...currentStaff, newMember];
        
        // Si el usuario actual existe en el staff, actualizar su permiso
        const currentUserInStaff = updatedStaff.find((m: any) => (m.id || m.uid) === currentUserId);
        if (currentUserInStaff && currentUserId) {
          currentUserInStaff.canSignForMe = {
            ...(currentUserInStaff.canSignForMe || {}),
            [selectedMemberId]: canSignForMe
          };
        } else if (currentUserId) {
          // Agregar el usuario actual al staff con el permiso
          const currentUserData = await getDoc(doc(db, 'users', currentUserId));
          if (currentUserData.exists()) {
            const userData = currentUserData.data();
            updatedStaff.push({
              id: currentUserId,
              uid: currentUserId,
              email: userData.email,
              firstName: userData.firstName || userData.name || '',
              lastName: userData.lastName || '',
              role: userData.role,
              photoURL: userData.photoURL || null,
              projectRole: 'business',
              canSignForMe: { [selectedMemberId]: canSignForMe },
              addedAt: new Date().toISOString()
            });
          }
        }
      }

      await updateDoc(projectRef, {
        staff: updatedStaff
      });

      setShowStaffMemberModal(false);
      setSelectedStaffMember(null);
      setNewProjectRole('');
      setCanSignForMe(false);
      
      // Actualizar el estado local del proyecto
      setProject((prev: any) => ({
        ...prev,
        staff: updatedStaff
      }));

      toast.success(t('projects.staff.memberUpdated') || 'Miembro actualizado exitosamente');
      
      // Registrar en historial
      await logProjectHistoryEntry('staff_updated', { 
        action: 'member_updated', 
        member: selectedStaffMember,
        projectRole: newProjectRole,
        canSignForMe: canSignForMe,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating staff member:', error);
      toast.error(t('projects.staff.errorUpdating') || 'Error al actualizar miembro');
    }
  };

  // Guardar columnas en Firestore
  const saveColumns = async (cols: any[]) => {
    if (!projectId || !userData?.businessId) return;
    const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
    await updateDoc(projectRef, { columns: cols });
    setColumns(cols);
    setProject((prev: any) => ({ ...prev, columns: cols }));
  };

  // saveTasks ya no se usa; las tareas se actualizan directamente en la subcolección

  // Añadir columna
  // const handleAddColumn = async () => {
  //   if (!newColumnName.trim()) return;
  //   const newCol = { id: Date.now().toString(), name: newColumnName.trim() };
  //   const updatedCols = [...columns, newCol];
  //   await saveColumns(updatedCols);
  //   setNewColumnName('');
  // };

  // Abrir modal para editar tarea existente
  const handleEditTask = async (task: any) => {
    console.log('Tarea cargada:', task);
    
    // ✅ BUSCAR SOLICITUDES ACEPTADAS PARA ESTA TAREA
    const acceptedRequest = assignmentRequests.find(r => 
      r.taskId === task.id && r.status === 'accepted'
    );
    
    // ✅ SI HAY SOLICITUD ACEPTADA Y LA TAREA NO TIENE WORKER, USAR DATOS DE LA SOLICITUD
    let workerData = task.worker || null;
    let finalPrice = task.finalPrice || null;
    let finalPriceUnit = task.finalPriceUnit || null;
    let finalDuration = task.finalDuration || null;
    let finalTotal = task.finalTotal || null;
    let assignmentRequestId = task.assignmentRequestId || null;
    
    if (acceptedRequest && (!task.worker || task.worker.length === 0)) {
      console.log('🔧 Aplicando datos de solicitud aceptada:', acceptedRequest);
      workerData = [acceptedRequest.workerId];
      finalPrice = acceptedRequest.counterPrice || acceptedRequest.price;
      finalPriceUnit = acceptedRequest.priceUnit;
      finalDuration = acceptedRequest.duration;
      finalTotal = acceptedRequest.total;
      assignmentRequestId = acceptedRequest.id;
      
      // ✅ GUARDAR AUTOMÁTICAMENTE EN FIRESTORE
      try {
        if (userData?.businessId && projectId) {
          const db = getFirestore();
          const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
          const projectSnap = await getDoc(projectRef);
          
          if (projectSnap.exists()) {
            const projectData = projectSnap.data() as any;
            if (Array.isArray(projectData.tasks)) {
              const idx = projectData.tasks.findIndex((t: any) => t.id === task.id);
              if (idx !== -1) {
                const updatedTasks = [...projectData.tasks];
                updatedTasks[idx] = {
                  ...updatedTasks[idx],
                  ...(workerData !== undefined && { worker: workerData }),
                  ...(acceptedRequest.workerId !== undefined && { assignedTo: acceptedRequest.workerId }),
                  ...(finalPrice !== undefined && { finalPrice: finalPrice }),
                  ...(finalPriceUnit !== undefined && { finalPriceUnit: finalPriceUnit }),
                  ...(finalDuration !== undefined && { finalDuration: finalDuration }),
                  ...(finalTotal !== undefined && { finalTotal: finalTotal }),
                  ...(assignmentRequestId !== undefined && { assignmentRequestId: assignmentRequestId })
                };
                
                await updateDoc(projectRef, { tasks: updatedTasks });
                console.log('✅ Tarea actualizada en Firestore con datos de worker');
                
                // Actualizar el estado local también
                setTasks(prev => prev.map(t => 
                  t.id === task.id 
                    ? { ...t, worker: workerData, assignedTo: acceptedRequest.workerId, finalPrice, finalPriceUnit, finalDuration, finalTotal, assignmentRequestId }
                    : t
                ));
              }
            }
          }
        }
      } catch (error) {
        console.error('Error al guardar worker en Firestore:', error);
        toast.error('Error al guardar asignación de worker');
      }
    }
    
    setEditingTask(task);
    setTaskFormData({
      id: task.id, // ✅ INCLUIR ID DEL TASK
      projectId: projectId, // ✅ INCLUIR PROJECT ID
      title: task.title || '',
      description: task.description || '',
      columnId: task.columnId || 'new',
      assignedTo: task.assignedTo || null,
      // ✅ CARGAR CAMPOS DE WORKER Y PAGO (con fallback a solicitud aceptada)
      worker: workerData,
      finalPrice: finalPrice,
      finalPriceUnit: finalPriceUnit,
      finalDuration: finalDuration,
      finalTotal: finalTotal,
      assignmentRequestId: assignmentRequestId,
      dueDate: task.dueDate
        ? (task.dueDate.seconds
            ? new Date(task.dueDate.seconds * 1000).toISOString().slice(0, 10)
            : task.dueDate)
        : '',
      startDate: task.startDate
        ? (task.startDate.seconds
            ? new Date(task.startDate.seconds * 1000).toISOString().slice(0, 10)
            : task.startDate)
        : '',
      startTime: task.startDate
        ? (task.startDate.seconds
            ? new Date(task.startDate.seconds * 1000).toTimeString().slice(0, 5)
            : new Date(task.startDate).toTimeString().slice(0, 5))
        : '',
      duration: task.duration || '',
      durationUnit: task.durationUnit || 'days',
      checklistEnabled: task.checklistEnabled || false,
      checklist: task.checklist || [] as Array<{
        id: string;
        text: string;
        completed: boolean;
      }>,
      location: task.location || {
        address: '', city: '', state: '', country: '', zipCode: '', latitude: '', longitude: '', zoom: 13
      },
      documents: task.documents || [] as Array<{
        id: string;
        name: string;
        size: number;
        type: string;
        uploadDate: string;
        url: string;
      }>,
      isScanningJob: task.isScanningJob || false,
      workerScanningEdit: task.workerScanningEdit || [] as string[], // Array de IDs de workers que pueden editar
      scanningData: task.scanningData || {
        purpose: 'The purpose of the project was to locate rebar & utilities within the slab . The client marked out the desired locations prior to the scanning and our markings were then placed onto the concrete using spray paint.',
        equipment: '• Concrete Scanning EM Antenna. This GPR antenna is handheld and rolls over the surface. The antenna needs a reasonably smooth, unobstructed surface for scanning and is not able to scan within 2"-4" of obstructions such as walls and metal tracks unless they are removed prior to our work. The data is displayed on a screen during the scanning and marked on the surface in real time. The total depth achieved can be as much as 18" or more with this antenna but can vary widely depending on the types of materials being scanned through and other factors such as the spacing of the reinforcing and/or moisture content. Depth accuracy depends on the ability to obtain an accurate depth calibration for the concrete. No harmful radiation is emitted and the work can be performed at any time with people in close proximity.',
        process: 'The process begins by using GPR to collect initial scans throughout the area. These scans are used to calibrate the equipment and determine the type of slab, reinforcing patterns, maximum depth penetration, and any other potential limitations. Each location is then scanned in a grid pattern in 4 directions and then rescanned with the antenna cross polarized. The GPR data is viewed in real time and anomalies in the data were located and marked on the surface. Relevant scan examples were saved and will be provided in this report. The EM locator was used to sweep the areas for live power readings and radio frequency signals.',
        limitations: 'Please keep in mind that there are limitations to any subsurface investigation. The equipment may not achieve maximum effectiveness due to conditions in the concrete or soil such as moisture content, age of the concrete, reinforcing size and spacing, and a variety of other factors. Depths are dependent on many factors so depth accuracy can vary throughout a site and should be treated as estimates only. No subsurface investigation or equipment can provide a complete image of what lies below. Our results should always be used in conjunction with as many methods as possible such as consulting existing plans and drawings, visual inspection of above-ground features, drilling or cutting as far as possible from all of our markings, etc.',
        findings: task.scanningData?.findings || '',
        images: task.scanningData?.images || []
      }
    });
    
    // Cargar archivos de la tarea
    setTaskFiles(task.files || []);
    
    setShowTaskModal(true);
  };

  // Nueva función para guardar (crear o editar) una tarea en la subcolección
  // Función para calcular el progreso general del proyecto basado en todas las tareas
  const calculateProjectProgress = async () => {
    if (!userData?.businessId || !projectId) return;
    try {
      const tasksRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/tasks`);
      const tasksSnap = await getDocs(tasksRef);
      const tasks = tasksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (tasks.length === 0) {
        // Si no hay tareas, el progreso es 0
        const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
        await updateDoc(projectRef, { progress: 0 });
        setProject((prev: any) => prev ? { ...prev, progress: 0 } : null);
        return;
      }
      
      // Calcular el promedio del progreso de todas las tareas
      const totalProgress = tasks.reduce((sum: number, task: any) => {
        return sum + (task.progress || 0);
      }, 0);
      
      const averageProgress = Math.round(totalProgress / tasks.length);
      
      // Actualizar el progreso del proyecto en Firestore
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, { progress: averageProgress });
      
      // Actualizar el estado local
      setProject((prev: any) => prev ? { ...prev, progress: averageProgress } : null);
      
      console.log(`[ProjectView] Progreso del proyecto actualizado: ${averageProgress}% (${tasks.length} tareas)`);
    } catch (error) {
      console.error('[ProjectView] Error calculando progreso del proyecto:', error);
    }
  };

  const saveTask = async (task: any) => {
    if (!userData?.businessId || !projectId) return;
    const tasksRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/tasks`);
    if (task.id && !task._new) {
      // Editar tarea existente
      const taskDoc = doc(db, `projects/${userData.businessId}/projects/${projectId}/tasks/${task.id}`);
      await updateDoc(taskDoc, task);
    } else {
      // Crear nueva tarea
      const docRef = await addDoc(tasksRef, task);
      task.id = docRef.id;
    }
    
    // Recalcular el progreso del proyecto después de guardar la tarea
    await calculateProjectProgress();
  };

  // Modificar handleSaveTask para usar saveTask en vez de saveTasks/array
  const handleSaveTask = async () => {
    if (!taskFormData.title.trim()) return;
    try {
      // Calcular progreso automático si el checklist está activo
      const finalProgress = getCurrentProgress();
      let startDateTime = null;
      if (taskFormData.startDate && taskFormData.startTime) {
        const [hours, minutes] = taskFormData.startTime.split(':');
        const startDate = new Date(taskFormData.startDate);
        startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        startDateTime = Timestamp.fromDate(startDate);
      } else if (taskFormData.startDate) {
        startDateTime = Timestamp.fromDate(new Date(taskFormData.startDate));
      }
      const cleanTaskFormData = Object.fromEntries(
        Object.entries(taskFormData).filter(([_, value]) => value !== undefined)
      );
      let newTask = editingTask
        ? { ...editingTask, ...cleanTaskFormData, progress: finalProgress, location: { ...taskFormData.location }, startDate: startDateTime, dueDate: taskFormData.dueDate ? Timestamp.fromDate(new Date(taskFormData.dueDate)) : null, updatedAt: new Date().toISOString() }
        : { ...cleanTaskFormData, progress: finalProgress, location: { ...taskFormData.location }, startDate: startDateTime, dueDate: taskFormData.dueDate ? Timestamp.fromDate(new Date(taskFormData.dueDate)) : null, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), _new: true };
      // Eliminar _new si es edición
      if (editingTask && newTask._new) {
        delete newTask._new;
      }
      const isNewTask = !editingTask;
      await saveTask(newTask);
      // Si es una tarea nueva, registrar en historial como 'task_created'
      if (isNewTask) {
        // Para tareas nuevas, guardar solo información básica
        const basicTaskInfo = {
          title: newTask.title,
          description: newTask.description,
          columnId: newTask.columnId,
          duration: newTask.duration,
          durationUnit: newTask.durationUnit,
          startDate: newTask.startDate,
          checklistEnabled: newTask.checklistEnabled,
          isScanningJob: newTask.isScanningJob
        };
        await logTaskHistoryEntry(newTask.id, 'task_created', basicTaskInfo);
        // Guardar también en la subcolección de historial de la tarea
        if (userData?.businessId && projectId && newTask.id) {
          const db = getFirestore();
          const taskHistoryRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/tasks/${newTask.id}/history`);
          const historyEntry = {
            taskId: newTask.id,
            projectId,
            businessId: userData.businessId,
            businessName: (userData as any).businessName || (userData as any).email || 'Business',
            action: 'task_created',
            data: {
              title: newTask.title,
              description: newTask.description,
              columnId: newTask.columnId,
              duration: newTask.duration,
              durationUnit: newTask.durationUnit
            },
            timestamp: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            performedBy: {
              uid: currentUser?.uid || 'unknown',
              email: currentUser?.email || 'unknown',
              role: userData?.role || 'business'
            }
          };
          await addDoc(taskHistoryRef, historyEntry);
        }
      }
      setShowTaskModal(false);
      setEditingTask(null);
      setTaskFiles([]);
      setTaskFormData({
        title: '', 
        description: '', 
        columnId: 'new', 
        assignedTo: null, 
        worker: null, 
        finalPrice: null, 
        finalPriceUnit: null, 
        finalDuration: null, 
        finalTotal: null, 
        assignmentRequestId: null, 
        dueDate: '', 
        startDate: '', 
        startTime: '', 
        duration: '', 
        durationUnit: 'days', 
        checklistEnabled: false, 
        checklist: [], 
        location: { address: '', city: '', state: '', country: '', zipCode: '', latitude: '', longitude: '', zoom: 13 }, 
        documents: [],
        isScanningJob: false,
        workerScanningEdit: [] as string[],
        scanningData: {
          purpose: 'The purpose of the project was to locate rebar & utilities within the slab . The client marked out the desired locations prior to the scanning and our markings were then placed onto the concrete using spray paint.',
          equipment: '• Concrete Scanning EM Antenna. This GPR antenna is handheld and rolls over the surface. The antenna needs a reasonably smooth, unobstructed surface for scanning and is not able to scan within 2"-4" of obstructions such as walls and metal tracks unless they are removed prior to our work. The data is displayed on a screen during the scanning and marked on the surface in real time. The total depth achieved can be as much as 18" or more with this antenna but can vary widely depending on the types of materials being scanned through and other factors such as the spacing of the reinforcing and/or moisture content. Depth accuracy depends on the ability to obtain an accurate depth calibration for the concrete. No harmful radiation is emitted and the work can be performed at any time with people in close proximity.',
          process: 'The process begins by using GPR to collect initial scans throughout the area. These scans are used to calibrate the equipment and determine the type of slab, reinforcing patterns, maximum depth penetration, and any other potential limitations. Each location is then scanned in a grid pattern in 4 directions and then rescanned with the antenna cross polarized. The GPR data is viewed in real time and anomalies in the data were located and marked on the surface. Relevant scan examples were saved and will be provided in this report. The EM locator was used to sweep the areas for live power readings and radio frequency signals.',
          limitations: 'Please keep in mind that there are limitations to any subsurface investigation. The equipment may not achieve maximum effectiveness due to conditions in the concrete or soil such as moisture content, age of the concrete, reinforcing size and spacing, and a variety of other factors. Depths are dependent on many factors so depth accuracy can vary throughout a site and should be treated as estimates only. No subsurface investigation or equipment can provide a complete image of what lies below. Our results should always be used in conjunction with as many methods as possible such as consulting existing plans and drawings, visual inspection of above-ground features, drilling or cutting as far as possible from all of our markings, etc.',
          findings: '',
          images: [] as Array<{
            id: string;
            scanImage: { url: string; name: string; uploadDate: string };
            diagramImage: { url: string; name: string; uploadDate: string };
          }>
        }
      });
      // Recargar tareas
      if (userData && userData.businessId) {
        const tasksRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/tasks`);
        const snap = await getDocs(tasksRef);
        const tasksList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTasks(tasksList);
      }
      // Log de historial según la pestaña activa (solo para edición)
      if (!isNewTask && editingTask) {
        let action = 'task_updated';
        let changes: any = {};
        
        switch (activeTaskTab) {
          case 'general': 
            action = 'details_updated';
            // Solo guardar campos que cambiaron en detalles generales
            if (editingTask.title !== newTask.title) changes.title = { old: editingTask.title, new: newTask.title };
            if (editingTask.description !== newTask.description) changes.description = { old: editingTask.description, new: newTask.description };
            if (editingTask.duration !== newTask.duration) changes.duration = { old: editingTask.duration, new: newTask.duration };
            if (editingTask.durationUnit !== newTask.durationUnit) changes.durationUnit = { old: editingTask.durationUnit, new: newTask.durationUnit };
            if (editingTask.startTime !== newTask.startTime) changes.startTime = { old: editingTask.startTime, new: newTask.startTime };
            if (JSON.stringify(editingTask.startDate) !== JSON.stringify(newTask.startDate)) changes.startDate = { old: editingTask.startDate, new: newTask.startDate };
            break;
          case 'checklist': 
            action = 'checklist_updated';
            changes = {
              checklistEnabled: { old: editingTask.checklistEnabled, new: newTask.checklistEnabled },
              checklistItems: { old: editingTask.checklist?.length || 0, new: newTask.checklist?.length || 0 },
              progress: { old: editingTask.progress, new: newTask.progress }
            };
            break;
          case 'location': 
            action = 'location_updated';
            const locationChanges = getObjectChanges(editingTask.location, newTask.location);
            if (Object.keys(locationChanges).length > 0) {
              changes.location = locationChanges;
            }
            break;
          case 'files': 
            action = 'files_updated';
            changes.filesCount = { old: editingTask.documents?.length || 0, new: newTask.documents?.length || 0 };
            break;
          case 'worker': 
            action = 'worker_updated';
            changes.worker = { old: editingTask.worker, new: newTask.worker };
            break;
          default: 
            action = 'task_updated';
            changes = getObjectChanges(editingTask, newTask);
        }
        
        // Solo registrar si hay cambios
        if (Object.keys(changes).length > 0) {
          await logTaskHistoryEntry(newTask.id, action, changes);
        }
      }
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Error saving task');
    }
  };



  // Funciones para manejar documentos del proyecto
  const handleProjectDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!userData?.businessId || !projectId) return;

    setUploadingDocument(true);

    try {
      const uploadedDocs = await Promise.all(
        Array.from(files).map(async (file) => {
          // Generar un ID único para el documento
          const docId = `project-${Date.now()}-${Math.random()}`;
          // Ruta en Storage: projects/{businessId}/documents/{filename}
          const storageRef = ref(storage, `projects/${userData.businessId}/documents/${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          return {
            id: docId,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadDate: new Date().toISOString(),
            url
          };
        })
      );

      // Guardar en Firestore
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const currentDocuments = projectDoc.data().documents || [];
        const updatedDocuments = [...currentDocuments, ...uploadedDocs];
        
        await updateDoc(projectRef, {
          documents: updatedDocuments
        });

        // Actualizar el estado del proyecto
        setProject((prev: any) => ({
          ...prev,
          documents: updatedDocuments
        }));

        // Recargar documentos para incluir el quotationPDF
        await loadProjectDocuments();
      }

      toast.success('Documentos subidos correctamente');
      await logProjectHistoryEntry('project_updated', { field: 'documents', action: 'add', documents: uploadedDocs });
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Error al subir documentos');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleProjectDocumentDelete = async (documentId: string) => {
    if (!userData?.businessId || !projectId) return;

    try {
      // Si es el PDF del quotation, no permitir eliminarlo
      if (documentId === 'quotation-pdf') {
        toast.error('No se puede eliminar el PDF de la cotización');
        return;
      }

      // Remover del estado local
      setProjectDocuments((prev: ProjectDocument[]) => prev.filter((doc: ProjectDocument) => doc.id !== documentId));

      // Actualizar en Firestore
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const currentDocuments = projectDoc.data().documents || [];
        const updatedDocuments = currentDocuments.filter((doc: any) => doc.id !== documentId);
        
        await updateDoc(projectRef, {
          documents: updatedDocuments
        });

        // Actualizar el estado del proyecto
        setProject((prev: any) => ({
          ...prev,
          documents: updatedDocuments
        }));
      }

      toast.success('Documento eliminado correctamente');
      await logProjectHistoryEntry('project_updated', { field: 'documents', action: 'delete', documentId });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error al eliminar documento');
    }
  };

  // Funciones para manejar archivos de tareas
  const handleTaskFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!userData?.businessId || !projectId || !editingTask?.id) return;

    setUploadingTaskFile(true);

    try {
      const uploadedFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          // Generar un ID único para el archivo
          const fileId = `task-${Date.now()}-${Math.random()}`;
          // Ruta en Storage: projects/{businessId}/tasks/{taskId}/files/{filename}
          const storageRef = ref(storage, `projects/${userData.businessId}/tasks/${editingTask.id}/files/${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          return {
            id: fileId,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadDate: new Date().toISOString(),
            url
          };
        })
      );

      // Actualizar el estado local
      setTaskFiles((prev) => [...prev, ...uploadedFiles]);

      // Guardar en Firestore
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        const updatedTasks = projectData.tasks.map((task: any) => {
          if (task.id === editingTask.id) {
            const currentFiles = task.files || [];
            return { ...task, files: [...currentFiles, ...uploadedFiles] };
          }
          return task;
        });
        
        await updateDoc(projectRef, { tasks: updatedTasks });
        
        // Actualizar el estado del proyecto
        setProject((prev: any) => ({
          ...prev,
          tasks: updatedTasks
        }));
      }

      toast.success(t('projects.kanban.fileUploaded'));
    } catch (error) {
      console.error('Error uploading task files:', error);
      toast.error(t('projects.kanban.uploadError'));
    } finally {
      setUploadingTaskFile(false);
    }
  };

  const handleTaskFileDelete = async (fileId: string) => {
    if (!userData?.businessId || !projectId || !editingTask?.id) return;

    try {
      // Remover del estado local
      setTaskFiles((prev) => prev.filter((file) => file.id !== fileId));

      // Actualizar en Firestore
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        const updatedTasks = projectData.tasks.map((task: any) => {
          if (task.id === editingTask.id) {
            const currentFiles = task.files || [];
            return { ...task, files: currentFiles.filter((file: any) => file.id !== fileId) };
          }
          return task;
        });
        
        await updateDoc(projectRef, { tasks: updatedTasks });
        
        // Actualizar el estado del proyecto
        setProject((prev: any) => ({
          ...prev,
          tasks: updatedTasks
        }));
      }

      toast.success(t('projects.kanban.fileDeleted'));
    } catch (error) {
      console.error('Error deleting task file:', error);
      toast.error(t('projects.kanban.deleteError'));
    }
  };

  // Funciones para manejar imágenes de scanning
  const handleAddScanningImageSet = () => {
    const imageSetId = `scan-${Date.now()}-${Math.random()}`;
    setTaskFormData((prev: typeof taskFormData) => ({
      ...prev,
      scanningData: {
        ...prev.scanningData,
        images: [
          ...prev.scanningData.images,
          {
            id: imageSetId,
            scanImage: { url: '', name: '', uploadDate: '' },
            diagramImage: { url: '', name: '', uploadDate: '' }
          }
        ]
      }
    }));
  };

  const handleScanningImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    imageSetId: string,
    imageType: 'scanImage' | 'diagramImage'
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!userData?.businessId || !projectId || !editingTask?.id) return;

    const fileKey = `${imageSetId}-${imageType}`;
    setUploadingScanningImages((prev) => ({ ...prev, [fileKey]: true }));

    try {
      const file = files[0];
      const fileExtension = file.name.split('.').pop();
      const fileName = `${imageSetId}-${imageType}.${fileExtension}`;
      
      // Ruta en Storage: projects/{businessId}/tasks/{taskId}/scanning/{fileName}
      const storageRef = ref(storage, `projects/${userData.businessId}/tasks/${editingTask.id}/scanning/${fileName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Actualizar el estado local
      setTaskFormData((prev: typeof taskFormData) => ({
        ...prev,
        scanningData: {
          ...prev.scanningData,
          images: prev.scanningData.images.map((img: any) =>
            img.id === imageSetId
              ? {
                  ...img,
                  [imageType]: {
                    url,
                    name: file.name,
                    uploadDate: new Date().toISOString()
                  }
                }
              : img
          )
        }
      }));

      toast.success(t('projects.kanban.fileUploaded'));
    } catch (error) {
      console.error('Error uploading scanning image:', error);
      toast.error(t('projects.kanban.uploadError'));
    } finally {
      setUploadingScanningImages((prev) => {
        const newState = { ...prev };
        delete newState[`${imageSetId}-${imageType}`];
        return newState;
      });
    }
  };

  const handleChecklistChange = (itemText: string, checked: boolean) => {
    setTaskFormData((prev: typeof taskFormData) => {
      const currentItems = prev.scanningData?.checklistItems || [];
      let updatedItems: string[];
      
      if (checked) {
        updatedItems = [...currentItems, itemText];
      } else {
        updatedItems = currentItems.filter((item: string) => item !== itemText);
      }
      
      // Generar el texto de findings automáticamente
      const generatedFindings = generateFindingsText(updatedItems);
      
      return {
        ...prev,
        scanningData: {
          ...prev.scanningData,
          checklistItems: updatedItems,
          findings: generatedFindings
        }
      };
    });
  };

  const handleRemoveScanningImageSet = (imageSetId: string) => {
    setTaskFormData((prev: typeof taskFormData) => ({
      ...prev,
      scanningData: {
        ...prev.scanningData,
        images: prev.scanningData.images.filter((img: any) => img.id !== imageSetId)
      }
    }));
  };

  // Función para obtener dirección desde coordenadas usando la API de Geocoding de Google
  const extractCoordsFromGoogleMapsUrl = async (url: string) => {
    let urlToProcess = url;

    // Si es una URL acortada (goo.gl o maps.app.goo.gl), expandirla
    if (url.includes('goo.gl') || url.includes('maps.app.goo.gl')) {
      try {
        // Usar un servicio para expandir la URL acortada
        const response = await fetch(`https://unshorten.me/json/${encodeURIComponent(url)}`);
        const data = await response.json();
        if (data.resolved_url) {
          urlToProcess = data.resolved_url;
        } else {
          // Alternativa: intentar expandir manualmente siguiendo la redirección
          const proxyResponse = await fetch(url, { method: 'HEAD', redirect: 'follow' }).catch(() => null);
          if (proxyResponse?.url) {
            urlToProcess = proxyResponse.url;
          }
        }
      } catch (error) {
        console.log('No se pudo expandir la URL acortada, intentando con la original');
      }
    }
    
    // Patrón 1: /@lat,lng,zoom
    const pattern1 = /@(-?\d+\.?\d*),(-?\d+\.?\d*),(\d+\.?\d*)[mz]/;
    const match1 = urlToProcess.match(pattern1);
    if (match1) {
      return { lat: parseFloat(match1[1]), lng: parseFloat(match1[2]) };
    }
    
    // Patrón 2: /place/lat+lng o coordenadas en formato DMS (grados, minutos, segundos)
    const pattern2 = /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/;
    const match2 = urlToProcess.match(pattern2);
    if (match2) {
      return { lat: parseFloat(match2[1]), lng: parseFloat(match2[2]) };
    }
    
    // Patrón 3: ?q=lat,lng
    const pattern3 = /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
    const match3 = urlToProcess.match(pattern3);
    if (match3) {
      return { lat: parseFloat(match3[1]), lng: parseFloat(match3[2]) };
    }
    
    // Patrón 4: ll=lat,lng
    const pattern4 = /[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
    const match4 = urlToProcess.match(pattern4);
    if (match4) {
      return { lat: parseFloat(match4[1]), lng: parseFloat(match4[2]) };
    }

    return null;
  };

  const fetchAddressFromCoords = async (lat: number, lng: number) => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}&language=es`
    );
    const data = await response.json();
    if (data.status === 'OK' && data.results.length > 0) {
      const result = data.results[0];
      let address = result.formatted_address || '';
      let city = '';
      let state = '';
      let country = '';
      let zipCode = '';
      result.address_components.forEach((comp: any) => {
        if (comp.types.includes('locality')) city = comp.long_name;
        if (comp.types.includes('administrative_area_level_1')) state = comp.long_name;
        if (comp.types.includes('country')) country = comp.long_name;
        if (comp.types.includes('postal_code')) zipCode = comp.long_name;
      });
      return { address, city, state, country, zipCode };
    }
    return {};
  };

  // Funciones para manejo de materiales
  const handleAddMaterial = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!projectId || !userData?.businessId) return;

    const materialToAdd = {
      id: Date.now().toString(),
      name: newMaterial.name,
      description: newMaterial.description,
      quantity: newMaterial.quantity,
      unit: newMaterial.unit,
      price: Number(newMaterial.price),
      addedAt: new Date().toISOString(),
      added: true // Campo para indicar que fue añadido después de la cotización
    };

    const updatedMaterials = [...(project.materials || []), materialToAdd];
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        materials: updatedMaterials
      });
      
      setProject((prev: any) => prev ? { ...prev, materials: updatedMaterials } : null);
      setNewMaterial({
        name: '',
        description: '',
        quantity: 1,
        unit: '',
        price: 0
      });
      setShowAddMaterialModal(false);
      await logProjectHistoryEntry('project_updated', { field: 'materials', action: 'add', material: materialToAdd });
    } catch (error) {
      console.error('Error adding material:', error);
    }
  };

  const handleEditMaterial = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!projectId || !userData?.businessId || !editingMaterial) return;

    const updatedMaterials = project.materials.map((mat: any) => 
      mat.id === editingMaterial.id 
        ? { ...mat, ...newMaterial, price: Number(newMaterial.price) }
        : mat
    );
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        materials: updatedMaterials
      });
      
      setProject((prev: any) => prev ? { ...prev, materials: updatedMaterials } : null);
      setNewMaterial({
        name: '',
        description: '',
        quantity: 1,
        unit: '',
        price: 0
      });
      setShowAddMaterialModal(false);
      setEditingMaterial(null);
      await logProjectHistoryEntry('project_updated', { field: 'materials', action: 'edit', material: { ...editingMaterial, ...newMaterial } });
    } catch (error) {
      console.error('Error editing material:', error);
    }
  };

  const handleDeleteMaterial = async (materialId: string) => {
    if (!projectId || !userData?.businessId) return;

    const materialToDelete = project.materials.find((mat: any) => mat.id === materialId);
    const updatedMaterials = project.materials.filter((mat: any) => mat.id !== materialId);
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        materials: updatedMaterials
      });
      
      setProject((prev: any) => prev ? { ...prev, materials: updatedMaterials } : null);
      await logProjectHistoryEntry('project_updated', { field: 'materials', action: 'delete', material: materialToDelete });
    } catch (error) {
      console.error('Error deleting material:', error);
    }
  };

  // Funciones para manejo de servicios
  const handleAddService = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!projectId || !userData?.businessId) return;

    const serviceToAdd = {
      id: Date.now().toString(),
      name: newService.name,
      description: newService.description,
      quantity: newService.quantity,
      unit: newService.unit,
      price: Number(newService.price),
      addedAt: new Date().toISOString(),
      added: true
    };

    const updatedServices = [...(project.services || []), serviceToAdd];
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        services: updatedServices
      });
      
      setProject((prev: any) => prev ? { ...prev, services: updatedServices } : null);
      setNewService({
        name: '',
        description: '',
        quantity: 1,
        unit: '',
        price: 0
      });
      setShowAddServiceModal(false);
      await logProjectHistoryEntry('project_updated', { field: 'services', action: 'add', service: serviceToAdd });
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleEditService = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!projectId || !userData?.businessId || !editingService) return;

    const updatedServices = project.services.map((serv: any) => 
      serv.id === editingService.id 
        ? { ...serv, ...newService, price: Number(newService.price) }
        : serv
    );
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        services: updatedServices
      });
      
      setProject((prev: any) => prev ? { ...prev, services: updatedServices } : null);
      setNewService({
        name: '',
        description: '',
        quantity: 1,
        unit: '',
        price: 0
      });
      setShowAddServiceModal(false);
      setEditingService(null);
      await logProjectHistoryEntry('project_updated', { field: 'services', action: 'edit', service: { ...editingService, ...newService } });
    } catch (error) {
      console.error('Error editing service:', error);
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!projectId || !userData?.businessId) return;

    const serviceToDelete = project.services.find((serv: any) => serv.id === serviceId);
    const updatedServices = project.services.filter((serv: any) => serv.id !== serviceId);
    
    try {
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      await updateDoc(projectRef, {
        services: updatedServices
      });
      
      setProject((prev: any) => prev ? { ...prev, services: updatedServices } : null);
      await logProjectHistoryEntry('project_updated', { field: 'services', action: 'delete', service: serviceToDelete });
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  // --- useEffect para inicializar el mapa de Google Maps en el tab de ubicación ---
  useEffect(() => {
    if (!showTaskModal || activeTaskTab !== 'location') return;
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const mapId = import.meta.env.VITE_GOOGLE_MAP_ID;
    let markerListener: any = null;
    let mapClickListener: any = null;
    let zoomListener: any = null;

    const initMap = async () => {
      await loadGoogleMapsScript(apiKey, mapId);
      if (!nativeMapRef.current) return;
      const lat = parseFloat(taskFormData.location.latitude) || 37.0902; // USA
      const lng = parseFloat(taskFormData.location.longitude) || -95.7129; // USA
      const zoom = parseInt(taskFormData.location.zoom) || 13;
      // Crear el mapa
      nativeMapInstance.current = new window.google.maps.Map(nativeMapRef.current, {
        center: { lat, lng },
        zoom,
        mapId: mapId || undefined,
      });
      // Crear el marcador draggable
      nativeMarkerInstance.current = new window.google.maps.Marker({
        position: { lat, lng },
        map: nativeMapInstance.current,
        draggable: true,
      });
      // Listener para mover el marcador con drag
      markerListener = nativeMarkerInstance.current.addListener('dragend', async (e: any) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        setTaskFormData((prev: typeof taskFormData) => ({
          ...prev,
          location: {
            ...prev.location,
            latitude: newLat,
            longitude: newLng,
          },
        }));
        // Hacer reverse geocoding
        const addressData = await fetchAddressFromCoords(newLat, newLng);
        setTaskFormData((prev: typeof taskFormData) => ({
          ...prev,
          location: {
            ...prev.location,
            ...addressData,
          },
        }));
      });
      // Listener para mover el marcador con clic en el mapa
      mapClickListener = nativeMapInstance.current.addListener('click', async (e: any) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        nativeMarkerInstance.current.setPosition({ lat: newLat, lng: newLng });
        setTaskFormData((prev: typeof taskFormData) => ({
          ...prev,
          location: {
            ...prev.location,
            latitude: newLat,
            longitude: newLng,
          },
        }));
        // Hacer reverse geocoding
        const addressData = await fetchAddressFromCoords(newLat, newLng);
        setTaskFormData((prev: typeof taskFormData) => ({
          ...prev,
          location: {
            ...prev.location,
            ...addressData,
          },
        }));
      });
      // Listener para sincronizar el zoom del mapa con el campo del formulario
      zoomListener = nativeMapInstance.current.addListener('zoom_changed', () => {
        const newZoom = nativeMapInstance.current.getZoom();
        setTaskFormData((prev: typeof taskFormData) => ({
          ...prev,
          location: {
            ...prev.location,
            zoom: newZoom,
          },
        }));
      });

      // Inicializar Google Places Autocomplete para el campo de dirección
      if (addressInputRef.current && window.google && window.google.maps) {
        autocompleteInstance.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            // Sin especificar 'types' para buscar TODO: direcciones, negocios, lugares, puntos de interés, etc.
            fields: ['address_components', 'formatted_address', 'geometry', 'name']
          }
        );

        // Listener cuando el usuario selecciona un lugar
        autocompleteInstance.current.addListener('place_changed', () => {
          const place = autocompleteInstance.current.getPlace();
          
          if (!place.geometry || !place.geometry.location) {
            console.log('No se encontró información de ubicación para este lugar');
            return;
          }

          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          // Extraer componentes de la dirección
          let address = place.formatted_address || '';
          let city = '';
          let state = '';
          let country = '';
          let zipCode = '';

          if (place.address_components) {
            place.address_components.forEach((comp: any) => {
              if (comp.types.includes('locality')) city = comp.long_name;
              if (comp.types.includes('administrative_area_level_1')) state = comp.long_name;
              if (comp.types.includes('country')) country = comp.long_name;
              if (comp.types.includes('postal_code')) zipCode = comp.long_name;
            });
          }

          // Actualizar el estado del formulario
          setTaskFormData((prev: typeof taskFormData) => ({
            ...prev,
            location: {
              ...prev.location,
              address,
              city,
              state,
              country,
              zipCode,
              latitude: lat,
              longitude: lng,
            },
          }));

          // Actualizar el mapa y el marcador
          if (nativeMapInstance.current && nativeMarkerInstance.current) {
            nativeMapInstance.current.setCenter({ lat, lng });
            nativeMapInstance.current.setZoom(15);
            nativeMarkerInstance.current.setPosition({ lat, lng });
          }
        });
      }
    };
    initMap();
    // Cleanup
    return () => {
      if (markerListener) window.google.maps.event.removeListener(markerListener);
      if (mapClickListener) window.google.maps.event.removeListener(mapClickListener);
      if (zoomListener) window.google.maps.event.removeListener(zoomListener);
      if (autocompleteInstance.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteInstance.current);
        autocompleteInstance.current = null;
      }
      if (nativeMarkerInstance.current) nativeMarkerInstance.current.setMap(null);
      if (nativeMapInstance.current) nativeMapInstance.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTaskModal, activeTaskTab]);

  // --- useEffect para sincronizar el marcador y el zoom si cambian manualmente los campos ---
  useEffect(() => {
    if (!showTaskModal || activeTaskTab !== 'location') return;
    if (!window.google || !window.google.maps || !nativeMapInstance.current || !nativeMarkerInstance.current) return;
    const lat = parseFloat(taskFormData.location.latitude) || 37.0902; // USA
    const lng = parseFloat(taskFormData.location.longitude) || -95.7129; // USA
    const zoom = parseInt(taskFormData.location.zoom) || 13;
    nativeMapInstance.current.setCenter({ lat, lng });
    nativeMarkerInstance.current.setPosition({ lat, lng });
    if (nativeMapInstance.current.getZoom() !== zoom) {
      nativeMapInstance.current.setZoom(zoom);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskFormData.location.latitude, taskFormData.location.longitude, taskFormData.location.zoom, showTaskModal, activeTaskTab]);

  // Estado para el worker seleccionado y la oferta
  const [selectedWorkerForOffer, setSelectedWorkerForOffer] = useState<any>(null);
  const [offerPrice, setOfferPrice] = useState(taskFormData.price || '');
  const [offerUnit, setOfferUnit] = useState('');
  const [offerDuration, setOfferDuration] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);
  const [assignmentRequests, setAssignmentRequests] = useState<any[]>([]);
  const [showAssignmentRequests, setShowAssignmentRequests] = useState(false);
  const [allProjectAssignmentRequests, setAllProjectAssignmentRequests] = useState<any[]>([]);
  const [taskFiles, setTaskFiles] = useState<Array<{
    id: string;
    name: string;
    size: number;
    type: string;
    uploadDate: string;
    url: string;
  }>>([]);
  const [uploadingTaskFile, setUploadingTaskFile] = useState(false);
  const [uploadingScanningImages, setUploadingScanningImages] = useState<{ [key: string]: boolean }>({});
  const [showAddSelfModal, setShowAddSelfModal] = useState(false);
  const [selectedBusinessRole, setSelectedBusinessRole] = useState('Project Manager');
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [editingStaffMember, setEditingStaffMember] = useState<any>(null);
  const [newProjectRole, setNewProjectRole] = useState('');
  const [showStaffMemberModal, setShowStaffMemberModal] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState<any>(null);
  const [canSignForMe, setCanSignForMe] = useState(false);

  // Función para enviar la oferta
  // Funciones para manejar solicitudes de asignación
  const handleAcceptAssignment = async (request: any) => {
    setProcessingAssignment(true);
    try {
      await updateDoc(doc(db, 'assignmentRequests', request.id), { 
        status: 'accepted', 
        updatedAt: new Date().toISOString() 
      });
      
      // Notificar al worker
      await addDoc(collection(db, 'notifications', request.workerId, 'notifications'), {
        type: 'assignmentAccepted',
        messageKey: 'notifications.assignmentAccepted',
        messageParams: {
          taskTitle: request.taskTitle,
          businessId: request.businessId,
          workerId: request.workerId
        },
        assignmentRequestId: request.id,
        businessId: request.businessId,
        workerId: request.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      
      // 1. Obtener comisión y calcular neto
      let percent = 0;
      try {
        const settingsDoc = await getDoc(doc(db, 'site', 'settings'));
        if (settingsDoc.exists()) {
          const com = settingsDoc.data().comissions || {};
          if (request.workerRole === 'worker') {
            percent = parseFloat(com.workerComission || '0');
          } else if (request.workerRole === 'premiumWorker') {
            percent = parseFloat(com.premiumWorkerComission || '0');
          } else if (request.workerRole === 'specialWorker') {
            percent = parseFloat(com.specialWorkerComission || '0');
          }
        }
      } catch (e) {
        percent = 0;
      }
      const total = request.counterPrice ? (request.counterPrice * (request.duration || 1)) : (request.total || 0);
      const fee = Math.round((total * percent) / 100);
      const net = total - fee;
      // 2. Actualizar/crear wallet principal
      const walletRef = doc(db, 'wallet', request.workerId);
      const walletSnap = await getDoc(walletRef);
      let disponible = 0;
      let retirado = 0;
      let pendiente = 0;
      if (walletSnap.exists()) {
        const data = walletSnap.data();
        disponible = data.disponible || 0;
        retirado = data.retirado || 0;
        pendiente = data.pendiente || 0;
      }
      pendiente += net;
      await updateDoc(walletRef, {
        disponible,
        retirado,
        pendiente
      }).catch(async (err) => {
        if (err.code === 'not-found') {
          await setDoc(walletRef, {
            disponible: 0,
            retirado: 0,
            pendiente: net
          });
        } else {
          throw err;
        }
      });
      // 3. Crear transacción en subcolección del wallet del worker
      await addDoc(collection(db, 'wallet', request.workerId, 'transacciones'), {
        tipo: 'pendiente_pago',
        monto: net,
        taskId: request.taskId,
        projectId: request.projectId || null,
        assignmentRequestId: request.id,
        businessId: request.businessId,
        taskTitle: request.taskTitle,
        fecha: new Date(),
        estado: 'pendiente',
        fee,
        feePercent: percent,
        total,
        startDate: request.startDate || null,
        duration: request.duration || null,
      });
      
      // 4. Crear pago en la colección payments del proyecto para que aparezca en Finances
      if (request.projectId && request.businessId) {
        const workerDoc = await getDoc(doc(db, 'users', request.workerId));
        const workerData = workerDoc.exists() ? workerDoc.data() : {};
        const workerName = `${workerData.firstName || ''} ${workerData.lastName || ''}`.trim() || 'Worker';
        
        await addDoc(collection(db, 'projects', request.businessId, 'projects', request.projectId, 'payments'), {
          type: 'expense',
          category: 'Labor',
          description: `Pago a ${workerName} por tarea: ${request.taskTitle}`,
          amount: total,
          netAmount: net,
          fee: fee,
          feePercent: percent,
          workerId: request.workerId,
          workerName: workerName,
          taskId: request.taskId,
          taskTitle: request.taskTitle,
          assignmentRequestId: request.id,
          date: Timestamp.fromDate(new Date()),
          status: 'pendiente',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      // REGISTRAR EN HISTORIAL
      await logOfferAccepted(request);
      setShowAssignmentModal(false);
      setSelectedAssignmentRequest(null);
      try {
        const userRef = doc(db, 'users', request.workerId);
        await updateDoc(userRef, {
          businessesAsignados: arrayUnion(request.businessId),
          proyectosAsignados: arrayUnion(request.projectId),
          tareasAsignadas: arrayUnion({ projectId: request.projectId, taskId: request.taskId, businessId: request.businessId })
        });
      } catch (err) {
        console.error('Error actualizando referencias en el usuario:', err);
      }
    } catch (error) {
      console.error('Error accepting assignment:', error);
    } finally {
      setProcessingAssignment(false);
    }
  };

  const handleRejectAssignment = async (request: any) => {
    setProcessingAssignment(true);
    try {
      await updateDoc(doc(db, 'assignmentRequests', request.id), { 
        status: 'rejected', 
        updatedAt: new Date().toISOString() 
      });
      
      // Notificar al worker
      await addDoc(collection(db, 'notifications', request.workerId, 'notifications'), {
        type: 'assignmentRejected',
        messageKey: 'notifications.assignmentRejected',
        messageParams: {
          taskTitle: request.taskTitle,
          businessId: request.businessId,
          workerId: request.workerId
        },
        assignmentRequestId: request.id,
        businessId: request.businessId,
        workerId: request.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      
      // ✅ REGISTRAR EN HISTORIAL
      await logOfferRejected(request);
      
      setShowAssignmentModal(false);
      setSelectedAssignmentRequest(null);
    } catch (error) {
      console.error('Error rejecting assignment:', error);
    } finally {
      setProcessingAssignment(false);
    }
  };

  const handleCounterAssignment = async (request: any) => {
    if (!counterPrice) return;
    setProcessingAssignment(true);
    try {
      const price = Number(counterPrice);
      const duration = request.duration || 1;
      const total = price * duration;
      
      await updateDoc(doc(db, 'assignmentRequests', request.id), {
        status: 'counter',
        counterPrice: price,
        total: total,
        updatedAt: new Date().toISOString(),
        lastCounterBy: 'business'
      });
      
      // Notificar al worker
      await addDoc(collection(db, 'notifications', request.workerId, 'notifications'), {
        type: 'assignmentCounter',
        messageKey: 'notifications.assignmentCounter',
        messageParams: {
          taskTitle: request.taskTitle,
          businessId: request.businessId,
          workerId: request.workerId,
          counterPrice: price,
          total: total
        },
        assignmentRequestId: request.id,
        businessId: request.businessId,
        workerId: request.workerId,
        createdAt: new Date().toISOString(),
        read: false
      });
      
      // ✅ REGISTRAR EN HISTORIAL
      await logCounterOffer(request, {
        price: price,
        total: total
      });
      
      setShowCounterOffer(false);
      setShowAssignmentModal(false);
      setSelectedAssignmentRequest(null);
      setCounterPrice('');
    } catch (error) {
      console.error('Error sending counter offer:', error);
    } finally {
      setProcessingAssignment(false);
    }
  };

  const handleSendOffer = async () => {
    if (!selectedWorkerForOffer || !userData?.businessId || !projectId) return;
    
    // Validar que todos los campos requeridos estén completos
    if (!offerPrice || !offerUnit || !offerDuration) {
      toast.error(t('projects.workerAssignment.completeAllFields') || 'Por favor completa todos los campos');
      return;
    }
    setSendingOffer(true);
    try {
      const assignmentRequest = {
        businessId: userData.businessId,
        projectId: projectId, // ✅ AGREGAR PROJECT ID
        workerId: selectedWorkerForOffer.uid,
        taskId: editingTask ? editingTask.id : Date.now().toString(),
        taskTitle: taskFormData.title,
        taskDescription: taskFormData.description,
        startDate: taskFormData.startDate,
        startTime: taskFormData.startTime,
        duration: Number(taskFormData.duration),
        price: Number(offerPrice),
        priceUnit: offerUnit,
        counterPrice: Number(offerPrice),
        lastCounterBy: 'business',
        total: Number(offerPrice) * Number(offerDuration),
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        location: { ...taskFormData.location },
      };
      // Generar un UID único
      const db = getFirestore();
      const docRef = doc(collection(db, 'assignmentRequests'));
      await setDoc(docRef, assignmentRequest);
      setSelectedWorkerForOffer(null);
      setOfferPrice('');
      setOfferUnit('');
      setOfferDuration('');
      
      // ✅ REGISTRAR EN HISTORIAL
      await logOfferSent(selectedWorkerForOffer, {
        price: Number(offerPrice),
        priceUnit: offerUnit,
        duration: Number(offerDuration),
        total: Number(offerPrice) * Number(offerDuration)
      });
      
      // Actualizar la lista de solicitudes de asignación
      setAssignmentRequests(prev => [...prev, { id: docRef.id, ...assignmentRequest }]);
      setShowAssignmentRequests(true);
      
      toast.success(t('projects.workerAssignment.offerSent'));
    } catch (error) {
      toast.error(t('projects.workerAssignment.offerError'));
    }
    setSendingOffer(false);
  };

  // Efecto para asignar automáticamente el worker a la tarea si la oferta es aceptada
  useEffect(() => {
    if (!userData?.businessId || !projectId) return;
    const db = getFirestore();
    
    // Escuchar todas las solicitudes aceptadas para este proyecto
    const q = query(
      collection(db, 'assignmentRequests'),
      where('businessId', '==', userData.businessId),
      where('status', '==', 'accepted')
    );
    const unsub = onSnapshot(q, async (snap) => {
      if (snap.empty) return;
      
      // Procesar todas las solicitudes aceptadas
      for (const docSnapshot of snap.docs) {
        const req = docSnapshot.data();
        
        // Verificar si esta solicitud ya fue procesada
        if (req.processed) continue;
        
        // Actualizar assignedTo y precio final en la tarea
        const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const projectData = projectSnap.data() as any;
          if (Array.isArray(projectData.tasks)) {
            const idx = projectData.tasks.findIndex((t: any) => t.id === req.taskId);
            if (idx !== -1) {
              const updatedTasks = [...projectData.tasks];
              const currentTask = updatedTasks[idx];
              
              // Si ya hay workers asignados, agregar el nuevo. Si no, crear array con el worker
              const currentWorkers = currentTask.worker ? 
                (Array.isArray(currentTask.worker) ? currentTask.worker : [currentTask.worker]) : 
                [];
              
              const newWorkers = currentWorkers.includes(req.workerId) ? 
                currentWorkers : 
                [...currentWorkers, req.workerId];
              
              updatedTasks[idx] = { 
                ...currentTask, 
                worker: newWorkers,
                // Mantener assignedTo para compatibilidad
                assignedTo: req.workerId,
                // Guardar el precio final acordado (counterPrice o price)
                // Usar nullish coalescing para evitar valores `undefined` que Firestore rechaza
                finalPrice: req.counterPrice ?? req.price ?? null,
                finalPriceUnit: req.priceUnit ?? null,
                finalDuration: req.duration ?? null,
                finalTotal: req.total ?? null,
                assignmentRequestId: req.id ?? null
              };
              
              await updateDoc(projectRef, { tasks: updatedTasks });
              
              // Marcar la solicitud como procesada para evitar duplicados
              await updateDoc(doc(db, 'assignmentRequests', docSnapshot.id), { processed: true });
              
              toast.success(t('projects.workerAssignment.workerAssigned', { name: req.workerId }));
              console.log('✅ Tarea actualizada con información de pago:', {
                taskId: req.taskId,
                finalPrice: req.counterPrice || req.price,
                finalPriceUnit: req.priceUnit,
                finalDuration: req.duration,
                finalTotal: req.total
              });
            }
          }
        }
      }
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.businessId, projectId]);

  // Log de usuario, business y project al renderizar
  useEffect(() => {
    console.log('[ProjectView] Usuario logueado:', currentUser);
    console.log('[ProjectView] userData:', userData);
    if (userData?.businessId) {
      console.log('[ProjectView] businessId:', userData.businessId);
    }
    if (project) {
      console.log('[ProjectView] Proyecto cargado:', project);
    }
  }, [currentUser, userData, project]);

  // Función para sincronizar pagos de assignmentRequests aceptadas a la colección payments
  const syncWorkerPayments = async () => {
    if (!userData?.businessId || !projectId) return;
    
    try {
      console.log('[ProjectView] Sincronizando pagos de workers...');
      
      // 1. Obtener todas las assignment requests aceptadas del proyecto
      const acceptedRequestsQuery = query(
        collection(db, 'assignmentRequests'),
        where('businessId', '==', userData.businessId),
        where('projectId', '==', projectId),
        where('status', '==', 'accepted')
      );
      
      const acceptedRequestsSnap = await getDocs(acceptedRequestsQuery);
      
      if (acceptedRequestsSnap.empty) {
        console.log('[ProjectView] No hay assignment requests aceptadas para sincronizar');
        return;
      }
      
      // 2. Obtener los pagos existentes en la colección payments
      const paymentsQuery = collection(db, 'projects', userData.businessId, 'projects', projectId, 'payments');
      const paymentsSnap = await getDocs(paymentsQuery);
      const existingPaymentRequestIds = new Set(
        paymentsSnap.docs
          .map(doc => doc.data().assignmentRequestId)
          .filter(Boolean)
      );
      
      // 3. Por cada assignment request aceptada, verificar si ya existe un pago
      let syncedCount = 0;
      for (const requestDoc of acceptedRequestsSnap.docs) {
        const request = { id: requestDoc.id, ...requestDoc.data() } as any;
        
        // Si ya existe un pago para esta request, saltarla
        if (existingPaymentRequestIds.has(request.id)) {
          continue;
        }
        
        // Calcular montos (igual que en handleAcceptAssignment)
        let percent = 0;
        try {
          const settingsDoc = await getDoc(doc(db, 'site', 'settings'));
          if (settingsDoc.exists()) {
            const com = settingsDoc.data().comissions || {};
            if (request.workerRole === 'worker') {
              percent = parseFloat(com.workerComission || '0');
            } else if (request.workerRole === 'premiumWorker') {
              percent = parseFloat(com.premiumWorkerComission || '0');
            } else if (request.workerRole === 'specialWorker') {
              percent = parseFloat(com.specialWorkerComission || '0');
            }
          }
        } catch (e) {
          percent = 0;
        }
        
        const total = request.counterPrice ? (request.counterPrice * (request.duration || 1)) : (request.total || 0);
        const fee = Math.round((total * percent) / 100);
        const net = total - fee;
        
        // Obtener nombre del worker
        const workerDoc = await getDoc(doc(db, 'users', request.workerId));
        const workerData = workerDoc.exists() ? workerDoc.data() : {};
        const workerName = `${workerData.firstName || ''} ${workerData.lastName || ''}`.trim() || 'Worker';
        
        // Crear pago en la colección payments
        await addDoc(collection(db, 'projects', userData.businessId, 'projects', projectId, 'payments'), {
          type: 'expense',
          category: 'Labor',
          description: `Pago a ${workerName} por tarea: ${request.taskTitle}`,
          amount: total,
          netAmount: net,
          fee: fee,
          feePercent: percent,
          workerId: request.workerId,
          workerName: workerName,
          taskId: request.taskId,
          taskTitle: request.taskTitle,
          assignmentRequestId: request.id,
          date: request.createdAt ? Timestamp.fromDate(new Date(request.createdAt)) : Timestamp.fromDate(new Date()),
          status: 'pendiente',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        syncedCount++;
      }
      
      if (syncedCount > 0) {
        console.log(`[ProjectView] ✅ ${syncedCount} pagos sincronizados exitosamente`);
        toast.success(`${syncedCount} pagos de workers sincronizados`);
      } else {
        console.log('[ProjectView] Todos los pagos ya estaban sincronizados');
      }
    } catch (error) {
      console.error('[ProjectView] Error sincronizando pagos:', error);
      toast.error('Error al sincronizar pagos de workers');
    }
  };

  // Ejecutar sincronización cuando se cargue el proyecto
  useEffect(() => {
    if (project && userData?.businessId && projectId) {
      syncWorkerPayments();
    }
  }, [project?.id, userData?.businessId, projectId]);

  // 2. Definición de onDragEnd para DragDropContext
  const onDragEnd = async (result: any) => {
    if (!result.destination) return;
    
    console.log('Drag result:', result);
    console.log('Columns:', columns);
    console.log('Tasks:', tasks);
    
    // Si es drag de tarea (por defecto, react-beautiful-dnd no especifica tipo)
    const sourceColIdx = columns.findIndex(col => col.id === result.source.droppableId);
    const destColIdx = columns.findIndex(col => col.id === result.destination.droppableId);
    
    console.log('Source column index:', sourceColIdx, 'Destination column index:', destColIdx);
    
    if (sourceColIdx === -1 || destColIdx === -1) {
      console.error('No se encontraron las columnas de origen o destino');
      return;
    }
    
    // Encontrar la tarea que se está moviendo
    const sourceTasks = tasks.filter(t => t.columnId === columns[sourceColIdx].id);
    console.log('Source tasks:', sourceTasks);
    console.log('Source index:', result.source.index);
    
    const taskToMove = sourceTasks[result.source.index];
    
    if (!taskToMove) {
      console.error('No se encontró la tarea a mover');
      console.log('Available tasks in source column:', sourceTasks);
      return;
    }
    
    console.log('Task to move:', taskToMove);
    
    // Crear nueva lista de tareas con la tarea actualizada
    const newTasks = tasks.map(t => {
      if (t.id === taskToMove.id) {
        console.log('Updating task:', t.title, 'to column:', columns[destColIdx].name);
        return { ...t, columnId: columns[destColIdx].id };
      }
      return t;
    });
    
    console.log('New tasks array:', newTasks);
    
    // Guardar el cambio en la subcolección de la tarea (es la fuente de verdad)
    try {
      if (userData?.businessId && projectId) {
        const taskDocRef = doc(db, `projects/${userData.businessId}/projects/${projectId}/tasks/${taskToMove.id}`);
        await updateDoc(taskDocRef, { columnId: columns[destColIdx].id });
        // Actualizar estado local inmediatamente
        const updatedTasks = newTasks;
        setTasks(updatedTasks);
        // También reflejar en el documento del proyecto si lo deseas (opcional)
        setProject((prev: any) => prev ? { ...prev, tasks: updatedTasks } : prev);
        console.log('Tarea movida exitosamente:', taskToMove.title, 'de', columns[sourceColIdx].name, 'a', columns[destColIdx].name);
      } else {
        // Fallback: actualizar sólo el estado local
        setTasks(newTasks);
        console.log('Tarea movida localmente (no autenticado):', taskToMove.title);
      }
    } catch (error) {
      console.error('Error al mover tarea:', error);
      toast.error('Error al mover tarea. Revisa la consola.');
    }
  };

  // 3. Definición de handleAssignWorker para el modal de asignación de trabajador
  const handleAssignWorker = (worker: WorkerType) => {
    setSelectedWorkerForOffer(worker);
    setOfferPrice(taskFormData.price || '');
    setOfferUnit('');
    setOfferDuration('');
    setShowAssignWorkerModal(false);
    setActiveTaskTab('worker');
  };

  // Funciones para manejar el checklist
  const handleToggleChecklist = () => {
    setTaskFormData((prev: typeof taskFormData) => ({
      ...prev,
      checklistEnabled: !prev.checklistEnabled
    }));
  };

  const handleAddChecklistItem = () => {
    const newItem = {
      id: Date.now().toString(),
      text: '',
      completed: false
    };
    setTaskFormData((prev: typeof taskFormData) => ({
      ...prev,
      checklist: [...prev.checklist, newItem]
    }));
  };

  const handleUpdateChecklistItem = async (itemId: string, field: 'text' | 'completed', value: string | boolean) => {
    setTaskFormData((prev: typeof taskFormData) => {
      const updatedChecklist = prev.checklist.map((item: any) => 
        item.id === itemId ? { ...item, [field]: value } : item
      );
      
      // Si se está marcando un check como completado, cambiar a "En Progreso"
      let newColumnId = prev.columnId;
      if (field === 'completed' && value === true && prev.columnId === 'new') {
        newColumnId = 'in_progress';
      }
      
      // Si todos los items del checklist están completados, cambiar a "Completado"
      if (field === 'completed' && value === true) {
        const allCompleted = updatedChecklist.every((item: any) => item.completed);
        if (allCompleted && updatedChecklist.length > 0) {
          newColumnId = 'done';
        }
      }
      
      // Calcular progreso automático si el checklist está activo
      let updatedProgress = prev.progress;
      if (prev.checklistEnabled && updatedChecklist.length > 0) {
        const completedItems = updatedChecklist.filter((item: any) => item.completed).length;
        updatedProgress = Math.round((completedItems / updatedChecklist.length) * 100);
      }
      
      return {
        ...prev,
        columnId: newColumnId,
        checklist: updatedChecklist,
        progress: updatedProgress
      };
    });
    
    // Recalcular el progreso del proyecto después de actualizar el checklist
    // Esperar un poco para que el estado se actualice
    setTimeout(() => {
      calculateProjectProgress();
    }, 500);
  };

  const handleDeleteChecklistItem = (itemId: string) => {
    setTaskFormData((prev: typeof taskFormData) => ({
      ...prev,
      checklist: prev.checklist.filter((item: any) => item.id !== itemId)
    }));
  };

  // Calcular progreso automático basado en checklist
  const calculateChecklistProgress = () => {
    if (!taskFormData.checklistEnabled || taskFormData.checklist.length === 0) {
      return taskFormData.progress || 0;
    }
    const completedItems = taskFormData.checklist.filter((item: any) => item.completed).length;
    return Math.round((completedItems / taskFormData.checklist.length) * 100);
  };

  // Función para obtener el progreso actual de la tarea
  const getCurrentProgress = () => {
    if (taskFormData.checklistEnabled && taskFormData.checklist.length > 0) {
      return calculateChecklistProgress();
    }
    return taskFormData.progress || 0;
  };


  // Función para quitar worker asignado
  const handleRemoveWorker = async (workerId: string) => {
    if (!editingTask || !userData?.businessId || !projectId) return;
    try {
      const db = getFirestore();
      const projectRef = doc(db, `projects/${userData.businessId}/projects/${projectId}`);
      const projectSnap = await getDoc(projectRef);
      if (projectSnap.exists()) {
        const projectData = projectSnap.data() as any;
        if (Array.isArray(projectData.tasks)) {
          const idx = projectData.tasks.findIndex((t: any) => t.id === editingTask.id);
          if (idx !== -1) {
            const updatedTasks = [...projectData.tasks];
            const currentTask = updatedTasks[idx];
            // Remover worker del array de workers
            let updatedWorkers = [];
            if (currentTask.worker) {
              if (Array.isArray(currentTask.worker)) {
                updatedWorkers = currentTask.worker.filter((w: string) => w !== workerId);
              } else {
                updatedWorkers = currentTask.worker === workerId ? [] : [currentTask.worker];
              }
            }
            // Limpiar campos de asignación y pago
            const updatedTask = {
              ...currentTask,
              worker: updatedWorkers.length > 0 ? updatedWorkers : null,
              assignedTo: updatedWorkers.length > 0 ? updatedWorkers[0] : null,
              finalPrice: null,
              finalPriceUnit: null,
              finalDuration: null,
              finalTotal: null,
              assignmentRequestId: null
            };
            updatedTasks[idx] = updatedTask;
            await updateDoc(projectRef, { tasks: updatedTasks });
            // Actualizar assignmentRequest a 'removed'
            const q = query(
              collection(db, 'assignmentRequests'),
              where('businessId', '==', userData.businessId),
              where('taskId', '==', editingTask.id),
              where('workerId', '==', workerId),
              where('status', '==', 'accepted')
            );
            const snap = await getDocs(q);
            for (const docu of snap.docs) {
              await updateDoc(doc(db, 'assignmentRequests', docu.id), {
                status: 'removed',
                updatedAt: new Date().toISOString()
              });
            }
            // Refrescar assignmentRequests en la UI
            const qAll = query(
              collection(db, 'assignmentRequests'),
              where('businessId', '==', userData.businessId),
              where('taskId', '==', editingTask.id)
            );
            const snapAll = await getDocs(qAll);
            const requests = snapAll.docs.map(d => ({ id: d.id, ...d.data() }));
            setAssignmentRequests(requests);
            setShowAssignmentRequests(requests.length > 0);
            // Registrar en historial
            await logWorkerRemoved(workerId, {
              finalPrice: currentTask.finalPrice,
              finalPriceUnit: currentTask.finalPriceUnit,
              finalDuration: currentTask.finalDuration,
              finalTotal: currentTask.finalTotal,
              assignmentRequestId: currentTask.assignmentRequestId
            });
            // Actualizar el estado local del modal
            setTaskFormData((prev: typeof taskFormData) => ({
              ...prev,
              worker: updatedWorkers.length > 0 ? updatedWorkers : null,
              assignedTo: updatedWorkers.length > 0 ? updatedWorkers[0] : null,
              finalPrice: null,
              finalPriceUnit: null,
              finalDuration: null,
              finalTotal: null,
              assignmentRequestId: null
            }));
            // Actualizar el estado global de tasks
            setTasks(prev => prev.map(t =>
              t.id === editingTask.id
                ? { ...t, worker: updatedWorkers.length > 0 ? updatedWorkers : null, assignedTo: updatedWorkers.length > 0 ? updatedWorkers[0] : null, finalPrice: null, finalPriceUnit: null, finalDuration: null, finalTotal: null, assignmentRequestId: null }
                : t
            ));
            toast.success('Worker removido exitosamente');
            console.log('✅ Worker removido de la tarea:', workerId);
          }
        }
      }
    } catch (error) {
      console.error('Error al remover worker:', error);
      toast.error('Error al remover worker');
    }
  };

  // ✅ FUNCIONES PARA HISTORIAL DE WORKERS
  const addWorkerHistoryEntry = async (taskId: string, action: string, data: any) => {
    if (!userData?.businessId || !projectId) return;
    
    try {
      const db = getFirestore();
      const historyEntry = {
        taskId,
        projectId,
        businessId: userData.businessId,
                 businessName: (userData as any).businessName || (userData as any).email || 'Business',
         action, // 'offer_sent', 'offer_accepted', 'offer_rejected', 'counter_offer', 'worker_removed', etc.
        data,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        performedBy: {
          uid: currentUser?.uid || 'unknown',
          email: currentUser?.email || 'unknown',
          role: userData.role || 'business'
        }
      };
      
      await addDoc(collection(db, 'taskWorkerHistory'), historyEntry);
      console.log('📝 Historial de worker registrado:', historyEntry);
    } catch (error) {
      console.error('Error al registrar historial de worker:', error);
    }
  };

  // ✅ REGISTRAR ENVÍO DE OFERTA
  const logOfferSent = async (worker: any, offerData: any) => {
    await addWorkerHistoryEntry(editingTask?.id || 'new-task', 'offer_sent', {
      worker: {
        uid: worker.uid,
        name: `${worker.firstName} ${worker.lastName}`,
        email: worker.email
      },
      offer: {
        price: offerData.price,
        priceUnit: offerData.priceUnit,
        duration: offerData.duration,
        total: offerData.total,
        status: 'pending'
      },
      taskInfo: {
        title: taskFormData.title,
        description: taskFormData.description
      }
    });
  };

  // ✅ REGISTRAR ACEPTACIÓN DE OFERTA
  const logOfferAccepted = async (request: any) => {
    await addWorkerHistoryEntry(request.taskId, 'offer_accepted', {
      worker: {
        uid: request.workerId,
        name: workers.find(w => w.uid === request.workerId)?.firstName + ' ' + workers.find(w => w.uid === request.workerId)?.lastName,
        email: workers.find(w => w.uid === request.workerId)?.email
      },
      finalOffer: {
        price: request.counterPrice || request.price,
        priceUnit: request.priceUnit,
        duration: request.duration,
        total: request.total,
        assignmentRequestId: request.id
      },
      taskInfo: {
        title: request.taskTitle,
        description: request.taskDescription
      }
    });
  };

  // ✅ REGISTRAR RECHAZO DE OFERTA
  const logOfferRejected = async (request: any) => {
    await addWorkerHistoryEntry(request.taskId, 'offer_rejected', {
      worker: {
        uid: request.workerId,
        name: workers.find(w => w.uid === request.workerId)?.firstName + ' ' + workers.find(w => w.uid === request.workerId)?.lastName,
        email: workers.find(w => w.uid === request.workerId)?.email
      },
      rejectedOffer: {
        price: request.price,
        priceUnit: request.priceUnit,
        duration: request.duration,
        total: request.total,
        assignmentRequestId: request.id
      },
      taskInfo: {
        title: request.taskTitle,
        description: request.taskDescription
      }
    });
  };

  // ✅ REGISTRAR CONTRADEMANDA
  const logCounterOffer = async (request: any, counterData: any) => {
    await addWorkerHistoryEntry(request.taskId, 'counter_offer', {
      worker: {
        uid: request.workerId,
        name: workers.find(w => w.uid === request.workerId)?.firstName + ' ' + workers.find(w => w.uid === request.workerId)?.lastName,
        email: workers.find(w => w.uid === request.workerId)?.email
      },
      originalOffer: {
        price: request.price,
        priceUnit: request.priceUnit,
        duration: request.duration,
        total: request.total
      },
      counterOffer: {
        price: counterData.price,
        priceUnit: request.priceUnit,
        duration: request.duration,
        total: counterData.total,
        initiatedBy: 'business'
      },
      assignmentRequestId: request.id,
      taskInfo: {
        title: request.taskTitle,
        description: request.taskDescription
      }
    });
  };

  // ✅ REGISTRAR REMOCIÓN DE WORKER
  const logWorkerRemoved = async (workerId: string, previousAssignment: any) => {
    const worker = workers.find(w => w.uid === workerId);
    await addWorkerHistoryEntry(editingTask?.id || 'unknown', 'worker_removed', {
      worker: {
        uid: workerId,
        name: worker ? `${worker.firstName} ${worker.lastName}` : 'Worker desconocido',
        email: worker?.email || 'N/A'
      },
      previousAssignment,
      removalReason: 'Manual removal by business',
      taskInfo: {
        title: editingTask?.title || 'Tarea desconocida',
        description: editingTask?.description || 'Sin descripción'
      }
    });
  };

  // Nueva función general para log de historial de task
  const logTaskHistoryEntry = async (taskId: string, action: string, data: any) => {
    if (!userData?.businessId || !projectId) return;
    try {
      const db = getFirestore();
      const historyEntry = {
        taskId,
        projectId,
        businessId: userData.businessId,
        businessName: (userData as any).businessName || (userData as any).email || 'Business',
        action, // 'details_updated', 'checklist_updated', 'location_updated', 'files_updated', etc.
        data,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        performedBy: {
          uid: currentUser?.uid || 'unknown',
          email: currentUser?.email || 'unknown',
          role: userData?.role || 'business'
        }
      };
      // Guardar en la subcolección de history del proyecto
      const historyRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/history`);
      await addDoc(historyRef, historyEntry);
      // Guardar en la subcolección de history de la tarea
      if (taskId) {
        const taskHistoryRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/tasks/${taskId}/history`);
        await addDoc(taskHistoryRef, historyEntry);
      }
      console.log('📝 Historial de task registrado en proyecto y task:', historyEntry);
    } catch (error) {
      console.error('Error al registrar historial de task:', error);
    }
  };

  // useEffect para cargar tareas desde la subcolección en tiempo real
  useEffect(() => {
    if (!userData?.businessId || !projectId) return;
    
    const tasksRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/tasks`);
    const unsubscribe = onSnapshot(tasksRef, (snap) => {
      const tasksList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTasks(tasksList);
    }, (error) => {
      console.error('Error escuchando tareas:', error);
    });
    
    return () => unsubscribe();
  }, [userData?.businessId, projectId]);

  // Eliminar tarea
  const handleDeleteTask = async (taskId: string) => {
    if (!userData?.businessId || !projectId || !taskId) return;
    
    // Validación 1: Verificar si tiene worker asignado en el campo worker
    const hasWorkerAssigned = editingTask && editingTask.worker && (
      (Array.isArray(editingTask.worker) && editingTask.worker.length > 0) ||
      (!Array.isArray(editingTask.worker) && editingTask.worker)
    );
    
    // Validación 2: Verificar si tiene assignment requests aceptadas
    let hasAcceptedRequest = false;
    try {
      const acceptedRequestsQuery = query(
        collection(db, 'assignmentRequests'),
        where('businessId', '==', userData.businessId),
        where('taskId', '==', taskId),
        where('status', '==', 'accepted')
      );
      const acceptedRequestsSnap = await getDocs(acceptedRequestsQuery);
      hasAcceptedRequest = !acceptedRequestsSnap.empty;
    } catch (error) {
      console.error('Error verificando assignment requests:', error);
    }
    
    // No permitir borrar si tiene worker asignado o assignment request aceptada
    if (hasWorkerAssigned || hasAcceptedRequest) {
      setShowDeleteErrorModal(true);
      return;
    }
    
    try {
      // Eliminar de Firestore (subcolección de tasks)
      const taskDocRef = doc(db, `projects/${userData.businessId}/projects/${projectId}/tasks/${taskId}`);
      await deleteDoc(taskDocRef);
      // Eliminar del estado local
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setShowTaskModal(false);
      setEditingTask(null);
      setTaskFiles([]);
      setTaskFormData({
        title: '', description: '', columnId: 'new', assignedTo: null, worker: null, finalPrice: null, finalPriceUnit: null, finalDuration: null, finalTotal: null, assignmentRequestId: null, dueDate: '', startDate: '', startTime: '', duration: '', durationUnit: 'days', checklistEnabled: false, checklist: [], location: { address: '', city: '', state: '', country: '', zipCode: '', latitude: '', longitude: '', zoom: 13 }, documents: [], isScanningJob: false, workerScanningEdit: [] as string[]
      });
      // Registrar en historial
      await logTaskHistoryEntry(taskId, 'task_deleted', { message: 'Tarea eliminada', deletedAt: new Date().toISOString() });
      toast.success('Tarea eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar tarea:', error);
      toast.error('Error al eliminar tarea');
    }
  };

  // Función helper para obtener solo los cambios entre dos objetos
  const getObjectChanges = (oldObj: any, newObj: any): any => {
    const changes: any = {};
    
    // Comparar todas las propiedades del nuevo objeto
    Object.keys(newObj).forEach(key => {
      // Ignorar propiedades de metadatos
      if (['updatedAt', 'createdAt', '_new'].includes(key)) return;
      
      const oldValue = oldObj?.[key];
      const newValue = newObj[key];
      
      // Detectar cambios
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes[key] = {
          old: oldValue,
          new: newValue
        };
      }
    });
    
    return changes;
  };

  // Función para registrar historial general del proyecto
  const logProjectHistoryEntry = async (action: string, data: any) => {
    if (!userData?.businessId || !projectId) return;
    try {
      const db = getFirestore();
      const historyEntry = {
        projectId,
        businessId: userData.businessId,
        businessName: (userData as any).businessName || (userData as any).email || 'Business',
        action, // 'project_created', 'project_updated', etc.
        data,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        performedBy: {
          uid: currentUser?.uid || 'unknown',
          email: currentUser?.email || 'unknown',
          role: userData?.role || 'business'
        }
      };
      const historyRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/history`);
      await addDoc(historyRef, historyEntry);
      console.log('📝 Historial de proyecto registrado:', historyEntry);
    } catch (error) {
      console.error('Error al registrar historial de proyecto:', error);
    }
  };

  // Función para cargar documentos del proyecto
  const loadProjectDocuments = React.useCallback(async () => {
    if (!project || !projectId || !userData?.businessId) return;
    
    try {
      const documents: Array<{
        id: string;
        name: string;
        url: string;
        type: string;
        size?: number;
        uploadDate?: string;
      }> = [];

      // Cargar documentos subidos manualmente
      if (project.documents && Array.isArray(project.documents)) {
        documents.push(...project.documents);
      }

      // Cargar PDF de quotation si existe
      if (project.quotationPDF && Array.isArray(project.quotationPDF) && project.quotationPDF.length > 0) {
        const quotationUrl = project.quotationPDF[0];
        if (quotationUrl) {
          documents.push({
            id: 'quotation-pdf',
            name: 'Cotización del Proyecto.pdf',
            url: quotationUrl,
            type: 'application/pdf',
            uploadDate: project.createdAt?.seconds ? new Date(project.createdAt.seconds * 1000).toISOString() : new Date().toISOString()
          });
        }
      }

      // Cargar documentos desde la colección de documentos del proyecto
      try {
        const documentsRef = collection(db, `projects/${userData.businessId}/projects/${projectId}/documents`);
        const documentsSnap = await getDocs(documentsRef);
        
        documentsSnap.docs.forEach(doc => {
          const data = doc.data();
          documents.push({
            id: doc.id,
            name: data.name || 'Documento sin nombre',
            url: data.url,
            type: data.type || 'application/octet-stream',
            size: data.size,
            uploadDate: data.uploadDate || data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000).toISOString() : new Date().toISOString()
          });
        });
      } catch (error) {
        console.log('No se encontró colección de documentos específica:', error);
      }

      setProjectDocuments(documents);
      console.log('[ProjectView] Documentos cargados:', documents);
    } catch (error) {
      console.error('[ProjectView] Error cargando documentos:', error);
    }
  }, [project, projectId, userData?.businessId]);

  // Cargar documentos cuando cambie el proyecto
  useEffect(() => {
    loadProjectDocuments();
  }, [loadProjectDocuments]);

  return (
    <div className="app-container min-h-screen flex bg-gradient-to-br from-red-50 via-white to-red-100">
      {/* Sidebar glassmorphism */}
      <div className="fixed top-0 left-0 h-full z-40 transition-all duration-300 w-64 flex flex-col bg-white/30 backdrop-blur-xl border-r border-white/20 shadow-2xl animate-fadeIn">
        <div className="flex flex-col px-4 py-6 gap-2">
          <span className="inline-block bg-red-100 text-red-600 text-xs font-bold rounded-full px-3 py-1 mb-1 opacity-100 w-auto">{t('projects.sidebar.project')}</span>
          {/* Nombre del proyecto y botón de editar */}
          <div className="flex items-center gap-2 mb-2 opacity-100 w-auto h-auto transition-all duration-300" style={{ minHeight: 32 }}>
            {editMode ? (
              <>
                <input
                  className="text-lg font-bold text-red-600 border border-red-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-400 w-full break-words"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={saving}
                />
                <button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white p-1 rounded-lg transition-colors ml-1"><FaCheck /></button>
                <button onClick={() => { setEditMode(false); setName(project?.name || 'Proyecto'); }} disabled={saving} className="bg-gray-400 hover:bg-gray-600 text-white p-1 rounded-lg transition-colors ml-1"><FaTimes /></button>
              </>
            ) : (
              <>
                <span className="text-lg font-bold text-gray-900 break-words max-w-full whitespace-pre-line">{project?.name || 'Proyecto'}</span>
                <button onClick={() => setEditMode('description')} className="bg-gray-100 hover:bg-red-100 text-red-600 p-1 rounded-lg transition-colors ml-1"><FaEdit /></button>
              </>
            )}
          </div>
        </div>
        <nav className="flex-1 flex flex-col gap-2 mt-4">
          <button
            onClick={() => setActiveTab('info')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'info' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaEye className="text-lg" />
            {t('projects.tabs.info')}
          </button>
          <button
            onClick={() => setActiveTab('client')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'client' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaUser className="text-lg" />
            {t('projects.tabs.client')}
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'tasks' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaClipboardList className="text-lg" />
            {t('projects.tabs.tasks')}
          </button>
          <button
            onClick={() => setActiveTab('staff')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'staff' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaUsers className="text-lg" />
            {t('projects.tabs.staff')}
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'documents' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaFileAlt className="text-lg" />
            {t('projects.tabs.documents')}
          </button>
          <button
            onClick={() => setActiveTab('materials')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'materials' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaBox className="text-lg" />
            {t('projects.tabs.materials')}
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'services' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaCogs className="text-lg" />
            {t('projects.tabs.services')}
          </button>
          <button
            onClick={() => setActiveTab('finances')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'finances' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaDollarSign className="text-lg" />
            {t('projects.tabs.finances')}
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'notes' ? 'bg-white/80 text-red-600 shadow border border-red-200' : 'text-gray-700 hover:bg-white/50'}`}
          >
            <FaEdit className="text-lg" />
            {t('projects.tabs.notes')}
          </button>
        </nav>
        {/* Logo de la empresa en la parte inferior */}
        {businessLogo && (
          <div className="m-4 mt-auto rounded-2xl bg-white/40 border border-white/30 shadow-lg p-4 flex flex-col items-center justify-center gap-2 animate-fadeIn">
            <img src={businessLogo} alt="Business Logo" className="h-16 w-auto rounded-xl bg-gray-100 border border-gray-200 shadow mb-2" />
            {/* <span className="text-xs text-gray-500 font-semibold text-center">Logo de la empresa</span> */}
          </div>
        )}
      </div>
      {/* Contenido principal ajustado */}
      <div className="flex-1 ml-0 transition-all duration-300 ml-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Renderizado exclusivo de cada tab */}
                  {activeTab === 'notes' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                      <h2 className="text-xl font-bold mb-4 text-red-600">{t('projects.notes.title')}</h2>
                      <div className="mb-6">
                        <textarea
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 min-h-[60px]"
                          placeholder={t('projects.notes.writeNote')}
                          value={newNote}
                          onChange={e => setNewNote(e.target.value)}
                          disabled={savingNote}
                        />
                        <button
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                          onClick={handleAddNote}
                          disabled={savingNote || !newNote.trim()}
                        >
                          {savingNote ? t('projects.notes.saving') : t('projects.notes.addNote')}
                        </button>
                      </div>
                      <div className="space-y-4">
                        {notes.length === 0 && <div className="text-gray-500">{t('projects.notes.noNotes')}</div>}
                        {notes.map((note, idx) => {
                          // Buscar el usuario en el staff del proyecto
                          const staffMember = project?.staff?.find((member: any) => 
                            (member.id || member.uid) === note.uid
                          );
                          
                          // Obtener el nombre completo y el rol del proyecto
                          const fullName = staffMember 
                            ? `${staffMember.firstName} ${staffMember.lastName}`.trim()
                            : note.user || 'Usuario desconocido';
                          const projectRole = staffMember?.projectRole || 'Sin rol asignado';
                          
                          return (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                              <div className="flex items-center gap-3 mb-1">
                                {/* Avatar del usuario */}
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  {staffMember?.photoURL ? (
                                    <img src={staffMember.photoURL} alt={`${fullName}`} className="w-full h-full object-cover" />
                                  ) : (
                                    <FaUser className="text-gray-400 text-sm" />
                                  )}
                                </div>
                                {/* Información del usuario */}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-red-600">{fullName}</span>
                                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">{projectRole}</span>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500">{new Date(note.timestamp).toLocaleString()}</span>
                              </div>
                              <div className="text-gray-900 whitespace-pre-line">{note.text}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
          {activeTab === 'info' && project && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              {/* Botón para ver historial del proyecto */}
              <div className="flex justify-end mb-4">
                <button
                  className="text-gray-400 hover:text-red-500 font-medium transition-colors flex items-center gap-2 p-0 bg-transparent shadow-none border-none"
                  style={{ background: 'none', boxShadow: 'none' }}
                  onClick={() => {
                    console.log('[ProjectView] Click en historial, projectId:', projectId);
                    if (projectId) {
                      navigate(`/admin/project-history/${projectId}`);
                    } else {
                      toast.error('No se puede abrir el historial: faltan datos de proyecto');
                    }
                  }}
                >
                  <FaHistory className="mr-1" />
                  {t('projects.info.viewHistory')}
                </button>
              </div>
              {/* ... aquí va SOLO el contenido de Info ... */}
              {/* Status, Amount, Fechas, Progreso, Descripción, Created at ... */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {/* Status */}
                <div className="flex items-center gap-4 bg-gradient-to-r from-red-50 to-white rounded-xl p-6 shadow-sm border border-red-100">
                  <span className="text-3xl"><FaClipboardList className="text-red-500" /></span>
                  <div>
                    <div className="text-xs uppercase text-red-600 font-bold tracking-wider mb-1">{t('projects.info.status')}</div>
                    <div className="text-lg font-semibold text-gray-900">{project.status}</div>
                  </div>
                </div>
                {/* Monto */}
                <div className="flex items-center gap-4 bg-gradient-to-r from-yellow-50 to-white rounded-xl p-6 shadow-sm border border-yellow-100">
                  <span className="text-3xl"><FaDollarSign className="text-yellow-500" /></span>
                  <div>
                    <div className="text-xs uppercase text-yellow-600 font-bold tracking-wider mb-1">{t('projects.info.amount')}</div>
                    <div className="text-lg font-semibold text-gray-900">{project.totalAmount != null ? `$${project.totalAmount}` : '-'}</div>
                  </div>
                </div>
                {/* Fechas: Inicio y Fin en la misma columna */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-4 bg-gradient-to-r from-green-50 to-white rounded-xl p-4 shadow-sm border border-green-100">
                    <span className="text-2xl"><FaClock className="text-green-500" /></span>
                    <div>
                      <div className="text-xs uppercase text-green-600 font-bold tracking-wider mb-1">{t('projects.info.startDate')}</div>
                      <div className="text-base font-medium text-gray-900">{project.startDate?.seconds ? new Date(project.startDate.seconds * 1000).toLocaleDateString() : '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 bg-gradient-to-r from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100">
                    <span className="text-2xl"><FaClock className="text-purple-500" /></span>
                    <div>
                      <div className="text-xs uppercase text-purple-600 font-bold tracking-wider mb-1">{t('projects.info.endDate')}</div>
                      <div className="text-base font-medium text-gray-900">{project.endDate?.seconds ? new Date(project.endDate.seconds * 1000).toLocaleDateString() : '-'}</div>
                    </div>
                  </div>
                </div>
                {/* Progreso en una fila aparte */}
                <div className="col-span-1 md:col-span-3 lg:col-span-4 flex flex-col gap-2 bg-gradient-to-r from-blue-50 to-white rounded-xl p-6 shadow-sm border border-blue-100 mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCheck className="text-blue-500 text-xl" />
                    <span className="text-xs uppercase text-blue-600 font-bold tracking-wider">{t('projects.info.progress')}</span>
                    <span className="ml-auto text-xs text-gray-500 font-semibold">{project.progress != null ? `${project.progress}%` : '0%'}</span>
                  </div>
                  <div className="w-full bg-blue-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${project.progress != null ? project.progress : 0}%` }}
                    ></div>
                  </div>
                </div>
                {/* Descripción con icono de editar */}
                <div className="col-span-1 md:col-span-3 lg:col-span-4 flex items-start gap-4 bg-gradient-to-r from-gray-50 to-white rounded-xl p-6 shadow-sm border border-gray-100 mt-2">
                  <span className="text-2xl"><FaEdit className="text-gray-400" /></span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs uppercase text-gray-600 font-bold tracking-wider">{t('projects.info.description')}</div>
                      {!editingDescription ? (
                        <button
                          onClick={() => {
                            setEditingDescription(true);
                            setDescription(project.description || '');
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                        >
                          <FaEdit className="text-xs" />
                          {t('common.edit')}
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveDescription}
                            disabled={saving}
                            className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                          >
                            <FaCheck className="text-xs" />
                            {t('common.save')}
                          </button>
                          <button
                            onClick={() => {
                              setEditingDescription(false);
                              setDescription(project.description || '');
                            }}
                            className="text-gray-600 hover:text-gray-700 text-sm font-medium flex items-center gap-1"
                          >
                            <FaTimes className="text-xs" />
                            {t('common.cancel')}
                          </button>
                        </div>
                      )}
                    </div>
                    {editingDescription ? (
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base text-gray-900 min-h-[100px]"
                        placeholder={t('projects.descriptionPlaceholder')}
                      />
                    ) : (
                      <div className="text-base text-gray-900 whitespace-pre-line">{project.description || '-'}</div>
                    )}
                  </div>
                </div>
                {/* Fecha de creación */}
                <div className="col-span-1 md:col-span-3 lg:col-span-4 flex items-center gap-4 bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 shadow-sm border border-gray-100 mt-2">
                  <span className="text-2xl"><FaCalendar className="text-gray-400" /></span>
                  <div>
                    <div className="text-xs uppercase text-gray-600 font-bold tracking-wider mb-1">{t('projects.info.createdAt')}</div>
                    <div className="text-base text-gray-900">{project.createdAt ? new Date(project.createdAt.seconds ? project.createdAt.seconds * 1000 : project.createdAt).toLocaleDateString() : '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'client' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-xl font-bold mb-4 text-red-600">{t('projects.tabs.client')}</h2>
              {clientData ? (
                <div className="space-y-4">
                  <div>
                    <span className="block text-xs text-gray-500 font-semibold mb-1">{t('projects.client.name', 'Name')}</span>
                    <span className="text-lg font-bold text-gray-900">{clientData.name || '-'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500 font-semibold mb-1">{t('projects.client.email', 'Email')}</span>
                    <span className="text-base text-gray-800">{clientData.email || '-'}</span>
                  </div>
                  {clientData.phone && (
                    <div>
                      <span className="block text-xs text-gray-500 font-semibold mb-1">{t('projects.client.phone', 'Phone')}</span>
                      <span className="text-base text-gray-800">{clientData.phone}</span>
                    </div>
                  )}
                  {(clientData.address || clientData.city || clientData.state || clientData.country) && (
                    <div>
                      <span className="block text-xs text-gray-500 font-semibold mb-1">{t('projects.client.address', 'Address')}</span>
                      <span className="text-base text-gray-800">
                        {clientData.address || ''}
                        {clientData.city ? `, ${clientData.city}` : ''}
                        {clientData.state ? `, ${clientData.state}` : ''}
                        {clientData.country ? `, ${clientData.country}` : ''}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  {t('projects.client.noData', 'No client data found.')}
                </div>
              )}
            </div>
          )}
          {activeTab === 'tasks' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-xl font-bold mb-4 text-red-600">{t('projects.tabs.tasks')}</h2>
              {/* Kanban funcional restaurado */}
              <DragDropContext
                onDragEnd={async (result) => {
                  if (!result.destination) return;
                  await onDragEnd(result);
                }}
              >
                <div className="flex gap-6 overflow-x-auto pb-4">
                  {columns.map((col) => (
                    <div
                      key={col.id}
                      className="min-w-80 bg-gray-50 rounded-xl shadow-sm p-4 flex flex-col gap-4 transition-colors duration-200"
                    >
                      <div className="flex items-center mb-2 gap-2">
                        {editingColumnId === col.id ? (
                          <>
                            <input
                              className="font-bold text-lg text-gray-800 bg-white/70 border border-red-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-400 w-full"
                              value={columnNameEdit}
                              onChange={e => setColumnNameEdit(e.target.value)}
                              autoFocus
                            />
                            <button
                              onClick={async () => {
                                if (!columnNameEdit.trim()) return;
                                const updatedCols = columns.map(c => c.id === col.id ? { ...c, name: columnNameEdit.trim() } : c);
                                await saveColumns(updatedCols);
                                setEditingColumnId(null);
                                setColumnNameEdit('');
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white p-1 rounded-lg transition-colors ml-1"
                              title="Guardar"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={() => { setEditingColumnId(null); setColumnNameEdit(''); }}
                              className="bg-gray-400 hover:bg-gray-600 text-white p-1 rounded-lg transition-colors ml-1"
                              title="Cancelar"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="font-bold text-lg text-gray-800">{col.name}</span>
                            <button
                              onClick={() => { setEditingColumnId(col.id); setColumnNameEdit(col.name); }}
                              className="bg-gray-100 hover:bg-red-100 text-red-600 p-1 rounded-lg transition-colors ml-1"
                              title="Editar columna"
                            >
                              <FaEdit />
                            </button>
                          </>
                        )}
                        {/* Botón para añadir tarea en la columna */}
                        <button
                          onClick={() => {
                            setEditingTask(null);
                            setTaskFormData({
                              title: '',
                              description: '',
                              columnId: col.id,
                              assignedTo: null,
                              dueDate: '',
                              startDate: '',
                              startTime: '',
                              duration: '',
                              durationUnit: 'days',
                              location: {
                                address: '', city: '', state: '', country: '', zipCode: '', latitude: '', longitude: '', zoom: 13
                              },
                              documents: []
                            });
                            setShowTaskModal(true);
                          }}
                          className="ml-auto bg-red-100 hover:bg-red-200 text-red-600 rounded-lg px-3 py-1 font-semibold flex items-center gap-2 transition-colors"
                        >
                          <FaPlus /> {t('projects.kanban.createTask')}
                        </button>
                      </div>
                      <Droppable droppableId={col.id}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`flex flex-col gap-4 min-h-32 ${snapshot.isDraggingOver ? 'bg-red-50' : ''}`}
                          >
                            {tasks.filter(t => t.columnId === col.id).map((task, idx) => (
                              <Draggable draggableId={task.id} index={idx} key={task.id}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    onClick={() => handleEditTask(task)}
                                    className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all duration-200 ${snapshot.isDragging ? 'bg-red-600 text-white shadow-lg transform rotate-2' : 'hover:shadow-md hover:border-red-200'}`}
                                    style={provided.draggableProps.style}
                                  >
                                    <div className="font-semibold text-lg mb-2">{task.title}</div>
                                    <div className={`text-sm ${snapshot.isDragging ? 'text-red-100' : 'text-gray-600'} mb-3`}>{task.description}</div>
                                    {/* Aquí puedes agregar más detalles de la tarea */}
                                    {task.checklistEnabled && Array.isArray(task.checklist) && task.checklist.length > 0 && (
                                      <div className="mt-2">
                                        <div className="flex items-center gap-2 text-xs text-gray-700 mb-1">
                                          <span>Checklist:</span>
                                          <span className="font-semibold text-blue-700">{task.checklist.filter((item:any) => item.completed).length} / {task.checklist.length}</span>
                                        </div>
                                        <div className="w-full bg-blue-100 rounded-full h-2 mb-1">
                                          <div
                                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${(task.checklist.filter((item:any) => item.completed).length / task.checklist.length) * 100}%` }}
                                          ></div>
                                        </div>
                                        {/* Mostrar ítems pendientes */}
                                        {task.checklist.filter((item:any) => !item.completed).length > 0 && (
                                          <ul className="mt-1 ml-2 list-disc text-xs text-gray-600">
                                            {task.checklist.filter((item:any) => !item.completed).map((item:any) => (
                                              <li key={item.id}>{item.text || '(Sin descripción)'}</li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                                      {/* Fecha y hora */}
                                      {task.startDate && (
                                        <span>
                                          <FaCalendar className="inline mr-1" />
                                          {(() => {
                                            let dateObj;
                                            if (task.startDate.seconds) {
                                              dateObj = new Date(task.startDate.seconds * 1000);
                                            } else {
                                              dateObj = new Date(task.startDate);
                                            }
                                            return dateObj.toLocaleDateString();
                                          })()}
                                        </span>
                                      )}
                                      {task.startTime && (
                                        <span>
                                          <FaClock className="inline mr-1" />
                                          {task.startTime}
                                        </span>
                                      )}
                                      {/* Dirección */}
                                      {task.location && task.location.address && (
                                        <span>
                                          <FaMapMarkerAlt className="inline mr-1 text-red-400" />
                                          {task.location.address}
                                        </span>
                                      )}
                                      {/* Worker asignado */}
                                      {task.worker && (Array.isArray(task.worker) ? task.worker.length > 0 : true) && (
                                        <span className="flex items-center gap-1">
                                          {(() => {
                                            const workerIds = Array.isArray(task.worker) ? task.worker : [task.worker.uid || task.worker];
                                            const uniqueWorkers = workers.filter(w => workerIds.includes(w.uid));
                                            if (uniqueWorkers.length === 1) {
                                              const w = uniqueWorkers[0];
                                              return (
                                                <img src={w.photoURL || '/default-avatar.png'} alt="avatar" className="w-6 h-6 rounded-full border border-gray-300 inline-block" title={w.firstName + ' ' + w.lastName} />
                                              );
                                            } else if (uniqueWorkers.length > 1) {
                                              return (
                                                <span className="flex items-center">
                                                  <img src={uniqueWorkers[0].photoURL || '/default-avatar.png'} alt="avatar" className="w-6 h-6 rounded-full border border-gray-300 inline-block" title={uniqueWorkers[0].firstName + ' ' + uniqueWorkers[0].lastName} />
                                                  <span className="ml-1 font-semibold">+{uniqueWorkers.length - 1}</span>
                                                </span>
                                              );
                                            }
                                            return null;
                                          })()}
                                        </span>
                                      )}
                                      {/* Documentos adjuntos */}
                                      {task.files && Array.isArray(task.files) && task.files.length > 0 && (
                                        <span title="Documentos adjuntos">
                                          <FaFileAlt className="inline text-blue-500" />
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  ))}
                  {/* Botón para añadir columna */}
                  <div className="min-w-80 flex flex-col items-center justify-center bg-white/60 backdrop-blur-xl rounded-xl shadow-md border border-dashed border-red-200 p-6 mx-2 my-4">
                    {editingColumnId === 'new' ? (
                      <div className="flex items-center gap-2 w-full">
                        <input
                          className="font-bold text-lg text-gray-800 bg-white/70 border border-red-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-400 w-full"
                          value={newColumnName}
                          onChange={e => setNewColumnName(e.target.value)}
                          placeholder={t('projects.kanban.newColumnPlaceholder', 'Nombre de la columna')}
                          autoFocus
                        />
                        <button
                          onClick={async () => {
                            if (!newColumnName.trim()) return;
                            const newCol = { id: Date.now().toString(), name: newColumnName.trim() };
                            const updatedCols = [...columns, newCol];
                            await saveColumns(updatedCols);
                            setNewColumnName('');
                            setEditingColumnId(null);
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white p-1 rounded-lg transition-colors ml-1"
                          title="Guardar"
                        >
                          <FaCheck />
                        </button>
                        <button
                          onClick={() => { setEditingColumnId(null); setNewColumnName(''); }}
                          className="bg-gray-400 hover:bg-gray-600 text-white p-1 rounded-lg transition-colors ml-1"
                          title="Cancelar"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingColumnId('new'); setNewColumnName(''); }}
                        className="flex flex-col items-center justify-center w-full h-full py-8 px-2 border-2 border-dashed border-red-300 bg-white/60 hover:bg-red-50 text-red-600 rounded-xl transition-colors font-semibold gap-2"
                      >
                        <FaPlus className="text-2xl" />
                        <span>{t('projects.kanban.addColumn', 'Añadir columna')}</span>
                      </button>
                    )}
                  </div>
                </div>
              </DragDropContext>
              {/* Modal de tarea (ya está en el código, no se repite aquí) */}
            </div>
          )}
          {activeTab === 'staff' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-red-600">{t('projects.tabs.staff')}</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddSelfModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
                  >
                    <FaUser /> {t('projects.staff.addSelf') || 'Añadirme al equipo'}
                  </button>
                  <button
                    onClick={() => setShowModal(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
                  >
                    <FaPlus /> {t('projects.tabs.addStaff')}
                  </button>
                </div>
              </div>
              
              {/* Lista de staff - Mostrar todos los miembros del proyecto */}
              {(() => {
                // Set para evitar duplicados
                const allStaffIds = new Set<string>();
                const allStaffMembers: any[] = [];
                
                // 1. Agregar miembros de project.staff (business, employees agregados manualmente)
                if (project?.staff && Array.isArray(project.staff)) {
                  project.staff.forEach((member: any) => {
                    const memberId = member.id || member.uid;
                    if (memberId && !allStaffIds.has(memberId)) {
                      allStaffIds.add(memberId);
                      allStaffMembers.push(member);
                    }
                  });
                }
                
                // 2. Agregar workers que tienen tareas asignadas
                const workersWithTasksIds = new Set<string>();
                
                // Revisar tareas con assignedTo o worker
                tasks.forEach((task: any) => {
                  if (task.assignedTo) {
                    workersWithTasksIds.add(task.assignedTo);
                  }
                  if (task.worker?.uid) {
                    workersWithTasksIds.add(task.worker.uid);
                  }
                  if (Array.isArray(task.worker)) {
                    task.worker.forEach((w: any) => {
                      if (typeof w === 'string') {
                        workersWithTasksIds.add(w);
                      } else if (w?.uid) {
                        workersWithTasksIds.add(w.uid);
                      }
                    });
                  }
                });
                
                // Agregar workers de assignment requests aceptadas
                allProjectAssignmentRequests.forEach((request: any) => {
                  if (request.workerId) {
                    workersWithTasksIds.add(request.workerId);
                  }
                });

                // Buscar workers con tareas en la lista de workers y agregarlos si no están ya
                workers.forEach((worker: any) => {
                  const workerId = worker.uid || worker.id;
                  if (workerId && workersWithTasksIds.has(workerId) && !allStaffIds.has(workerId)) {
                    allStaffIds.add(workerId);
                    allStaffMembers.push(worker);
                  }
                });

                console.log('[ProjectView Staff] Miembros totales:', allStaffMembers.length);
                console.log('[ProjectView Staff] Staff:', allStaffMembers);

                return allStaffMembers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allStaffMembers.map((member: any) => (
                    <div 
                      key={member.uid || member.id} 
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all cursor-pointer hover:border-red-400"
                      onClick={() => handleOpenStaffMemberModal(member)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {member.photoURL ? (
                            <img src={member.photoURL} alt={`${member.firstName} ${member.lastName}`} className="w-full h-full object-cover" />
                          ) : (
                            <FaUser className="text-gray-400 text-xl" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {member.firstName} {member.lastName}
                            </h3>
                            {member.role === 'business' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                                {t('common.business')}
                              </span>
                            )}
                            {member.role === 'employee' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                                {t('common.employee')}
                              </span>
                            )}
                            {member.role === 'worker' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                                {t('common.worker')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 truncate">{member.email}</p>
                          {member.projectRole ? (
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-purple-600 font-semibold">
                                <FaUserTie className="inline mr-1" />
                                {member.projectRole}
                              </p>
                              <button
                                onClick={() => {
                                  setEditingStaffMember(member);
                                  setNewProjectRole(member.projectRole);
                                  setShowEditRoleModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                                title={t('projects.staff.editRole') || 'Editar rol'}
                              >
                                <FaEdit />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingStaffMember(member);
                                setNewProjectRole('Project Manager');
                                setShowEditRoleModal(true);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 mt-1 font-medium"
                            >
                              + {t('projects.staff.assignRole') || 'Asignar rol'}
                            </button>
                          )}
                          {member.address?.city && (
                            <p className="text-xs text-gray-500 mt-1">
                              <FaMapMarkerAlt className="inline mr-1" />
                              {member.address.city}{member.address.state ? `, ${member.address.state}` : ''}
                            </p>
                          )}
                          {member.experience?.skills && member.experience.skills.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {member.experience.skills.slice(0, 2).map((skill: any, idx: number) => (
                                <span key={idx} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                                  {i18n.language === 'es' ? skill.name_es : skill.name_en}
                                </span>
                              ))}
                              {member.experience.skills.length > 2 && (
                                <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                                  +{member.experience.skills.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="mt-2 text-xs text-gray-500">
                            {tasks.filter((t: any) => {
                              const memberId = member.uid || member.id;
                              return t.assignedTo === memberId || 
                                (Array.isArray(t.worker) && t.worker.includes(memberId)) ||
                                allProjectAssignmentRequests.some((r: any) => r.workerId === memberId && r.taskId === t.id);
                            }).length} {t('projects.staff.tasksAssigned') || 'tareas asignadas'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <FaUsers className="mx-auto text-gray-400 text-5xl mb-4" />
                    <p className="text-gray-600 text-lg mb-2">{t('projects.tabs.noStaff', 'No hay miembros en el equipo')}</p>
                    <p className="text-gray-500 text-sm mb-4">{t('projects.tabs.addStaffDescription', 'Agrega miembros al equipo del proyecto o asigna workers a tareas')}</p>
                    <div className="flex gap-2 justify-center mt-4">
                      <button
                        onClick={() => setShowAddSelfModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center gap-2"
                      >
                        <FaUser /> {t('projects.staff.addSelf') || 'Añadirme al equipo'}
                      </button>
                      <button
                        onClick={() => setShowModal(true)}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center gap-2"
                      >
                        <FaPlus /> {t('projects.tabs.addStaff')}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {activeTab === 'documents' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-xl font-bold mb-4 text-red-600">{t('projects.tabs.documents')}</h2>
              {/* Subida de documentos */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.documents.upload')}</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={handleProjectDocumentUpload}
                    disabled={uploadingDocument}
                    className="hidden"
                    id="project-document-upload"
                  />
                  <label htmlFor="project-document-upload" className="cursor-pointer">
                    <div className="text-gray-600">
                      <div className="text-lg mb-2">{t('projects.documents.dragAndDrop')}</div>
                      <div className="text-sm text-gray-500 mb-2">{t('projects.documents.supportedFormats')}</div>
                      <div className="text-xs text-gray-400">{t('projects.documents.maxFileSize')}</div>
                    </div>
                  </label>
                </div>
                {uploadingDocument && (
                  <div className="mt-2 text-center">
                    <div className="inline-flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                      <span className="text-sm text-gray-600">{t('projects.documents.uploading')}</span>
                    </div>
                  </div>
                )}
              </div>
              {/* Lista de documentos */}
              <div className="space-y-4">
                {projectDocuments.length === 0 ? (
                  <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-lg mb-2">📄</div>
                    {t('projects.documents.noDocuments')}
                  </div>
                ) : (
                  projectDocuments.map((doc) => (
                    <div key={doc.id} className={`flex items-center justify-between bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${
                      doc.id === 'quotation-pdf' ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                    }`}>
                      <div className="flex items-center gap-3">
                        {doc.id === 'quotation-pdf' && (
                          <div className="text-blue-600 text-lg">📋</div>
                        )}
                        <div>
                          <div className={`font-medium ${doc.id === 'quotation-pdf' ? 'text-blue-900' : 'text-gray-900'}`}>
                            {doc.name}
                            {doc.id === 'quotation-pdf' && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                Cotización
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {doc.size ? `${(doc.size / 1024 / 1024).toFixed(2)} MB • ` : ''}
                            {doc.uploadDate ? new Date(doc.uploadDate).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            doc.id === 'quotation-pdf' 
                              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {t('projects.documents.download')}
                        </a>
                        {doc.id !== 'quotation-pdf' && (
                          <button
                            onClick={() => handleProjectDocumentDelete(doc.id)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                          >
                            {t('projects.documents.delete', 'Eliminar')}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          {activeTab === 'materials' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-red-600">{t('projects.tabs.materials')}</h2>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowAddMaterialModal(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <FaPlus /> {t('projects.materials.add')}
                  </button>
                  <button
                    onClick={() => setShowHistoryModal(true)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <FaHistory /> {t('projects.info.viewHistory')}
                  </button>
                </div>
              </div>
              
              {project?.materials && project.materials.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.materials.name', 'Name')}</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.materials.description', 'Description')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.materials.quantity', 'Quantity')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.materials.unit', 'Unit')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.materials.price', 'Price')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.materials.total', 'Total')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {project.materials.map((mat: any) => (
                        <tr key={mat.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{mat.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{mat.description}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900">{mat.quantity}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900">{mat.unit}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900">${(Number(mat.price) || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900 font-semibold">${((Number(mat.quantity) || 0) * (Number(mat.price) || 0)).toFixed(2)}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-500">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => {
                                  setEditingMaterial(mat);
                                  setNewMaterial({
                                    name: mat.name,
                                    description: mat.description,
                                    quantity: mat.quantity,
                                    unit: mat.unit,
                                    price: mat.price
                                  });
                                  setShowAddMaterialModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title={t('common.edit')}
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handleDeleteMaterial(mat.id)}
                                className="text-red-600 hover:text-red-900"
                                title={t('common.delete')}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  {t('projects.materials.noData', 'No materials found.')}
                </div>
              )}
            </div>
          )}
          {activeTab === 'services' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-red-600">{t('projects.tabs.services')}</h2>
                <button
                  onClick={() => setShowAddServiceModal(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                >
                  <FaPlus /> {t('projects.services.add')}
                </button>
              </div>
              
              {project?.services && project.services.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.services.name', 'Name')}</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.services.description', 'Description')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.services.quantity', 'Quantity')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.services.unit', 'Unit')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.services.price', 'Price')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('projects.services.total', 'Total')}</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {project.services.map((serv: any) => (
                        <tr key={serv.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{serv.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{serv.description}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900">{serv.quantity}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900">{serv.unit}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900">${(Number(serv.price) || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-900 font-semibold">${((Number(serv.quantity) || 0) * (Number(serv.price) || 0)).toFixed(2)}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-500">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => {
                                  setEditingService(serv);
                                  setNewService({
                                    name: serv.name,
                                    description: serv.description,
                                    quantity: serv.quantity,
                                    unit: serv.unit,
                                    price: serv.price
                                  });
                                  setShowAddServiceModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title={t('common.edit')}
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handleDeleteService(serv.id)}
                                className="text-red-600 hover:text-red-900"
                                title={t('common.delete')}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  {t('projects.services.noData', 'No services found.')}
                </div>
              )}
            </div>
          )}
          {activeTab === 'finances' && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <FinancesTab project={project} projectId={projectId!} businessId={userData?.businessId || ''} />
            </div>
          )}
        </div>
      </div>

      {/* Modal de selección de staff */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <h2 className="text-xl font-bold text-red-600 mb-4">{t('projects.tabs.addStaff')}</h2>
            <input
              className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder={t('common.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="flex flex-col gap-4 max-h-96 overflow-y-auto mb-4">
              {paginatedWorkers.map(worker => (
                <div key={worker.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-gray-900">{worker.firstName} {worker.lastName}</div>
                      {worker.role === 'employee' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {t('common.employee')}
                        </span>
                      )}
                      {worker.role === 'worker' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('common.worker')}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{worker.email}</div>
                    <div className="text-sm text-gray-400">{worker.address?.city || '-'}, {worker.address?.state || '-'}, {worker.address?.country || '-'}</div>
                    <div className="text-sm text-gray-400">Skills: {Array.isArray(worker.experience?.skills) && worker.experience.skills.length > 0 ? worker.experience.skills.map((s:any) => s.name_en || s.name_es || s.id).filter(Boolean).join(', ') : '-'}</div>
                  </div>
                  <button
                    onClick={() => handleAddStaff(worker)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    {t('add')}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-center items-center gap-4 mt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p-1))}
                disabled={page===1}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >{'<'}</button>
              <span className="text-gray-700 font-medium">{page} / {totalPages || 1}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p+1))}
                disabled={page===totalPages}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >{'>'}</button>
            </div>
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal de detalles del trabajador */}
      {showWorkerDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <h2 className="text-xl font-bold text-red-600 mb-4">{selectedWorkerForOffer?.firstName} {selectedWorkerForOffer?.lastName}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="text-gray-900">{selectedWorkerForOffer?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Ciudad</label>
                <p className="text-gray-900">{selectedWorkerForOffer?.address?.city || 'No especificada'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Skills</label>
                <p className="text-gray-900">
                  {Array.isArray(selectedWorkerForOffer?.experience?.skills) && selectedWorkerForOffer.experience.skills.length > 0 
                    ? selectedWorkerForOffer.experience.skills.map((s:any) => s.name_en || s.name_es || s.id).filter(Boolean).join(', ')
                    : 'No especificadas'
                  }
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Certificaciones</label>
                <p className="text-gray-900">
                  {Array.isArray(selectedWorkerForOffer?.certifications) && selectedWorkerForOffer.certifications.length > 0
                    ? selectedWorkerForOffer.certifications.map((c:any) => c.name).join(', ')
                    : 'No especificadas'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowWorkerDetails(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal de asignación de trabajador */}
      {showAssignWorkerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <h2 className="text-xl font-bold text-red-600 mb-4">{t('projects.tabs.assignWorker')}</h2>
            <input
              className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder={t('common.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="flex flex-col gap-4 max-h-80 overflow-y-auto mb-4">
              {paginatedWorkers.map(worker => (
                <div key={worker.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <div className="font-semibold text-gray-900">{worker.firstName} {worker.lastName}</div>
                    <div className="text-sm text-gray-500">{worker.email}</div>
                    <div className="text-sm text-gray-400">{worker.address?.city || '-'}, {worker.address?.state || '-'}, {worker.address?.country || '-'}</div>
                  </div>
                  <button
                    onClick={() => handleAssignWorker(worker)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    {t('assign')}
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowAssignWorkerModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal de agregar material */}
      {showAddMaterialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 relative">
            <h2 className="text-xl font-bold text-red-600 mb-4">
              {editingMaterial ? t('projects.materials.edit') : t('projects.materials.add')}
            </h2>
            <form onSubmit={editingMaterial ? handleEditMaterial : handleAddMaterial} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.materials.name')}</label>
                <input
                  type="text"
                  value={newMaterial.name}
                  onChange={(e) => setNewMaterial((prev: typeof newMaterial) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.materials.description')}</label>
                <textarea
                  value={newMaterial.description}
                  onChange={(e) => setNewMaterial((prev: typeof newMaterial) => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.materials.quantity')}</label>
                  <input
                    type="number"
                    value={newMaterial.quantity}
                    onChange={(e) => setNewMaterial((prev: typeof newMaterial) => ({ ...prev, quantity: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.materials.unit')}</label>
                  <input
                    type="text"
                    value={newMaterial.unit}
                    onChange={(e) => setNewMaterial((prev: typeof newMaterial) => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.materials.price')}</label>
                <input
                  type="number"
                  value={newMaterial.price}
                  onChange={(e) => setNewMaterial((prev: typeof newMaterial) => ({ ...prev, price: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddMaterialModal(false);
                    setEditingMaterial(null);
                    setNewMaterial({
                      name: '',
                      description: '',
                      quantity: 1,
                      unit: '',
                      price: 0
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  {editingMaterial ? t('save') : t('projects.materials.add')}
                </button>
              </div>
            </form>
            <button
              onClick={() => {
                setShowAddMaterialModal(false);
                setEditingMaterial(null);
                setNewMaterial({
                  name: '',
                  description: '',
                  quantity: 1,
                  unit: '',
                  price: 0
                });
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal para editar rol de miembro del staff */}
      {showEditRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 relative">
            <h2 className="text-xl font-bold text-purple-600 mb-4">
              {t('projects.staff.editRoleTitle') || 'Asignar rol en el proyecto'}
            </h2>
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-gray-600 mb-1">{t('projects.staff.member') || 'Miembro'}</p>
                <p className="font-semibold text-gray-900">
                  {editingStaffMember?.firstName} {editingStaffMember?.lastName}
                </p>
                <p className="text-sm text-gray-600">{editingStaffMember?.email}</p>
              </div>
              
              <div>
                <p className="text-gray-700 mb-4">
                  {t('projects.staff.selectRole') || 'Selecciona el rol en este proyecto:'}
                </p>
                <div className="space-y-3">
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-500 transition-colors">
                    <input
                      type="radio"
                      name="projectRole"
                      value="Project Manager"
                      checked={newProjectRole === 'Project Manager'}
                      onChange={(e) => setNewProjectRole(e.target.value)}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Project Manager</div>
                      <div className="text-sm text-gray-600">
                        {t('projects.staff.projectManagerDesc') || 'Gestiona y supervisa todo el proyecto'}
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-500 transition-colors">
                    <input
                      type="radio"
                      name="projectRole"
                      value="Supervisor"
                      checked={newProjectRole === 'Supervisor'}
                      onChange={(e) => setNewProjectRole(e.target.value)}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Supervisor</div>
                      <div className="text-sm text-gray-600">
                        {t('projects.staff.supervisorDesc') || 'Supervisa el trabajo en campo y calidad'}
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-500 transition-colors">
                    <input
                      type="radio"
                      name="projectRole"
                      value=""
                      checked={newProjectRole === ''}
                      onChange={(e) => setNewProjectRole(e.target.value)}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">{t('projects.staff.noRole') || 'Sin rol específico'}</div>
                      <div className="text-sm text-gray-600">
                        {t('projects.staff.noRoleDesc') || 'Miembro del equipo sin rol definido'}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditRoleModal(false);
                    setEditingStaffMember(null);
                    setNewProjectRole('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                >
                  {t('cancel') || 'Cancelar'}
                </button>
                <button
                  type="button"
                  onClick={handleUpdateStaffRole}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  {t('projects.staff.updateRole') || 'Actualizar rol'}
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setShowEditRoleModal(false);
                setEditingStaffMember(null);
                setNewProjectRole('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal para añadirse a sí mismo al staff */}
      {showAddSelfModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 relative">
            <h2 className="text-xl font-bold text-blue-600 mb-4">
              {t('projects.staff.addSelfTitle') || 'Añadirme al equipo del proyecto'}
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-gray-700 mb-4">
                  {t('projects.staff.addSelfDescription') || 'Selecciona tu rol en este proyecto:'}
                </p>
                <div className="space-y-3">
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                    <input
                      type="radio"
                      name="businessRole"
                      value="Project Manager"
                      checked={selectedBusinessRole === 'Project Manager'}
                      onChange={(e) => setSelectedBusinessRole(e.target.value)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Project Manager</div>
                      <div className="text-sm text-gray-600">
                        {t('projects.staff.projectManagerDesc') || 'Gestiona y supervisa todo el proyecto'}
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                    <input
                      type="radio"
                      name="businessRole"
                      value="Supervisor"
                      checked={selectedBusinessRole === 'Supervisor'}
                      onChange={(e) => setSelectedBusinessRole(e.target.value)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Supervisor</div>
                      <div className="text-sm text-gray-600">
                        {t('projects.staff.supervisorDesc') || 'Supervisa el trabajo en campo y calidad'}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSelfModal(false);
                    setSelectedBusinessRole('Project Manager');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                >
                  {t('cancel') || 'Cancelar'}
                </button>
                <button
                  type="button"
                  onClick={handleAddSelfToStaff}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  {t('projects.staff.addSelfButton') || 'Añadirme al equipo'}
                </button>
              </div>
            </div>
            <button
              onClick={() => {
                setShowAddSelfModal(false);
                setSelectedBusinessRole('Project Manager');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-blue-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal de detalles del miembro de staff */}
      {showStaffMemberModal && selectedStaffMember && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-red-600">{t('projects.staff.memberDetails') || 'Detalles del Miembro'}</h2>
                <p className="text-sm text-gray-500 mt-1">{t('projects.staff.memberDetailsDesc') || 'Información y permisos del miembro del equipo'}</p>
              </div>
              <button
                onClick={() => {
                  setShowStaffMemberModal(false);
                  setSelectedStaffMember(null);
                  setNewProjectRole('');
                  setCanSignForMe(false);
                }}
                className="text-gray-400 hover:text-red-600 text-3xl font-bold leading-none"
                title="Cerrar"
              >×</button>
            </div>

            <div className="p-6 space-y-6">
              {/* Información del miembro */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">{t('projects.staff.memberInfo') || 'Información del Miembro'}</h3>
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {selectedStaffMember.photoURL ? (
                      <img src={selectedStaffMember.photoURL} alt={`${selectedStaffMember.firstName} ${selectedStaffMember.lastName}`} className="w-full h-full object-cover" />
                    ) : (
                      <FaUser className="text-gray-400 text-3xl" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {selectedStaffMember.firstName} {selectedStaffMember.lastName}
                      </h4>
                      {selectedStaffMember.role === 'business' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {t('common.business')}
                        </span>
                      )}
                      {selectedStaffMember.role === 'employee' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {t('common.employee')}
                        </span>
                      )}
                      {selectedStaffMember.role === 'worker' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('common.worker')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{selectedStaffMember.email}</p>
                    {selectedStaffMember.address?.city && (
                      <p className="text-sm text-gray-500">
                        <FaMapMarkerAlt className="inline mr-1" />
                        {selectedStaffMember.address.city}{selectedStaffMember.address.state ? `, ${selectedStaffMember.address.state}` : ''}
                      </p>
                    )}
                  </div>
                </div>

                {/* Habilidades */}
                {selectedStaffMember.experience?.skills && selectedStaffMember.experience.skills.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-gray-700 mb-2">{t('projects.staff.skills') || 'Habilidades'}:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedStaffMember.experience.skills.map((skill: any, idx: number) => (
                        <span key={idx} className="inline-block bg-white border border-gray-200 text-gray-700 text-xs px-3 py-1 rounded-full">
                          {i18n.language === 'es' ? skill.name_es : skill.name_en}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tareas asignadas */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-700">
                    <strong>{tasks.filter((t: any) => {
                      const memberId = selectedStaffMember.uid || selectedStaffMember.id;
                      return t.assignedTo === memberId || 
                        (Array.isArray(t.worker) && t.worker.includes(memberId)) ||
                        allProjectAssignmentRequests.some((r: any) => r.workerId === memberId && r.taskId === t.id);
                    }).length}</strong> {t('projects.staff.tasksAssigned') || 'tareas asignadas'}
                  </p>
                </div>
              </div>

              {/* Asignar rol en el proyecto */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('projects.staff.projectRole') || 'Rol en el Proyecto'}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('projects.staff.projectRoleDesc') || 'Define la función específica de este miembro en el proyecto'}
                </p>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-red-400 transition-colors">
                    <input
                      type="radio"
                      name="projectRole"
                      value="Project Manager"
                      checked={newProjectRole === 'Project Manager'}
                      onChange={(e) => setNewProjectRole(e.target.value)}
                      className="w-4 h-4 text-red-600 focus:ring-red-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Project Manager</div>
                      <div className="text-xs text-gray-600">
                        {t('projects.staff.projectManagerDesc') || 'Gestiona y supervisa todo el proyecto'}
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-red-400 transition-colors">
                    <input
                      type="radio"
                      name="projectRole"
                      value="Supervisor"
                      checked={newProjectRole === 'Supervisor'}
                      onChange={(e) => setNewProjectRole(e.target.value)}
                      className="w-4 h-4 text-red-600 focus:ring-red-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Supervisor</div>
                      <div className="text-xs text-gray-600">
                        {t('projects.staff.supervisorDesc') || 'Supervisa el trabajo en campo y calidad'}
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-red-400 transition-colors">
                    <input
                      type="radio"
                      name="projectRole"
                      value=""
                      checked={newProjectRole === ''}
                      onChange={(e) => setNewProjectRole(e.target.value)}
                      className="w-4 h-4 text-red-600 focus:ring-red-500"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">{t('projects.staff.noRole') || 'Sin rol específico'}</div>
                      <div className="text-xs text-gray-600">
                        {t('projects.staff.noRoleDesc') || 'Miembro del equipo sin rol definido'}
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Permiso para firmar */}
              <div className="border border-gray-200 rounded-lg p-4 bg-yellow-50">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FaEdit className="text-yellow-600" />
                  {t('projects.staff.signaturePermission') || 'Permiso de Firma'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('projects.staff.signaturePermissionDesc') || 'Permite que este miembro pueda firmar reportes de scanning jobs en tu nombre'}
                </p>
                <label className="flex items-center p-4 border-2 border-yellow-300 rounded-lg cursor-pointer hover:border-yellow-500 transition-colors bg-white">
                  <input
                    type="checkbox"
                    checked={canSignForMe}
                    onChange={(e) => setCanSignForMe(e.target.checked)}
                    className="w-5 h-5 text-yellow-600 focus:ring-yellow-500 rounded"
                  />
                  <div className="ml-3">
                    <div className="font-semibold text-gray-900">{t('projects.staff.canSignForMe') || 'Puede firmar por mí'}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {t('projects.staff.canSignForMeDesc') || 'Este usuario podrá firmar reportes de scanning en tu nombre cuando sea necesario'}
                    </div>
                  </div>
                </label>

                {/* Mostrar otros usuarios que han dado permiso a este miembro */}
                {(() => {
                  // Buscar quiénes permiten que el miembro seleccionado firme por ellos
                  const selectedMemberId = selectedStaffMember.id || selectedStaffMember.uid;
                  const usersWhoGrantedPermission = project?.staff?.filter((member: any) => {
                    const memberId = member.id || member.uid;
                    // Excluir al miembro seleccionado mismo
                    if (memberId === selectedMemberId) return false;
                    // Verificar si este miembro permite que el seleccionado firme por él
                    return member.canSignForMe && member.canSignForMe[selectedMemberId] === true;
                  }) || [];

                  if (usersWhoGrantedPermission.length === 0) return null;

                  return (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-yellow-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        {t('projects.staff.othersWhoGrantedPermission') || 'Otros usuarios que han dado permiso'}:
                      </p>
                      <div className="space-y-1">
                        {usersWhoGrantedPermission.map((user: any) => {
                          const userId = user.id || user.uid;
                          const userName = userId === currentUser?.uid 
                            ? t('common.you') || 'Tú'
                            : `${user.firstName} ${user.lastName}`;
                          
                          return (
                            <div key={userId} className="text-xs text-gray-600 flex items-center gap-1">
                              <FaCheck className="text-green-600 text-xs" />
                              {userName}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Botones de acción */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    const memberId = selectedStaffMember.id || selectedStaffMember.uid;
                    handleRemoveStaffMember(memberId);
                    setShowStaffMemberModal(false);
                  }}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg font-medium transition-colors duration-200 hover:bg-red-50 flex items-center gap-2"
                >
                  <FaTimes />
                  {t('projects.staff.remove') || 'Eliminar del equipo'}
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStaffMemberModal(false);
                      setSelectedStaffMember(null);
                      setNewProjectRole('');
                      setCanSignForMe(false);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('cancel') || 'Cancelar'}
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdateStaffMember}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <FaCheck />
                    {t('common.save') || 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de agregar/editar servicio */}
      {showAddServiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 relative">
            <h2 className="text-xl font-bold text-red-600 mb-4">
              {editingService ? t('projects.services.edit') : t('projects.services.add')}
            </h2>
            <form onSubmit={editingService ? handleEditService : handleAddService} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.services.name')}</label>
                <input
                  type="text"
                  value={newService.name}
                  onChange={(e) => setNewService((prev: typeof newService) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.services.description')}</label>
                <textarea
                  value={newService.description}
                  onChange={(e) => setNewService((prev: typeof newService) => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.services.quantity')}</label>
                  <input
                    type="number"
                    value={newService.quantity}
                    onChange={(e) => setNewService((prev: typeof newService) => ({ ...prev, quantity: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.services.unit')}</label>
                  <input
                    type="text"
                    value={newService.unit}
                    onChange={(e) => setNewService((prev: typeof newService) => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.services.price')}</label>
                <input
                  type="number"
                  value={newService.price}
                  onChange={(e) => setNewService((prev: typeof newService) => ({ ...prev, price: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddServiceModal(false);
                    setEditingService(null);
                    setNewService({
                      name: '',
                      description: '',
                      quantity: 1,
                      unit: '',
                      price: 0
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  {editingService ? t('save') : t('projects.services.add')}
                </button>
              </div>
            </form>
            <button
              onClick={() => {
                setShowAddServiceModal(false);
                setEditingService(null);
                setNewService({
                  name: '',
                  description: '',
                  quantity: 1,
                  unit: '',
                  price: 0
                });
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal de historial de materiales */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <h2 className="text-xl font-bold text-red-600 mb-4">{t('projects.materials.history')}</h2>
            <div className="space-y-4">
              {/* Aquí se mostraría el historial de cambios de materiales */}
              <p className="text-gray-600">{t('projects.materials.materialsHistoryInDevelopment')}</p>
            </div>
            <button
              onClick={() => setShowHistoryModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal de creación/edición de tarea con tabs */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <h2 className="text-xl font-bold text-red-600 mb-4">{editingTask ? t('projects.kanban.editTask') : t('projects.kanban.createTask')}</h2>
            {/* Tabs */}
            <div className="flex border-b mb-6">
              <button
                className={`px-4 py-2 font-semibold ${activeTaskTab === 'general' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
                onClick={() => setActiveTaskTab('general')}
              >
                {t('projects.kanban.details')}
              </button>
              <button
                className={`px-4 py-2 font-semibold ${activeTaskTab === 'checklist' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
                onClick={() => setActiveTaskTab('checklist')}
              >
                {t('projects.kanban.checklist.title')}
              </button>
              <button
                className={`px-4 py-2 font-semibold ${activeTaskTab === 'location' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
                onClick={() => setActiveTaskTab('location')}
              >
                {t('projects.location.title')}
              </button>
              <button
                className={`px-4 py-2 font-semibold ${activeTaskTab === 'worker' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
                onClick={() => setActiveTaskTab('worker')}
              >
                {t('projects.workerAssignment.title')}
              </button>
              <button
                className={`px-4 py-2 font-semibold ${activeTaskTab === 'files' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
                onClick={() => setActiveTaskTab('files')}
              >
                {t('projects.kanban.tabs.files')}
              </button>
              {taskFormData.isScanningJob && (
                <button
                  className={`px-4 py-2 font-semibold ${activeTaskTab === 'scanning' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
                  onClick={() => setActiveTaskTab('scanning')}
                >
                  {t('projects.kanban.scanningJob.report')}
                </button>
              )}
            </div>
            {/* Tab de Detalles */}
            {activeTaskTab === 'general' && (
              <form onSubmit={e => { e.preventDefault(); handleSaveTask(); }} className="space-y-4">
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (projectId && editingTask?.id) {
                        window.open(`/admin/task-history/${projectId}/${editingTask.id}`, '_blank');
                      } else {
                        toast.error('No se puede abrir el historial: faltan datos de proyecto o tarea');
                      }
                    }}
                    className="p-0 m-0 bg-transparent border-none shadow-none hover:bg-transparent focus:outline-none"
                    style={{ background: 'none', boxShadow: 'none' }}
                  >
                    <FaHistory className="text-gray-300 text-xl" />
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.taskTitle')}</label>
                  <input
                    type="text"
                    value={taskFormData.title}
                    onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.taskDescription')}</label>
                  <textarea
                    value={taskFormData.description}
                    onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.startDate')}</label>
                    <input
                      type="date"
                      value={taskFormData.startDate}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.startTime')}</label>
                    <input
                      type="time"
                      value={taskFormData.startTime}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, startTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.duration')}</label>
                      <input
                        type="number"
                        min={1}
                        value={taskFormData.duration}
                        onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, duration: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.durationUnit')}</label>
                      <select
                        value={taskFormData.durationUnit}
                        onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, durationUnit: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="days">{t('projects.kanban.days')}</option>
                        <option value="hours">{t('projects.kanban.hours')}</option>
                      </select>
                    </div>
                  </div>
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.status')}</label>
                    <select
                      value={taskFormData.columnId || 'new'}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, columnId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="new">{t('projects.kanban.new')}</option>
                      <option value="in_progress">{t('projects.kanban.inProgress')}</option>
                      <option value="done">{t('projects.kanban.done')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('projects.kanban.progress')}
                      {taskFormData.checklistEnabled && taskFormData.checklist.length > 0 && (
                        <span className="ml-2 text-xs text-gray-500">({t('projects.kanban.checklist.progress')})</span>
                      )}
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={getCurrentProgress()}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => {
                        const newProgress = Number(e.target.value);
                        let newColumnId = prev.columnId;
                        
                        // Si se está ajustando el progreso y la tarea está en "Nuevo", cambiar a "En Progreso"
                        if (newProgress > 0 && prev.columnId === 'new') {
                          newColumnId = 'in_progress';
                        }
                        
                        // Si el progreso llega al 100%, cambiar a "Completado"
                        if (newProgress === 100) {
                          newColumnId = 'done';
                        }
                        
                        return { 
                          ...prev, 
                          progress: newProgress,
                          columnId: newColumnId
                        };
                      })}
                      disabled={taskFormData.checklistEnabled && taskFormData.checklist.length > 0}
                      className={`w-full ${taskFormData.checklistEnabled && taskFormData.checklist.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {getCurrentProgress()}%
                    </div>
                  </div>
                </div>

                {/* Checkbox para Scanning Job */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isScanningJob"
                      checked={taskFormData.isScanningJob}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, isScanningJob: e.target.checked }))}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="isScanningJob" className="text-sm font-medium text-gray-700 cursor-pointer">
                      {t('projects.kanban.scanningJob.title')}
                    </label>
                  </div>
                  <p className="text-xs text-gray-600 mt-2 ml-8">{t('projects.kanban.scanningJob.description')}</p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowTaskModal(false); setEditingTask(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                  {editingTask && (
                    <button
                      type="button"
                      onClick={() => handleDeleteTask(editingTask.id)}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg font-medium transition-colors duration-200 border border-red-300"
                    >
                      {t('common.delete')}
                    </button>
                  )}
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {editingTask ? t('common.save') : t('projects.kanban.createTask')}
                  </button>
                </div>
              </form>
            )}
            {/* Tab de Checklist */}
            {activeTaskTab === 'checklist' && (
              <div className="space-y-4">
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-4 text-base">{t('projects.kanban.checklist.title')}</span>
                
                {/* Switch para activar/desactivar checklist */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('projects.kanban.checklist.title')}</label>
                    <p className="text-xs text-gray-500">{t('projects.kanban.checklist.progress')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleChecklist}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                      taskFormData.checklistEnabled ? 'bg-red-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        taskFormData.checklistEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Checklist */}
                {taskFormData.checklistEnabled && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">{t('projects.kanban.checklist.title')}</label>
                      <button
                        type="button"
                        onClick={handleAddChecklistItem}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                      >
                        {t('projects.kanban.checklist.addItem')}
                      </button>
                    </div>
                    
                    {taskFormData.checklist.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-lg mb-2">📋</div>
                        {t('projects.kanban.checklist.noItems')}
                        <p className="text-xs text-gray-400 mt-1">Haz clic en "Añadir item" para comenzar</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {taskFormData.checklist.map((item: any) => (
                          <div key={item.id} className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                            <input
                              type="checkbox"
                              checked={item.completed}
                              onChange={(e) => handleUpdateChecklistItem(item.id, 'completed', e.target.checked)}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                            <input
                              type="text"
                              value={item.text}
                              onChange={(e) => handleUpdateChecklistItem(item.id, 'text', e.target.value)}
                              placeholder={t('projects.kanban.checklist.itemPlaceholder')}
                              className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors ${
                                item.completed ? 'line-through text-gray-500 bg-gray-50' : ''
                              }`}
                            />
                            <button
                              type="button"
                              onClick={() => handleDeleteChecklistItem(item.id)}
                              className="text-red-600 hover:text-red-800 transition-colors p-2 rounded-lg hover:bg-red-50"
                              title={t('common.delete')}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Progreso del checklist */}
                    {taskFormData.checklistEnabled && taskFormData.checklist.length > 0 && (
                      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-blue-800 font-medium">{t('projects.kanban.checklist.progress')}</span>
                          <span className="font-semibold text-blue-900">
                            {taskFormData.checklist.filter((item: any) => item.completed).length} / {taskFormData.checklist.length}
                          </span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${taskFormData.checklist.length > 0 ? (taskFormData.checklist.filter((item: any) => item.completed).length / taskFormData.checklist.length) * 100 : 0}%` 
                            }}
                          ></div>
                        </div>
                        <div className="text-xs text-blue-600 mt-1 text-center">
                          {t('projects.kanban.checklist.progressDescription')}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Mensaje cuando checklist está deshabilitado */}
                {!taskFormData.checklistEnabled && (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-lg mb-2">📋</div>
                    <p className="text-sm mb-2">{t('projects.kanban.checklist.disabled')}</p>
                    <p className="text-xs text-gray-400">Activa el checklist para comenzar a añadir items</p>
                  </div>
                )}

                {/* Botón de guardar para checklist */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowTaskModal(false); setEditingTask(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTask}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {editingTask ? t('common.save') : t('projects.kanban.createTask')}
                  </button>
                </div>
              </div>
            )}
            {/* Tab de Ubicación */}
            {activeTaskTab === 'location' && (
              <div className="space-y-4">
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1 text-base">{t('projects.location.title')}</span>
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.map')}</span>
                <div ref={nativeMapRef} style={{ width: '100%', height: 350, borderRadius: 12, border: '1px solid #eee' }} />
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.latitude')}</span>
                    <input
                      type="number"
                      value={taskFormData.location.latitude}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, latitude: e.target.value } }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.longitude')}</span>
                    <input
                      type="number"
                      value={taskFormData.location.longitude}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, longitude: e.target.value } }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.zoom')}</span>
                    <input
                      type="number"
                      value={taskFormData.location.zoom}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, zoom: e.target.value } }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.address')}</span>
                    <input
                      ref={addressInputRef}
                      type="text"
                      value={taskFormData.location.address}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, address: e.target.value } }))}
                      onPaste={async (e) => {
                        const pastedText = e.clipboardData.getData('text');
                        // Detectar si es una URL de Google Maps (incluyendo URLs acortadas)
                        if (pastedText.includes('google.com/maps') || pastedText.includes('goo.gl') || pastedText.includes('maps.app.goo.gl')) {
                          e.preventDefault();
                          const coords = await extractCoordsFromGoogleMapsUrl(pastedText);
                          if (coords) {
                            // Obtener dirección desde las coordenadas
                            const addressData = await fetchAddressFromCoords(coords.lat, coords.lng);
                            setTaskFormData((prev: typeof taskFormData) => ({
                              ...prev,
                              location: {
                                ...prev.location,
                                ...addressData,
                                latitude: coords.lat,
                                longitude: coords.lng,
                              },
                            }));
                            // Actualizar el mapa y el marcador
                            if (nativeMapInstance.current && nativeMarkerInstance.current) {
                              nativeMapInstance.current.setCenter({ lat: coords.lat, lng: coords.lng });
                              nativeMapInstance.current.setZoom(15);
                              nativeMarkerInstance.current.setPosition({ lat: coords.lat, lng: coords.lng });
                            }
                          }
                        }
                      }}
                      placeholder="Busca una dirección, negocio o pega una URL de Google Maps..."
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.city')}</span>
                    <input
                      type="text"
                      value={taskFormData.location.city}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, city: e.target.value } }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.state')}</span>
                    <input
                      type="text"
                      value={taskFormData.location.state}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, state: e.target.value } }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.country')}</span>
                    <input
                      type="text"
                      value={taskFormData.location.country}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, country: e.target.value } }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-1">{t('projects.location.zipCode')}</span>
                    <input
                      type="text"
                      value={taskFormData.location.zipCode}
                      onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ ...prev, location: { ...prev.location, zipCode: e.target.value } }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                {/* Botón de guardar para ubicación */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowTaskModal(false); setEditingTask(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTask}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {editingTask ? t('common.save') : t('projects.kanban.createTask')}
                  </button>
                </div>
              </div>
            )}
            {/* Tab de asignación de trabajadores */}
            {activeTaskTab === 'worker' && (
              <div className="space-y-4">
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-4 text-base">{t('projects.workerAssignment.title')}</span>
                


                {/* Mostrar workers asignados actualmente */}
                {(taskFormData.worker && taskFormData.worker.length > 0) || assignmentRequests.filter(r => r.status === 'accepted').length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('projects.workerAssignment.currentlyAssigned')}</h3>
                    <div className="space-y-3">
                      {/* Mostrar workers del campo worker */}
                      {taskFormData.worker && taskFormData.worker.map((workerId: string) => {
                        const worker = workers.find(w => w.uid === workerId);
                        return (
                          <div key={workerId} className="border border-green-200 rounded-lg p-4 bg-green-50 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">
                                  {worker ? `${worker.firstName} ${worker.lastName}` : 'Worker desconocido'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {worker ? worker.email : 'Email no disponible'}
                                </div>
                                <div className="text-sm text-green-600 mt-1">
                                  <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                    ✅ Asignado
                                  </span>
                                </div>
                                {taskFormData.finalPrice && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    <div>Precio: ${taskFormData.finalPrice} {taskFormData.finalPriceUnit === 'hour' ? '/hora' : '/día'}</div>
                                    <div>Total: ${taskFormData.finalTotal}</div>
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveWorker(workerId)}
                                className="ml-4 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                                title="Quitar worker"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Mostrar workers de solicitudes aceptadas si no hay en el campo worker */}
                      {(!taskFormData.worker || taskFormData.worker.length === 0) && 
                        assignmentRequests.filter(r => r.status === 'accepted').map((request) => {
                          const worker = workers.find(w => w.uid === request.workerId);
                          return (
                            <div key={request.id} className="border border-green-200 rounded-lg p-4 bg-green-50 hover:shadow-md transition-shadow">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900">
                                    {worker ? `${worker.firstName} ${worker.lastName}` : 'Worker desconocido'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {worker ? worker.email : 'Email no disponible'}
                                  </div>
                                  <div className="text-sm text-green-600 mt-1">
                                    <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                      ✅ Asignado (via solicitud)
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    <div>Precio: ${request.price} {request.priceUnit === 'hour' ? '/hora' : '/día'}</div>
                                    <div>Duración: {request.duration} {request.priceUnit === 'hour' ? 'horas' : 'días'}</div>
                                    <div>Total: ${request.total}</div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveWorker(request.workerId)}
                                  className="ml-4 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                                  title="Quitar worker"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )}

                {/* Mostrar solicitudes de asignación si existen */}
                {showAssignmentRequests && assignmentRequests.length > 0 && (
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">{t('projects.workerAssignment.assignmentRequests')}</h3>
                      <button
                        onClick={() => setShowAssignmentRequests(false)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                      >
                        {t('projects.workerAssignment.sendMoreOffers')}
                      </button>
                    </div>
                    <div className="space-y-3">
                      {assignmentRequests.filter(request => request.status !== 'removed').map(request => (
                        <div key={request.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:shadow-md transition-shadow cursor-pointer">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">
                                {workers.find(w => w.uid === request.workerId)?.firstName} {workers.find(w => w.uid === request.workerId)?.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {workers.find(w => w.uid === request.workerId)?.email}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                                  request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  request.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                  request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {request.status === 'counter' ? t('projects.assignment.statuses.counter') : t(`projects.assignment.statuses.${request.status}`)}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {t('projects.assignment.price')}: ${request.price} {request.priceUnit === 'hour' ? t('projects.assignment.perHour') : t('projects.assignment.perDay')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {t('projects.assignment.duration')}: {request.duration} {request.priceUnit === 'hour' ? t('projects.assignment.hours') : t('projects.assignment.days')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {t('projects.assignment.total')}: ${request.total}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 ml-4">
                              {new Date(request.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedAssignmentRequest(request);
                              setShowAssignmentModal(true);
                            }}
                            className="mt-3 w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors flex items-center justify-center gap-2"
                          >
                            <FaEye />
                            {t('projects.assignment.viewDetails')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Mostrar trabajadores disponibles solo si no hay solicitudes pendientes y no hay workers asignados */}
                {!showAssignmentRequests && (!taskFormData.worker || taskFormData.worker.length === 0) && (
                  <>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('projects.workerAssignment.assignNewWorker')}</h3>
                      <input
                        type="text"
                        placeholder={t('projects.workerAssignment.searchPlaceholder')}
                        value={assignSearch}
                        onChange={e => setAssignSearch(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div className="flex flex-col gap-4 max-h-80 overflow-y-auto mb-4">
                      {workers
                        .filter(w => w.role === 'worker' && w.status === 'active')
                        .filter(w => {
                          const q = assignSearch.toLowerCase();
                          return (
                            w.firstName?.toLowerCase().includes(q) ||
                            w.lastName?.toLowerCase().includes(q) ||
                            w.email?.toLowerCase().includes(q) ||
                            w.address?.city?.toLowerCase().includes(q) ||
                            (w.experience?.skills || []).some((s: any) => s.name_en?.toLowerCase().includes(q) || s.name_es?.toLowerCase().includes(q))
                          );
                        })
                        .map(worker => (
                          <div key={worker.uid} className="border border-gray-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div>
                              <div className="font-semibold text-gray-900">{worker.firstName} {worker.lastName}</div>
                              <div className="text-sm text-gray-500">{worker.email}</div>
                              <div className="text-sm text-gray-400">{worker.address?.city || '-'}, {worker.address?.state || '-'}, {worker.address?.country || '-'}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                <b>{t('projects.workerAssignment.skills')}:</b> {(worker.experience?.skills || []).map((s: any) => i18n.language === 'es' ? s.name_es : s.name_en).join(', ') || '-'}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                <b>{t('projects.workerAssignment.certifications')}:</b> {(worker.certifications || []).map((c: any) => c.name).join(', ') || '-'}
                              </div>
                            </div>
                            <button
                              className="ml-auto px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                              onClick={() => {
                                setSelectedWorkerForOffer(worker);
                                setOfferPrice(taskFormData.price || '');
                                setOfferUnit('');
                                setOfferDuration('');
                              }}
                            >
                              {t('projects.workerAssignment.offerButton')}
                            </button>
                          </div>
                        ))}
                      {workers.filter(w => w.role === 'worker' && w.status === 'active').length === 0 && (
                        <div className="text-gray-500 text-center">{t('projects.workerAssignment.noWorkers')}</div>
                      )}
                    </div>
                  </>
                )}

                {/* Mostrar mensaje cuando hay workers asignados */}
                {taskFormData.worker && taskFormData.worker.length > 0 && !showAssignmentRequests && (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-lg mb-2">👷</div>
                    <p className="text-sm mb-2">{t('projects.workerAssignment.workersAlreadyAssigned')}</p>
                    <p className="text-xs text-gray-400">{t('projects.workerAssignment.removeWorkerToAssign')}</p>
                  </div>
                )}
                
                {/* ✅ Botones de acción */}
                <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
                  {/* Botón de Guardar Cambios eliminado aquí, ya existe abajo */}
                </div>
                {/* Modal de oferta */}
                {selectedWorkerForOffer && (
                  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 relative">
                      <h2 className="text-xl font-bold text-red-600 mb-4">{t('projects.workerAssignment.offerTitle', { name: selectedWorkerForOffer.firstName })}</h2>
                      <div className="mb-4">
                        <label className="block text-xs text-gray-500 mb-1">{t('projects.workerAssignment.price')}</label>
                        <input type="number" value={offerPrice} onChange={e => setOfferPrice(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded" />
                      </div>
                      <div className="mb-4">
                        <label className="block text-xs text-gray-500 mb-1">{t('projects.workerAssignment.unit')}</label>
                        <select value={offerUnit} onChange={e => setOfferUnit(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded">
                          <option value="">{t('projects.workerAssignment.selectUnit') || 'Seleccionar unidad'}</option>
                          <option value="hour">{t('projects.workerAssignment.hour')}</option>
                          <option value="day">{t('projects.workerAssignment.day')}</option>
                        </select>
                      </div>
                      <div className="mb-4">
                        <label className="block text-xs text-gray-500 mb-1">{t('projects.workerAssignment.duration')}</label>
                        <input type="number" value={offerDuration} onChange={e => setOfferDuration(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setSelectedWorkerForOffer(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50">{t('common.cancel')}</button>
                        <button 
                          onClick={handleSendOffer} 
                          disabled={sendingOffer || !offerPrice || !offerUnit || !offerDuration} 
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                        >
                          {t('projects.workerAssignment.sendOffer')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botón de guardar para worker assignment */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowTaskModal(false); setEditingTask(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTask}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {editingTask ? t('common.save') : t('projects.kanban.createTask')}
                  </button>
                </div>
              </div>
            )}
            {/* Tab de Archivos */}
            {activeTaskTab === 'files' && (
              <div className="space-y-4">
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded mb-4 text-base">{t('projects.kanban.tabs.files')}</span>
                
                {/* Área de subida de archivos */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.kanban.uploadFiles')}</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleTaskFileUpload}
                      disabled={uploadingTaskFile}
                      className="hidden"
                      id="task-file-upload"
                    />
                    <label htmlFor="task-file-upload" className="cursor-pointer">
                      <div className="text-gray-600">
                        <div className="text-lg mb-2">{t('projects.kanban.dragAndDrop')}</div>
                        <div className="text-sm text-gray-500 mb-2">{t('projects.kanban.supportedFormats')}</div>
                        <div className="text-xs text-gray-400">{t('projects.kanban.maxFileSize')}</div>
                      </div>
                    </label>
                  </div>
                  {uploadingTaskFile && (
                    <div className="mt-2 text-center">
                      <div className="inline-flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                        <span className="text-sm text-gray-600">{t('projects.kanban.uploadingFiles')}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Lista de archivos */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('projects.materials.taskFiles')}</h3>
                  {taskFiles.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      {t('projects.kanban.noFiles')}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {taskFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                              {file.type.startsWith('image/') ? (
                                <img src={file.url} alt={file.name} className="w-8 h-8 object-cover rounded" />
                              ) : (
                                <span className="text-red-600 text-lg">📄</span>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{file.name}</div>
                              <div className="text-sm text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB • {new Date(file.uploadDate).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                            >
                              {t('projects.kanban.downloadFile')}
                            </a>
                            <button
                              onClick={() => handleTaskFileDelete(file.id)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                            >
                              {t('projects.kanban.deleteFile')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Botón de guardar para archivos */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowTaskModal(false); setEditingTask(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTask}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {editingTask ? t('common.save') : t('projects.kanban.createTask')}
                  </button>
                </div>
              </div>
            )}
            {/* Tab de Scanning */}
            {activeTaskTab === 'scanning' && taskFormData.isScanningJob && (
              <div className="space-y-4">
                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded mb-4 text-base">{t('projects.kanban.scanningJob.report')}</span>
                
                {/* PURPOSE */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.kanban.scanningJob.purpose')}</label>
                  <textarea
                    value={taskFormData.scanningData?.purpose || ''}
                    onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ 
                      ...prev, 
                      scanningData: { ...prev.scanningData, purpose: e.target.value } 
                    }))}
                    placeholder="Enter the purpose of the scanning..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={4}
                    disabled={taskFormData.scanningReport?.signed}
                  />
                </div>

                {/* EQUIPMENT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.kanban.scanningJob.equipment')}</label>
                  <textarea
                    value={taskFormData.scanningData?.equipment || ''}
                    onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ 
                      ...prev, 
                      scanningData: { ...prev.scanningData, equipment: e.target.value } 
                    }))}
                    placeholder="Describe the equipment used..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={4}
                    disabled={taskFormData.scanningReport?.signed}
                  />
                </div>

                {/* PROCESS */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.kanban.scanningJob.process')}</label>
                  <textarea
                    value={taskFormData.scanningData?.process || ''}
                    onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ 
                      ...prev, 
                      scanningData: { ...prev.scanningData, process: e.target.value } 
                    }))}
                    placeholder="Describe the scanning process..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={4}
                    disabled={taskFormData.scanningReport?.signed}
                  />
                </div>

                {/* LIMITATIONS */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.kanban.scanningJob.limitations')}</label>
                  <textarea
                    value={taskFormData.scanningData?.limitations || ''}
                    onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ 
                      ...prev, 
                      scanningData: { ...prev.scanningData, limitations: e.target.value } 
                    }))}
                    placeholder="Describe the scanning limitations..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={4}
                    disabled={taskFormData.scanningReport?.signed}
                  />
                </div>

                {/* FINDINGS CHECKLIST */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">{t('projects.kanban.scanningJob.findings')}</label>
                  
                  <div className="space-y-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {/* A. Structural Reinforcement */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.structuralReinforcement.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(taskFormData.scanningData?.checklistItems || []).includes("Rebar detected")}
                            onChange={(e) => handleChecklistChange("Rebar detected", e.target.checked)}
                            disabled={taskFormData.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.rebarDetected')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(taskFormData.scanningData?.checklistItems || []).includes("Wire mesh detected")}
                            onChange={(e) => handleChecklistChange("Wire mesh detected", e.target.checked)}
                            disabled={taskFormData.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.wireMeshDetected')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(taskFormData.scanningData?.checklistItems || []).includes("Post-tension tendons detected")}
                            onChange={(e) => handleChecklistChange("Post-tension tendons detected", e.target.checked)}
                            disabled={taskFormData.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.postTensionTendons')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(taskFormData.scanningData?.checklistItems || []).includes("No reinforcement detected in the scanned area")}
                            onChange={(e) => handleChecklistChange("No reinforcement detected in the scanned area", e.target.checked)}
                            disabled={taskFormData.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.noReinforcement')}</span>
                        </label>
                      </div>
                    </div>

                    {/* B. Detected Utilities */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.detectedUtilities.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Electrical conduit")} onChange={(e) => handleChecklistChange("Electrical conduit", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.electricalConduit')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Communications conduit")} onChange={(e) => handleChecklistChange("Communications conduit", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.communicationsConduit')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Metal pipe")} onChange={(e) => handleChecklistChange("Metal pipe", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.metalPipe')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("PVC pipe")} onChange={(e) => handleChecklistChange("PVC pipe", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.pvcPipe')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Energized utilities detected (EM readings)")} onChange={(e) => handleChecklistChange("Energized utilities detected (EM readings)", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.energizedUtilities')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("No utilities detected")} onChange={(e) => handleChecklistChange("No utilities detected", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.noUtilities')}</span>
                        </label>
                      </div>
                    </div>

                    {/* C. Slab Conditions */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.slabConditions.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("High moisture levels in slab")} onChange={(e) => handleChecklistChange("High moisture levels in slab", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.highMoisture')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Interference in signals")} onChange={(e) => handleChecklistChange("Interference in signals", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.interference')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Limited depth penetration")} onChange={(e) => handleChecklistChange("Limited depth penetration", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.limitedDepth')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Irregular surface affected scanning")} onChange={(e) => handleChecklistChange("Irregular surface affected scanning", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.irregularSurface')}</span>
                        </label>
                      </div>
                    </div>

                    {/* D. Surface Markings */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.surfaceMarkings.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Marked in red (electrical)")} onChange={(e) => handleChecklistChange("Marked in red (electrical)", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.markedRed')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Marked in yellow (reinforcement)")} onChange={(e) => handleChecklistChange("Marked in yellow (reinforcement)", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.markedYellow')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("High-risk areas marked")} onChange={(e) => handleChecklistChange("High-risk areas marked", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.highRiskAreas')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Manual exposure required before drilling/cutting")} onChange={(e) => handleChecklistChange("Manual exposure required before drilling/cutting", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.manualExposure')}</span>
                        </label>
                      </div>
                    </div>

                    {/* E. Recommendations */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.recommendations.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Avoid drilling near marked areas")} onChange={(e) => handleChecklistChange("Avoid drilling near marked areas", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.avoidDrilling')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Manually expose marked items before cutting")} onChange={(e) => handleChecklistChange("Manually expose marked items before cutting", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.manuallyExpose')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Review structural drawings")} onChange={(e) => handleChecklistChange("Review structural drawings", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.reviewDrawings')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(taskFormData.scanningData?.checklistItems || []).includes("Recommend re-scan due to interference")} onChange={(e) => handleChecklistChange("Recommend re-scan due to interference", e.target.checked)} disabled={taskFormData.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.recommendRescan')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SCANNING AND DIAGRAM IMAGES */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Scan & Diagram Images</h3>
                    <button
                      type="button"
                      onClick={handleAddScanningImageSet}
                      disabled={taskFormData.scanningReport?.signed}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      + Add Image Set
                    </button>
                  </div>
                  
                  {taskFormData.scanningReport?.signed && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-yellow-800 mb-2">
                            <span className="font-medium">⚠️ Scanning Report Locked:</span> This report has been signed and cannot be edited.
                          </p>
                          {taskFormData.scanningReport?.signedPdf && (
                            <div className="text-xs text-gray-700 space-y-1 mb-3">
                              <div><span className="font-medium">Signed by:</span> {taskFormData.scanningReport.signedPdf.signedBy?.name}</div>
                              <div><span className="font-medium">Date:</span> {new Date(taskFormData.scanningReport.signedPdf.signedAt).toLocaleString()}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      {taskFormData.scanningReport?.signedPdf?.url && (
                        <div className="flex gap-2">
                          <a
                            href={taskFormData.scanningReport.signedPdf.url}
                            download={`scanning-report-${taskFormData.id}.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Download Signed PDF
                          </a>
                          <button
                            type="button"
                            onClick={() => setShowScanningReportModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                            View & Manage PDF
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {taskFormData.scanningData?.images?.length === 0 ? (
                    <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm">No image sets added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {taskFormData.scanningData?.images?.map((imageSet: any, index: any) => (
                        <div key={imageSet.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-gray-700">Image Set {index + 1}</h4>
                            <button
                              type="button"
                              onClick={() => handleRemoveScanningImageSet(imageSet.id)}
                              disabled={taskFormData.scanningReport?.signed}
                              className="text-red-600 hover:text-red-800 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <FaTimes />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            {/* Scan Image */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Scan Image</label>
                              {imageSet.scanImage.url ? (
                                <div className="space-y-2">
                                  <img 
                                    src={imageSet.scanImage.url} 
                                    alt="Scan" 
                                    className="w-full h-40 object-cover rounded-lg border border-gray-300"
                                  />
                                  <div className="text-xs text-gray-600">
                                    <p className="font-medium truncate">{imageSet.scanImage.name}</p>
                                    <p>{new Date(imageSet.scanImage.uploadDate).toLocaleDateString()}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTaskFormData((prev: typeof taskFormData) => ({
                                        ...prev,
                                        scanningData: {
                                          ...prev.scanningData,
                                          images: prev.scanningData.images.map((img: any) =>
                                            img.id === imageSet.id
                                              ? {
                                                  ...img,
                                                  scanImage: { url: '', name: '', uploadDate: '' }
                                                }
                                              : img
                                          )
                                        }
                                      }));
                                    }}
                                    disabled={taskFormData.scanningReport?.signed}
                                    className="w-full px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-red-400 transition-colors">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleScanningImageUpload(e, imageSet.id, 'scanImage')}
                                    disabled={uploadingScanningImages[`${imageSet.id}-scanImage`] || taskFormData.scanningReport?.signed}
                                    className="hidden"
                                    id={`scan-${imageSet.id}`}
                                  />
                                  <label htmlFor={`scan-${imageSet.id}`} className="cursor-pointer">
                                    <div className="text-gray-600">
                                      <div className="text-sm mb-1">📸 Click to upload</div>
                                      <div className="text-xs text-gray-500">or drag and drop</div>
                                    </div>
                                  </label>
                                  {uploadingScanningImages[`${imageSet.id}-scanImage`] && (
                                    <div className="mt-2 flex justify-center">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Diagram Image */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Diagram Image</label>
                              {imageSet.diagramImage.url ? (
                                <div className="space-y-2">
                                  <img 
                                    src={imageSet.diagramImage.url} 
                                    alt="Diagram" 
                                    className="w-full h-40 object-cover rounded-lg border border-gray-300"
                                  />
                                  <div className="text-xs text-gray-600">
                                    <p className="font-medium truncate">{imageSet.diagramImage.name}</p>
                                    <p>{new Date(imageSet.diagramImage.uploadDate).toLocaleDateString()}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTaskFormData((prev: typeof taskFormData) => ({
                                        ...prev,
                                        scanningData: {
                                          ...prev.scanningData,
                                          images: prev.scanningData.images.map((img: any) =>
                                            img.id === imageSet.id
                                              ? {
                                                  ...img,
                                                  diagramImage: { url: '', name: '', uploadDate: '' }
                                                }
                                              : img
                                          )
                                        }
                                      }));
                                    }}
                                    disabled={taskFormData.scanningReport?.signed}
                                    className="w-full px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-red-400 transition-colors">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleScanningImageUpload(e, imageSet.id, 'diagramImage')}
                                    disabled={uploadingScanningImages[`${imageSet.id}-diagramImage`] || taskFormData.scanningReport?.signed}
                                    className="hidden"
                                    id={`diagram-${imageSet.id}`}
                                  />
                                  <label htmlFor={`diagram-${imageSet.id}`} className="cursor-pointer">
                                    <div className="text-gray-600">
                                      <div className="text-sm mb-1">📊 Click to upload</div>
                                      <div className="text-xs text-gray-500">or drag and drop</div>
                                    </div>
                                  </label>
                                  {uploadingScanningImages[`${imageSet.id}-diagramImage`] && (
                                    <div className="mt-2 flex justify-center">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Allow Worker to Edit Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="allowWorkerEdit"
                        checked={(taskFormData.workerScanningEdit || []).length > 0}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            // Si se desmarca, limpiar todos los workers
                            setTaskFormData((prev: typeof taskFormData) => ({
                              ...prev,
                              workerScanningEdit: []
                            }));
                          } else {
                            // Si se marca, agregar todos los workers asignados por defecto
                            const assignedWorkerIds: string[] = [];
                            if (taskFormData.worker) {
                              if (Array.isArray(taskFormData.worker)) {
                                assignedWorkerIds.push(...taskFormData.worker);
                              } else {
                                assignedWorkerIds.push(taskFormData.worker);
                              }
                            }
                            // También incluir workers de solicitudes aceptadas
                            assignmentRequests.filter(r => r.status === 'accepted').forEach(r => {
                              if (r.workerId && !assignedWorkerIds.includes(r.workerId)) {
                                assignedWorkerIds.push(r.workerId);
                              }
                            });
                            setTaskFormData((prev: typeof taskFormData) => ({
                              ...prev,
                              workerScanningEdit: assignedWorkerIds
                            }));
                          }
                        }}
                        className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                      />
                      <label htmlFor="allowWorkerEdit" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Allow worker to edit scanning report
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 ml-8">
                      When enabled, selected workers will be able to edit the scanning job report fields.
                    </p>
                    
                    {/* Lista de workers asignados para seleccionar */}
                    {(taskFormData.workerScanningEdit || []).length > 0 && (
                      <div className="mt-4 ml-8 bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Select workers who can edit:</h4>
                        <div className="space-y-2">
                          {/* Workers del campo worker */}
                          {taskFormData.worker && (
                            <>
                              {Array.isArray(taskFormData.worker) ? (
                                taskFormData.worker.map((workerId: string) => {
                                  const worker = workers.find(w => w.uid === workerId);
                                  return (
                                    <label key={workerId} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                                        checked={(taskFormData.workerScanningEdit || []).includes(workerId)}
                                        onChange={(e) => {
                                          const currentIds = taskFormData.workerScanningEdit || [];
                                          if (e.target.checked) {
                                            setTaskFormData((prev: typeof taskFormData) => ({
                                              ...prev,
                                              workerScanningEdit: [...currentIds, workerId]
                                            }));
                                          } else {
                                            setTaskFormData((prev: typeof taskFormData) => ({
                                              ...prev,
                                              workerScanningEdit: currentIds.filter((id: string) => id !== workerId)
                                            }));
                                          }
                                        }}
                                      />
                                      <span>
                                        {worker ? `${worker.firstName} ${worker.lastName}` : workerId}
                                        {worker && <span className="text-gray-500 ml-2">({worker.email})</span>}
                                      </span>
                                    </label>
                                  );
                                })
                              ) : (
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                                    checked={(taskFormData.workerScanningEdit || []).includes(taskFormData.worker)}
                                    onChange={(e) => {
                                      const currentIds = taskFormData.workerScanningEdit || [];
                                      if (e.target.checked) {
                                        setTaskFormData((prev: typeof taskFormData) => ({
                                          ...prev,
                                          workerScanningEdit: [...currentIds, taskFormData.worker]
                                        }));
                                      } else {
                                        setTaskFormData((prev: typeof taskFormData) => ({
                                          ...prev,
                                          workerScanningEdit: currentIds.filter((id: string) => id !== taskFormData.worker)
                                        }));
                                      }
                                    }}
                                  />
                                  <span>
                                    {(() => {
                                      const worker = workers.find(w => w.uid === taskFormData.worker);
                                      return worker ? `${worker.firstName} ${worker.lastName} (${worker.email})` : taskFormData.worker;
                                    })()}
                                  </span>
                                </label>
                              )}
                            </>
                          )}
                          
                          {/* Workers de solicitudes aceptadas */}
                          {assignmentRequests.filter(r => r.status === 'accepted').map((request) => {
                            const worker = workers.find(w => w.uid === request.workerId);
                            if (!request.workerId || (taskFormData.worker && 
                              (Array.isArray(taskFormData.worker) ? taskFormData.worker.includes(request.workerId) : taskFormData.worker === request.workerId))) {
                              return null; // Ya está en la lista de workers asignados
                            }
                            return (
                              <label key={request.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                                  checked={(taskFormData.workerScanningEdit || []).includes(request.workerId)}
                                  onChange={(e) => {
                                    const currentIds = taskFormData.workerScanningEdit || [];
                                    if (e.target.checked) {
                                      setTaskFormData((prev: typeof taskFormData) => ({
                                        ...prev,
                                        workerScanningEdit: [...currentIds, request.workerId]
                                      }));
                                    } else {
                                      setTaskFormData((prev: typeof taskFormData) => ({
                                        ...prev,
                                        workerScanningEdit: currentIds.filter((id: string) => id !== request.workerId)
                                      }));
                                    }
                                  }}
                                />
                                <span>
                                  {worker ? `${worker.firstName} ${worker.lastName}` : request.workerId}
                                  {worker && <span className="text-gray-500 ml-2">({worker.email})</span>}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        {(taskFormData.workerScanningEdit || []).length === 0 && (
                          <p className="text-xs text-gray-500 mt-2">No workers selected. The scanning report will be read-only for all workers.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* CLOSURE */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.kanban.scanningJob.closure')}</label>
                  <textarea
                    value={taskFormData.scanningData?.closure || ''}
                    onChange={e => setTaskFormData((prev: typeof taskFormData) => ({ 
                      ...prev, 
                      scanningData: { ...prev.scanningData, closure: e.target.value } 
                    }))}
                    placeholder="Enter closing remarks or summary..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={4}
                    disabled={taskFormData.scanningReport?.signed}
                  />
                  {taskFormData.scanningReport?.signed && (
                    <p className="mt-1 text-xs text-yellow-600">
                      ⚠️ This field is locked because the report has been signed. Delete the signed PDF to make changes.
                    </p>
                  )}
                </div>

                {/* Botón de guardar para scanning */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowTaskModal(false); setEditingTask(null); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowScanningReportModal(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {t('projects.kanban.scanningJob.generateReport')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTask}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    {editingTask ? t('common.save') : t('projects.kanban.createTask')}
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={() => { setShowTaskModal(false); setEditingTask(null); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600 text-2xl font-bold"
              title="Cerrar"
            >×</button>
          </div>
        </div>
      )}

      {/* Modal de Solicitudes de Asignación */}
      {showAssignmentModal && selectedAssignmentRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{t('projects.assignment.requestDetails')}</h2>
              <button 
                onClick={() => {
                  setShowAssignmentModal(false);
                  setSelectedAssignmentRequest(null);
                  setShowCounterOffer(false);
                  setCounterPrice('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('projects.assignment.workerInfo')}</h3>
                <p className="text-sm text-gray-600">
                  {workers.find(w => w.uid === selectedAssignmentRequest.workerId)?.firstName} {workers.find(w => w.uid === selectedAssignmentRequest.workerId)?.lastName}
                </p>
                <p className="text-sm text-gray-600">
                  {workers.find(w => w.uid === selectedAssignmentRequest.workerId)?.email}
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('projects.assignment.offerDetails')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('projects.assignment.price')}:</span>
                    <span>${selectedAssignmentRequest.price} {selectedAssignmentRequest.priceUnit === 'hour' ? '/hora' : '/día'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('projects.assignment.duration')}:</span>
                    <span>{selectedAssignmentRequest.duration} {selectedAssignmentRequest.priceUnit === 'hour' ? 'horas' : 'días'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('projects.assignment.total')}:</span>
                    <span>${selectedAssignmentRequest.status === 'counter' && selectedAssignmentRequest.lastCounterBy === 'worker' && selectedAssignmentRequest.counterPrice 
                      ? selectedAssignmentRequest.counterPrice * selectedAssignmentRequest.duration 
                      : selectedAssignmentRequest.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('projects.assignment.status')}:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      selectedAssignmentRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedAssignmentRequest.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      selectedAssignmentRequest.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedAssignmentRequest.status === 'counter' ? t('projects.assignment.statuses.counter') : t(`projects.assignment.statuses.${selectedAssignmentRequest.status}`)}
                    </span>
                  </div>
                </div>
              </div>

              {selectedAssignmentRequest.status === 'counter' && selectedAssignmentRequest.lastCounterBy === 'worker' && selectedAssignmentRequest.counterPrice && (
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">🔄 {t('projects.assignment.workerCounteroffer')}</h4>
                  <div className="text-sm text-blue-700">
                    <div>{t('projects.assignment.price')}: ${selectedAssignmentRequest.counterPrice} {selectedAssignmentRequest.priceUnit === 'hour' ? t('projects.assignment.perHour') : t('projects.assignment.perDay')}</div>
                    <div>{t('projects.assignment.total')}: ${selectedAssignmentRequest.counterPrice * selectedAssignmentRequest.duration}</div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Mostrar botones de acción solo si la solicitud está pendiente o en contraoferta */}
            {(selectedAssignmentRequest.status === 'pending' || selectedAssignmentRequest.status === 'counter') ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAcceptAssignment(selectedAssignmentRequest)}
                    disabled={processingAssignment}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FaCheck />
                    {t('projects.assignment.accept')}
                  </button>
                  <button
                    onClick={() => handleRejectAssignment(selectedAssignmentRequest)}
                    disabled={processingAssignment}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FaTimes />
                    {t('projects.assignment.reject')}
                  </button>
                </div>
                
                <button
                  onClick={() => setShowCounterOffer(!showCounterOffer)}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  🔄
                  {t('projects.assignment.statuses.counter')}
                </button>
                
                {showCounterOffer && (
                  <div className="space-y-3">
                    <input
                      type="number"
                      value={counterPrice}
                      onChange={(e) => setCounterPrice(e.target.value)}
                      placeholder="Precio por hora/día"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <button
                      onClick={() => handleCounterAssignment(selectedAssignmentRequest)}
                      disabled={!counterPrice || processingAssignment}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md transition-colors disabled:opacity-50"
                    >
                      {processingAssignment ? t('projects.assignment.sending') : t('projects.assignment.sendCounteroffer')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Mostrar mensaje informativo si la solicitud ya fue procesada */
              <div className={`p-4 rounded-lg text-center ${
                selectedAssignmentRequest.status === 'accepted' ? 'bg-green-50 border border-green-200' :
                selectedAssignmentRequest.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                'bg-gray-50 border border-gray-200'
              }`}>
                <p className={`font-semibold ${
                  selectedAssignmentRequest.status === 'accepted' ? 'text-green-700' :
                  selectedAssignmentRequest.status === 'rejected' ? 'text-red-700' :
                  'text-gray-700'
                }`}>
                  {selectedAssignmentRequest.status === 'accepted' && '✅ Esta solicitud ya ha sido aceptada'}
                  {selectedAssignmentRequest.status === 'rejected' && '❌ Esta solicitud fue rechazada'}
                  {selectedAssignmentRequest.status === 'removed' && '🚫 Esta asignación fue removida'}
                </p>
                {selectedAssignmentRequest.status === 'accepted' && (
                  <p className="text-sm text-gray-600 mt-2">
                    El worker está asignado a esta tarea.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {showDeleteErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full border border-red-200 flex flex-col items-center justify-center animate-fadeIn relative">
            <button className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-red-600" onClick={() => setShowDeleteErrorModal(false)}>&times;</button>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FaTimes className="text-3xl text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-700 mb-4 text-center">⚠️ {t('projects.deleteTask.cannotDelete')}</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-gray-800 text-center mb-3">
                <strong>{t('projects.deleteTask.hasWorkerAssigned')}</strong>
              </p>
              <p className="text-sm text-gray-600 text-center mb-2">
                {t('projects.deleteTask.workerProtection')}
              </p>
              <p className="text-sm text-gray-700 text-center font-medium">
                💡 {t('projects.deleteTask.removeWorkerFirst')}
              </p>
            </div>
            <button onClick={() => setShowDeleteErrorModal(false)} className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-md">
              {t('projects.deleteTask.understood')}
            </button>
          </div>
        </div>
      )}
      {showScanningReportModal && (
        <ScanningReportModal
          isOpen={showScanningReportModal}
          onClose={() => setShowScanningReportModal(false)}
          onPdfSigned={async () => {
            // Recargar el task desde Firestore para reflejar el estado actualizado
            if (taskFormData.id && userData?.businessId && projectId) {
              try {
                const taskRef = doc(db, 'projects', userData.businessId, 'projects', projectId, 'tasks', taskFormData.id);
                const taskSnap = await getDoc(taskRef);
                if (taskSnap.exists()) {
                  const updatedTask = { id: taskSnap.id, ...taskSnap.data() };
                  setTaskFormData(updatedTask);
                  console.log('[ProjectView] Task recargado con estado actualizado');
                }
              } catch (error) {
                console.error('[ProjectView] Error recargando task:', error);
              }
            }
          }}
          task={taskFormData}
          client={clientData}
          currentUser={currentUser}
          userData={userData}
        />
      )}
    </div>
  );
};

export default ProjectView;
